// Social Solve Management: Oberfläche im Browser.
import { openWork } from "../shared/invoice.js";
import type { AutoVideo, DriveScriptInfo, DriveScriptsResponse, ApiTokenRow, BilledRow, NotificationRow, Client, ClientBilling, Company, Me, MilkeeOverview, MkAccount, MkCustomerFinance, MkDoc, MkEntry, MkInsights, MkProject, MkTime, MkTimer, Note, Project, SheetTable, State, StateKey, UserRow } from "../shared/types.js";
import { STATE_KEYS } from "../shared/types.js";
import { ApiError, api, milkeeDown, type ApnsInfo, type GoogleInfo, type MilkeeInfo, type NextcloudInfo } from "./api.js";
import { initClosing, vJahresabschluss } from "./closing.js";
import { afterRenderInvoice, initInvoices, invoiceChanged, openNewInvoice, vRechnung } from "./invoices.js";
import { initDeliveries, openNewDelivery, vAbgabe, vAbgaben } from "./deliveries.js";
import { afterPaint, clientScripts, editorBusy, forgetClientScripts, initScripts, leaveEditor, openNewScript, vSkript, vSkripte } from "./scripts.js";
import { ic } from "./icons.js";
import { GS_FIELDS, colLetter, isDone, isMineFor, itemKey, openComments, parseSheet, projectName, sheetIdFrom, type GSpreadsheet, type LiveItem, type LiveTab, type VideoFeedback } from "../shared/sheet.js";
import { MON, MONL, MONS, WD, WDL, byId, sens, daysFrom, esc, fmtD, fmtDate, fromInput, hostOf, invNr, must, nowIso, rawVal, rel, relCls, safeUrl, toInput, uid, val } from "./util.js";
import { spanMinutes } from "../shared/time.js";
import type { HealthStatus } from "../shared/health.js";
import { addDays, type CalDeadline, type CalEvent, type CalendarResponse, type MsCalendar, type MsStatus } from "../shared/calendar.js";

const LOGO = document.querySelector(".brand svg")?.outerHTML ?? "";

/* ============ Konstanten & Zustand ============ */

const PHASES = ["Anfrage", "Laufend", "Feedback", "Zu verrechnen", "Verrechnet", "Abgeschlossen"];
/** Arbeitsphasen (Board-Spalten); «Verrechnet» und «Abgeschlossen» landen im Ordner darunter. */
const WORK_PHASES = ["Anfrage", "Laufend", "Feedback", "Zu verrechnen"];
const DONE_PHASES = ["Verrechnet", "Abgeschlossen"];
const isOpenWork = (p: Project) => !DONE_PHASES.includes(p.phase) && p.phase !== "Zu verrechnen";
const TABS: Record<string, { name: string; icon: string; desc: string }> = {
  table: { name: "Live-Tabelle", icon: "sheet", desc: "Eine Google-Tabelle des Kunden, live und sauber nach Deadline angezeigt." },
  projects: { name: "Projekte", icon: "board", desc: "Projekte mit Phase und Deadline." },
  notes: { name: "Notizen", icon: "note", desc: "Notizen und To-dos, die nur zu diesem Kunden gehören." },
};
const COLORS = ["#80CFF5", "#B7A6F7", "#8EE0BE", "#F3C77A", "#F2A6C8", "#9FB6CC", "#F5A97F", "#A9D46F", "#E6B3F5"];

const blank = (): State => ({ company: { owner: "" }, clients: [], projects: [], notes: {}, tables: {} });
let S: State = blank();
let ME: Me | null = null;
let LOADED = false;
let REV = 0;
let SYNC: "ok" | "saving" | "error" = "ok";
let ENROLL: { qr: string; secret: string } | null = null;
let SA_EMAIL = "";
const SAVED: Partial<Record<StateKey, string>> = {};

type View = "uebersicht" | "kunden" | "kunde" | "projekte" | "kalender" | "skripte" | "skript" | "abgaben" | "abgabe" | "zeit" | "finanzen" | "rechnungen" | "offerten" | "buchungen" | "jahresabschluss" | "rechnung" | "einstellungen";
const UI = {
  view: "uebersicht" as View,
  /** zuletzt gewählter Kunde beim schnellen To-do */
  lastTodoCid: "",
  cid: "" as string,
  sid: "" as string,
  did: "" as string,
  /** Rechnung im Editor: MILKEE-ID oder «neu» */
  rid: "" as string,
  ctab: "overview",
  clientQ: "",
  clientFilter: "aktiv",
  projView: "clients" as "clients" | "list" | "board",
  projClient: "",
  set: "sicherheit",
  liveMode: "plan" as "plan" | "raw",
  liveTabIdx: 0,
  clientEditId: null as string | null,
  confirm: null as null | (() => void | Promise<void>),
};
let USERS: UserRow[] | null = null;
let TOKENS: ApiTokenRow[] | null = null;
let GOOGLE: GoogleInfo | null = null;
let NOTIFS: NotificationRow[] = [];
let NOTIF_SEEN = new Set<string>();
let MK: MilkeeInfo = { configured: false, company: "" };
/** null = nicht geladen */
let NCI: NextcloudInfo | null = null;
let APNS: ApnsInfo | null = null;
let FIN: { state: "idle" | "loading" | "ok" | "error"; data?: MilkeeOverview; err?: string } = { state: "idle" };
/** Verrechnete Videos pro Kunde. undefined = noch nicht geladen, null = wird geladen. */
const BILLED: Record<string, BilledRow[] | null | undefined> = {};
let MK_CUSTOMERS: { id: number; name: string }[] | null = null;
/** Spiegel von MILKEE, nur im Browser zwischengespeichert. undefined = noch nicht geladen. */
const MKD: {
  projects?: MkProject[] | null;
  times?: { list: MkTime[]; range: number; projectId: number } | null;
  timer?: MkTimer;
  docs?: { kind: "invoice" | "proposal"; status: string; year: number; list: MkDoc[] } | null;
  entries?: { year: number; type: string; list: MkEntry[] } | null;
  accounts?: MkAccount[] | null;
  err?: string;
} = {};
const MKUI = { timeRange: 31, timeProject: 0, docKind: "invoice" as "invoice" | "proposal", docStatus: "unpaid", docYear: 0, entryYear: new Date().getFullYear(), entryType: "", finRange: "year" as RangeKey, cfinRange: "12m" as RangeKey };

function applyRemote<K extends StateKey>(k: K, v: State[K] | undefined) {
  if (k === "company") S.company = { owner: "", ...((v as Company | undefined) ?? {}) };
  else S[k] = (v ?? blank()[k]) as State[K];
}

const CO = () => S.company;
const C = (id: string) => S.clients.find((c) => c.id === id);
const P = (id: string) => S.projects.find((p) => p.id === id);
const cdot = (c: Client | undefined) => `<i class="dot" style="--c:${c ? esc(c.color) : "var(--dim)"}"></i>`;
const activeProjects = (cid?: string) => S.projects.filter((p) => isOpenWork(p) && (!cid || p.client === cid));
const openTodos = (cid: string) => (S.notes[cid] ?? []).filter((n) => n.kind === "todo" && !n.done);
const firstName = () => ((ME?.user.name || CO().owner || "").split(" ")[0] ?? "").trim();
const isMine = (it: LiveItem) => isMineFor(firstName() || "david")(it);
const isAdmin = () => ME?.user.role === "admin";

interface Upcoming {
  kind: string;
  cid: string;
  title: string;
  sub: string;
  due: string;
  to: string;
  ctab?: string;
  /** Stand des Videos aus der Live-Tabelle (abgegeben, Feedback …) */
  state?: VideoState;
}

/** Was ein Video gerade braucht: abgegeben und wartet, oder Feedback zurück bei dir */
interface VideoState {
  /** abgegebene Version, wartet auf Feedback: zählt nicht als offene Aufgabe */
  waiting: boolean;
  label: string;
  cls: string;
}
function videoState(it: LiveItem): VideoState | undefined {
  const v = it.stand.trim().match(/^(?:v|version\s*)(\d{1,2})\b/i);
  if (!v) return undefined;
  const who = it.todo.trim();
  // Version steht drin und niemand oder jemand anderes ist dran: du hast abgegeben
  if (!who || !isMine(it)) return { waiting: true, label: `v${v[1]} abgegeben`, cls: "ok" };
  return { waiting: false, label: `Feedback zu v${v[1]}`, cls: "warn" };
}
function upcoming(cid?: string, days = 30): Upcoming[] {
  const items: Upcoming[] = [];
  // Videos aus der Live-Tabelle, für die die Automatik schon ein Projekt angelegt hat, nur einmal zeigen
  const live = new Map<string, LiveItem>();
  for (const [k, L] of Object.entries(LIVE)) for (const it of L.items) live.set(`${k}|${itemKey(it)}`, it);
  const linked = new Set<string>();
  for (const p of S.projects) {
    const key = typeof p.sheetKey === "string" && p.sheetKey ? `${p.client}|${p.sheetKey}` : "";
    if (key) linked.add(key);
    if ((cid && p.client !== cid) || !isOpenWork(p) || !p.due || daysFrom(p.due) > days) continue;
    const v = key ? live.get(key) : undefined;
    const state = v ? videoState(v) : undefined;
    items.push({ kind: "Projekt", cid: p.client, title: p.title, sub: v ? videoSub(v, state, p.phase) : p.phase, due: p.due, to: `kunde-${p.client}`, ...(v ? { ctab: "table" } : {}), ...(state ? { state } : {}) });
  }
  for (const [k, L] of Object.entries(LIVE)) {
    if (cid && k !== cid) continue;
    for (const it of L.items) {
      if (linked.has(`${k}|${itemKey(it)}`) || isDone(it) || !isMine(it) || !it.due || daysFrom(it.due) > days) continue;
      const state = videoState(it);
      items.push({ kind: "Video", cid: k, title: it.video, sub: videoSub(it, state, "Video"), due: it.due, to: `kunde-${k}`, ctab: "table", ...(state ? { state } : {}) });
    }
  }
  return items.sort((a, b) => new Date(a.due).getTime() - new Date(b.due).getTime());
}
/** Unterzeile: bei abgegebenen Videos, bei wem sie liegen; sonst Phase, Abschnitt, Stand */
function videoSub(it: LiveItem, state: VideoState | undefined, lead: string) {
  if (state?.waiting) return [it.section, it.todo.trim() ? `bei ${it.todo.trim()}` : "", "wartet auf Feedback"].filter(Boolean).join(" · ");
  return [lead, it.section, state ? "" : it.stand].filter(Boolean).join(" · ");
}
/** Offene Aufgaben (ohne abgegebene Versionen, die auf Feedback warten) */
const todoItems = (items: Upcoming[]) => items.filter((i) => !i.state?.waiting);

/* ============ Anmeldung ============ */

type GateKind = "login" | "mfa" | "enroll" | "newpw" | "error";
const gateCard = (inner: string) => `<div class="gate-card"><div class="gate-brand">${LOGO}<div><b>Social Solve</b><span>Management</span></div></div>${inner}</div>`;
const codeInput = `<input class="in tnum" id="g-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]{6}" placeholder="123456" required style="font-size:22px;letter-spacing:.3em;text-align:center">`;

function gateHTML(kind: GateKind, msg?: string): string {
  const err = msg ? `<div class="note-box warn">${ic("lock", 16)}<span>${esc(msg)}</span></div>` : "";
  if (kind === "error")
    return gateCard(`<h1 class="h2">Daten konnten nicht geladen werden</h1>${err}<div class="filters"><button class="btn" data-a="reload">Neu laden</button><button class="btn ghost" data-a="logout">Abmelden</button></div>`);
  if (kind === "mfa")
    return gateCard(`<h1 class="h2">Bestätigungscode</h1><p class="muted">Gib den 6-stelligen Code aus deiner Authenticator-App ein.</p>${err}<form data-form="mfa" class="stack" style="gap:12px">${codeInput}<button class="btn pri block" type="submit">Bestätigen</button></form><button class="btn ghost sm" data-a="logout" style="align-self:center">Abmelden</button>`);
  if (kind === "enroll" && ENROLL)
    return gateCard(`<h1 class="h2">2-Faktor einrichten</h1><p class="muted">Einmalig: Scanne den QR-Code mit einer Authenticator-App (z. B. Google Authenticator, Microsoft Authenticator oder 1Password). Danach fragt Social Solve Management bei jeder Anmeldung nach dem Code.</p>${err}<div class="enroll-qr"><img src="${esc(ENROLL.qr)}" alt="QR-Code für die Authenticator-App" width="180" height="180"></div><details><summary class="small muted" style="cursor:pointer">QR-Code geht nicht? Schlüssel von Hand eingeben</summary><code class="secret">${esc(ENROLL.secret)}</code></details><form data-form="enroll" class="stack" style="gap:12px"><label class="flabel" for="g-code">Code aus der App</label>${codeInput}<button class="btn pri block" type="submit">Einrichtung abschliessen</button></form><button class="btn ghost sm" data-a="logout" style="align-self:center">Abmelden</button>`);
  if (kind === "newpw")
    return gateCard(`<h1 class="h2">Eigenes Passwort festlegen</h1><p class="muted">Du hast dich mit einem Startpasswort angemeldet. Wähle jetzt ein eigenes mit mindestens 14 Zeichen, am besten aus deinem Passwort-Manager.</p>${err}<form data-form="newpw" class="stack" style="gap:12px"><div class="field"><label for="g-pw1">Neues Passwort</label><input class="in" id="g-pw1" type="password" autocomplete="new-password" minlength="14" required></div><div class="field"><label for="g-pw2">Wiederholen</label><input class="in" id="g-pw2" type="password" autocomplete="new-password" minlength="14" required></div><button class="btn pri block" type="submit">Speichern</button></form>`);
  return gateCard(`<h1 class="h2">Anmelden</h1>${err}<form data-form="login" class="stack" style="gap:12px"><div class="field"><label for="g-email">E-Mail</label><input class="in" id="g-email" type="email" autocomplete="username" required></div><div class="field"><label for="g-pass">Passwort</label><input class="in" id="g-pass" type="password" autocomplete="current-password" required></div><button class="btn pri block" type="submit">Anmelden</button></form><p class="small dim" style="margin:0">Geschützt mit Passwort und 2-Faktor-Code. Konten vergibt ein Admin.</p>`);
}

function showGate(kind: GateKind, msg?: string) {
  (document.querySelector(".app") as HTMLElement).hidden = true;
  must("bNav").hidden = true;
  const g = must("gate");
  g.hidden = false;
  g.innerHTML = gateHTML(kind, msg);
  g.querySelector<HTMLInputElement>("input")?.focus();
}
function showApp() {
  const g = must("gate");
  g.hidden = true;
  g.innerHTML = "";
  (document.querySelector(".app") as HTMLElement).hidden = false;
  must("bNav").hidden = false;
}

async function init() {
  try {
    await afterAuth(await api.me());
  } catch (e) {
    showGate("login", e instanceof ApiError && e.code !== "auth" ? e.message : undefined);
  }
}

async function afterAuth(me: Me) {
  ME = me;
  if (me.aal === 1) return me.totpEnabled ? showGate("mfa") : startEnroll();
  if (me.mustChangePassword) return showGate("newpw");
  await loadAll();
}

async function startEnroll(msg?: string) {
  try {
    ENROLL = await api.totpSetup();
    showGate("enroll", msg);
  } catch (e) {
    showGate("login", (e as Error).message);
  }
}

/** Abgelaufene Anmeldung: zurück zum Login. */
function onAuthLost(e: unknown): boolean {
  if (e instanceof ApiError && (e.code === "auth" || e.status === 401)) {
    LOADED = false;
    ME = null;
    showGate("login", "Deine Anmeldung ist abgelaufen. Bitte neu anmelden.");
    return true;
  }
  return false;
}

async function loadAll() {
  try {
    const [{ state, rev }, info, mk] = await Promise.all([api.state(), api.sheetsInfo(), api.milkeeStatus().catch(() => ({ configured: false, company: "" }))]);
    MK = mk;
    if (mk.configured) void api.mkTimer().then((t) => ((MKD.timer = t), renderShell()), () => undefined);
    for (const k of STATE_KEYS) {
      SAVED[k] = JSON.stringify(state[k] ?? null);
      applyRemote(k, state[k]);
    }
    REV = rev;
    SA_EMAIL = info.email;
  } catch (e) {
    if (!onAuthLost(e)) showGate("error", (e as Error).message);
    return;
  }
  LOADED = true;
  showApp();
  render(true);
  startWatches();
  void loadNotifs(true);
  void loadHealth();
  void autoLinkMilkee();
}

async function logout() {
  await api.logout().catch(() => undefined);
  location.hash = "";
  location.reload();
}

/* ---------- Speichern: geänderte Bereiche an den Server ---------- */

let saveT: ReturnType<typeof setTimeout> | undefined;
let SAVING = false;
let AGAIN = false;
function save() {
  clearTimeout(saveT);
  SYNC = "saving";
  renderSync();
  saveT = setTimeout(() => void flush(), 350);
}
async function flush() {
  if (SAVING) {
    AGAIN = true;
    return;
  }
  SAVING = true;
  let failed = false;
  let foreign = false;
  for (const k of STATE_KEYS) {
    const j = JSON.stringify(S[k] ?? null);
    if (SAVED[k] === j) continue;
    const prev = SAVED[k];
    SAVED[k] = j;
    try {
      const r = await api.put(k, JSON.parse(j));
      if (r.rev !== REV + 1) foreign = true;
      REV = r.rev;
    } catch (e) {
      SAVED[k] = prev;
      failed = true;
      if (onAuthLost(e)) break;
      toast(e instanceof ApiError && e.code !== "network" ? `Nicht gespeichert: ${e.message}` : "Speichern hat nicht geklappt. Versuch es gleich nochmals.");
    }
  }
  SYNC = failed ? "error" : "ok";
  SAVING = false;
  renderSync();
  if (AGAIN) {
    AGAIN = false;
    void flush();
  } else if (foreign) void refreshState();
}

/** Änderungen von anderen Geräten oder Personen übernehmen. */
async function refreshState() {
  if (!LOADED || SAVING || SYNC === "saving") return;
  try {
    const { state, rev } = await api.state();
    let changed = false;
    for (const k of STATE_KEYS) {
      const j = JSON.stringify(state[k] ?? null);
      if (SAVED[k] === j) continue;
      SAVED[k] = j;
      applyRemote(k, state[k]);
      changed = true;
      if (k === "tables") startWatches();
    }
    REV = rev;
    if (changed) render();
    void loadNotifs();
  } catch (e) {
    onAuthLost(e);
  }
}
async function checkRev() {
  if (!LOADED || document.visibilityState !== "visible") return;
  try {
    const { rev } = await api.rev();
    if (rev !== REV) await refreshState();
  } catch (e) {
    onAuthLost(e);
  }
}
setInterval(() => void checkRev(), 30_000);
setInterval(() => void loadNotifs(), 60_000);
setInterval(() => void loadHealth(), 60_000);

/* ---------- Hinweise (Glocke oben) ---------- */

/** Desktop-Mitteilungen des Browsers (auch wenn der Tab im Hintergrund ist) */
const desktopOn = () => "Notification" in window && Notification.permission === "granted" && localStorage.getItem("desktopNotes") !== "off";
function desktopNote(n: NotificationRow) {
  try {
    const d = new Notification(n.title, { body: n.body, tag: n.id, icon: "/favicon-32.png" });
    d.onclick = () => {
      window.focus();
      if (n.link) {
        go(n.link);
        if (n.tab) {
          UI.ctab = n.tab;
          render(true);
        }
      }
      d.close();
    };
  } catch {
    /* Browser ohne Mitteilungen */
  }
}

async function loadNotifs(first = false) {
  // im Hintergrund nur nachschauen, wenn Desktop-Mitteilungen an sind
  if (!LOADED || (!first && document.visibilityState !== "visible" && !desktopOn())) return;
  try {
    const list = (await api.notifications()).notifications;
    const fresh = list.filter((n) => !NOTIF_SEEN.has(n.id) && !n.seen);
    NOTIFS = list;
    NOTIF_SEEN = new Set(list.map((n) => n.id));
    if (!first && fresh.length) {
      if (document.visibilityState !== "visible" && desktopOn()) fresh.slice(0, 5).forEach(desktopNote);
      else toast(fresh.length === 1 ? `${fresh[0]!.title}${fresh[0]!.body ? ` · ${fresh[0]!.body}` : ""}` : `${fresh.length} neue Hinweise`);
    }
    renderShell();
  } catch (e) {
    onAuthLost(e);
  }
}
const unseenNotifs = () => NOTIFS.filter((n) => !n.seen).length;
/** Glocke öffnen: Liste zeigen, alle als gesehen markieren (Zahl verschwindet), Hinweise bleiben stehen. */
function desktopHint() {
  if (!("Notification" in window)) return "";
  if (Notification.permission === "denied") return `<p class="help" style="margin:12px 0 0">Desktop-Mitteilungen sind im Browser blockiert. Erlauben kannst du sie in den Website-Einstellungen des Browsers.</p>`;
  if (desktopOn()) return `<p class="help" style="margin:12px 0 0">${ic("check", 13)} Desktop-Mitteilungen sind an, auch wenn der Tab im Hintergrund ist. <button class="btn ghost sm" data-a="desktop-off">Ausschalten</button></p>`;
  return `<div class="note-box" style="margin-top:12px">${ic("bell", 16)}<span>Hinweise auch als Mitteilung auf dem Computer, wenn der Tab im Hintergrund ist? <button class="btn sm pri" data-a="desktop-on">Einschalten</button></span></div>`;
}
function notifModal() {
  const ago = (iso: string) => {
    const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    return m < 1 ? "gerade eben" : m < 60 ? `vor ${m} Min.` : m < 1440 ? `vor ${Math.round(m / 60)} Std.` : fmtDate(iso);
  };
  const row = (n: NotificationRow) =>
    `<div class="notif-row ${n.seen ? "" : "new"}"><button class="opt notif" data-a="notif-go" data-id="${esc(n.id)}"><span class="lic">${ic(n.link.startsWith("abgabe-") ? "send" : "sheet", 16)}</span><span class="min0"><b>${esc(n.title)}</b>${n.body ? `<span class="notif-body">${esc(n.body)}</span>` : ""}<small>${ago(n.createdAt)}</small></span>${n.seen ? "" : '<span class="chip acc">Neu</span>'}</button><button class="ibtn sm" data-a="notif-del" data-id="${esc(n.id)}" title="Entfernen" aria-label="Entfernen">${ic("x", 14)}</button></div>`;
  const fresh = NOTIFS.filter((n) => !n.seen);
  const old = NOTIFS.filter((n) => n.seen);
  openModal(
    `${mhead("Hinweise")}${
      NOTIFS.length
        ? `<div class="stack notif-list" style="gap:6px">${fresh.map(row).join("")}${old.length ? `<div class="notif-sep">${fresh.length ? "Früher" : "Bereits gesehen"}</div>${old.map(row).join("")}` : ""}</div>${desktopHint()}<div class="modal-foot"><button class="btn ghost" data-a="notif-clear">Alle entfernen</button><button class="btn" data-a="close">Schliessen</button></div>`
        : `${emptyBox("Keine Hinweise", "Änderungen in den Live-Tabellen (neue Videos, v1 › v2, Final) und Rückmeldungen zu Abgaben erscheinen hier.")}${desktopHint()}<div class="modal-foot"><button class="btn" data-a="close">Schliessen</button></div>`
    }`,
  );
  if (fresh.length) {
    // Beim Öffnen gelten alle als gesehen; die Liste oben bleibt, bis das Fenster zu ist
    NOTIFS = NOTIFS.map((n) => ({ ...n, seen: true }));
    renderShell();
    void api.notificationsSeen().catch((err: unknown) => onAuthLost(err));
  }
}

function renderSync() {
  const el = byId("sync");
  if (!el) return;
  const [cls, txt] = SYNC === "saving" ? ["saving", "Speichert …"] : SYNC === "error" ? ["error", "Nicht gespeichert"] : ["", "Alles gespeichert"];
  el.className = `sync ${cls}`;
  el.innerHTML = `<i></i>${txt}`;
}

/* ============ Live-Tabellen (Google Sheets über den Server) ============ */

interface Live {
  state: "loading" | "ok" | "stale" | "error";
  items: LiveItem[];
  tabs: LiveTab[];
  title?: string;
  at?: number;
  err?: ApiError | null;
}
const LIVE: Record<string, Live> = {};
/** Kommentare der Kunden zu den Videos (Google Drive), pro Kunde nach Video */
const FEEDBACK: Record<string, Record<string, VideoFeedback>> = {};
let FB_EMAIL = "";
async function fetchFeedback(cid: string, refresh = false) {
  try {
    const r = await api.liveFeedback(cid, refresh);
    const before = JSON.stringify(FEEDBACK[cid] ?? {});
    FEEDBACK[cid] = r.feedback;
    FB_EMAIL = r.email || FB_EMAIL;
    if (JSON.stringify(r.feedback) !== before) liveRender();
  } catch {
    /* Kommentare sind ein Zusatz: still bleiben */
  }
}
const feedbackOf = (cid: string, it: LiveItem) => FEEDBACK[cid]?.[itemKey(it)];
/** aufgeklappte Kommentar-Boxen (bleiben beim Neuzeichnen offen, z. B. nach dem Abhaken) */
const FB_OPEN = new Set<string>();
document.addEventListener(
  "toggle",
  (e) => {
    const d = e.target as HTMLDetailsElement;
    const k = d.dataset?.fbk;
    if (!k) return;
    if (d.open) FB_OPEN.add(k);
    else FB_OPEN.delete(k);
  },
  true,
);
/** Eine Kommentarzeile mit Häkchen (nur für mich, der Kunde sieht es nicht) */
function fbItem(cid: string, c: VideoFeedback["comments"][number], when: (s: string) => string, extra = "") {
  return `<li class="${c.resolved ? "done" : ""} ${c.done ? "ticked" : ""}"><button class="check ${c.done ? "on" : ""}" data-a="fb-done" data-cid="${esc(cid)}" data-id="${esc(c.id)}" data-k="${c.done ? "0" : "1"}" title="${c.done ? "Wieder offen" : "Erledigt (nur für dich)"}" aria-label="${c.done ? "Wieder offen" : "Abhaken"}">${c.done ? ic("check", 13) : ""}</button><div class="min0"><div class="vfb-h"><b>${esc(c.author || "Kommentar")}</b><span>${esc(when(c.at))}${c.file ? ` · ${esc(c.file)}` : ""}${c.resolved ? " · in Drive erledigt" : ""}${extra}</span></div><p>${esc(c.text)}</p>${c.replies
    .map((r) => `<div class="vfb-r"><b>${esc(r.author)}</b> ${esc(r.text)}</div>`)
    .join("")}</div></li>`;
}
const fbWhen = (s: string) => {
  if (!s) return "";
  const m = Math.round((Date.now() - new Date(s).getTime()) / 60000);
  return m < 1 ? "gerade eben" : m < 60 ? `vor ${m} Min.` : m < 1440 ? `vor ${Math.round(m / 60)} Std.` : fmtDate(s);
};
/** Kommentare zu einem Video: kurz in der Zeile, aufklappbar mit allen Kommentaren und Antworten */
function feedbackBlock(cid: string, it: LiveItem, open = false) {
  const f = feedbackOf(cid, it);
  if (!f) return "";
  if (f.error === "not_shared" && !f.comments.length)
    return `<div class="vfb err">${ic("lock", 13)} Kommentare: Video ist nicht mit ${esc(FB_EMAIL || "dem Google-Dienstkonto")} geteilt.</div>`;
  if (!f.comments.length) return "";
  const todo = openComments(f);
  const fk = `${cid}|${itemKey(it)}`;
  const list = [...f.comments]
    .sort((a, b) => Number(a.resolved || !!a.done) - Number(b.resolved || !!b.done) || a.at.localeCompare(b.at))
    .map((c) => fbItem(cid, c, fbWhen))
    .join("");
  const head = todo.length ? `${todo.length} ${todo.length === 1 ? "offener Punkt" : "offene Punkte"}` : `${f.comments.length} ${f.comments.length === 1 ? "Kommentar" : "Kommentare"}, alles erledigt`;
  return `<details class="vfb ${todo.length ? "has" : ""}" data-fbk="${esc(fk)}" ${open || FB_OPEN.has(fk) ? "open" : ""}><summary>${ic("note", 14)}<span class="vfb-s"><b>${head}</b>${todo[0] ? `<span class="ellip">«${esc(todo[0].text)}»</span>` : ""}</span>${ic("chevd", 14)}</summary><ul>${list}</ul><a class="vfb-link" href="${esc(safeUrl(f.url))}" target="_blank" rel="noopener">${ic("play", 13)} Im Video ansehen</a></details>`;
}
const POLL: Record<string, { id: string; iv: ReturnType<typeof setInterval> }> = {};

async function fetchSheet(cid: string) {
  const t = S.tables[cid];
  if (!t || t.source !== "gsheet" || !t.id) return;
  try {
    const p = await api.sheets<GSpreadsheet>({ action: "get", spreadsheetId: t.id, fields: GS_FIELDS.join(",") });
    onLive(cid, p, null);
    void fetchFeedback(cid);
  } catch (e) {
    if (onAuthLost(e)) return;
    onLive(cid, null, e instanceof ApiError ? e : new ApiError("network", String(e)));
  }
}
function startWatches() {
  const want = Object.entries(S.tables).filter(([, t]) => t.source === "gsheet" && t.id) as [string, SheetTable & { id: string }][];
  for (const cid of Object.keys(POLL)) {
    if (!want.some(([c, t]) => c === cid && t.id === POLL[cid]?.id)) {
      clearInterval(POLL[cid]?.iv);
      delete POLL[cid];
      delete LIVE[cid];
    }
  }
  for (const [cid, t] of want) {
    if (POLL[cid]) continue;
    LIVE[cid] = { state: "loading", items: [], tabs: [] };
    void fetchSheet(cid);
    POLL[cid] = { id: t.id, iv: setInterval(() => document.visibilityState === "visible" && void fetchSheet(cid), 60_000) };
  }
  liveRender();
}
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible" || !LOADED) return;
  Object.keys(POLL).forEach((cid) => void fetchSheet(cid));
  void checkRev();
});
function onLive(cid: string, payload: GSpreadsheet | null, err: ApiError | null) {
  const L = (LIVE[cid] ??= { state: "loading", items: [], tabs: [] });
  if (payload) {
    if (!Array.isArray(payload.sheets)) {
      L.state = L.items.length ? "stale" : "error";
      L.err = new ApiError("parse", "Unerwartetes Format");
    } else {
      Object.assign(L, parseSheet(payload));
      L.state = "ok";
      L.err = null;
      L.at = Date.now();
    }
  } else if (err) {
    L.err = err;
    if (["not_shared", "not_found", "forbidden", "auth", "config", "bad_request"].includes(err.code)) {
      L.items = [];
      L.tabs = [];
      L.state = "error";
    } else L.state = L.items.length ? "stale" : "error";
  }
  liveRender();
}
function liveRender() {
  if (UI.view === "uebersicht" || (UI.view === "kunde" && LIVE[UI.cid])) render();
}

async function liveSet(sel: HTMLSelectElement) {
  const cid = UI.cid;
  const L = LIVE[cid];
  const t = S.tables[cid];
  const it = L?.items.find((x) => x.key === sel.dataset.key);
  if (!it || !t?.id) return;
  const f = sel.dataset.f === "todo" ? "todo" : "stand";
  const value = sel.value;
  const col = f === "stand" ? it.standCol : it.todoCol;
  if (col < 0) return;
  const prev = it[f];
  if (prev === value) return;
  it[f] = value;
  render();
  const range = `'${it.tab.replace(/'/g, "''")}'!${colLetter(col)}${it.row}`;
  try {
    await api.sheets({ action: "update", spreadsheetId: t.id, range, values: [[value]] });
    toast(`In Google Sheets gespeichert: ${it.video} → ${value || "offen"}`);
    void fetchSheet(cid);
    if (f === "stand" && isDone({ ...it, stand: value }) && billingOf(C(cid))?.auto) setTimeout(() => void runAutomation(cid, false), 800);
  } catch (e) {
    if (onAuthLost(e)) return;
    const c = e instanceof ApiError ? e.code : "network";
    if (c === "network" || c === "google_error" || c.startsWith("http_5")) {
      toast("Unklar, ob es gespeichert wurde. Die Tabelle wird neu geladen.");
      void fetchSheet(cid);
    } else {
      it[f] = prev;
      render();
      toast(c === "not_shared" ? `Social Solve Management darf nicht schreiben. Teile die Tabelle als Bearbeiter mit ${SA_EMAIL || "dem Dienstkonto"}.` : "Speichern ging gerade nicht. Versuch es nochmals.");
    }
  }
}

/* ============ Shell ============ */

type NavItem = [View, string, string];
const NAV: [string | null, NavItem[]][] = [
  [null, [["uebersicht", "Übersicht", "home"]]],
  [
    "Arbeit",
    [
      ["kunden", "Kunden", "users"],
      ["projekte", "Projekte", "board"],
      ["kalender", "Kalender", "cal"],
      ["skripte", "Skripte", "film"],
      ["abgaben", "Abgaben", "send"],
      ["zeit", "Zeiterfassung", "clock"],
    ],
  ],
  [
    "Buchhaltung",
    [
      ["finanzen", "Auswertung", "trend"],
      ["rechnungen", "Rechnungen", "receipt"],
      ["offerten", "Offerten", "offer"],
      ["buchungen", "Buchungen", "scale"],
      ["jahresabschluss", "Jahresabschluss", "download"],
    ],
  ],
];
const NAVFLAT: NavItem[] = [...NAV.flatMap((g) => g[1]), ["einstellungen", "Einstellungen", "gear"]];
const activeNav = () => (UI.view === "kunde" ? "kunden" : UI.view === "skript" ? "skripte" : UI.view === "abgabe" ? "abgaben" : UI.view === "rechnung" ? "rechnungen" : UI.view);

function renderShell() {
  const active = activeNav();
  const ap = activeProjects().length;
  const counts: Partial<Record<View, string>> = ap ? { projekte: `${ap} aktiv` } : {};
  must("sideNav").innerHTML = NAV.map(
    ([label, items]) =>
      (label ? `<div class="nav-label">${label}</div>` : "") +
      items.map(([k, l, i]) => `<a href="#${k}" class="${active === k ? "on" : ""}">${ic(i)}<span>${l}</span>${counts[k] ? `<span class="count">${counts[k]}</span>` : ""}</a>`).join(""),
  ).join("");
  must("sideFoot").innerHTML = `<nav class="nav"><a href="#einstellungen" class="${active === "einstellungen" ? "on" : ""}">${ic("gear")}<span>Einstellungen</span></a><a href="#" data-a="logout">${ic("lock")}<span>Abmelden</span></a></nav><div class="sync" id="sync"></div>`;
  const bn: NavItem[] = [
    ["uebersicht", "Übersicht", "home"],
    ["kunden", "Kunden", "users"],
    ["kalender", "Kalender", "cal"],
    ["projekte", "Projekte", "board"],
  ];
  must("bNav").innerHTML =
    bn.map(([k, l, i]) => `<a href="#${k}" class="${active === k ? "on" : ""}">${ic(i, 20)}<span>${l}</span></a>`).join("") +
    `<button data-a="more" class="${bn.some((b) => b[0] === active) ? "" : "on"}">${ic("more", 20)}<span>Mehr</span></button>`;
  must("mBrand").innerHTML = LOGO;
  must("cmdBtn").innerHTML = `${ic("search")}<span>Kunde, Projekt oder Seite suchen …</span><kbd>⌘ K</kbd>`;
  must("quickAddBtn").innerHTML = `${ic("plus", 16)}<span>Neu</span>`;
  const ns = byId("notifSlot");
  const un = unseenNotifs();
  const priv = document.documentElement.classList.contains("priv");
  if (ns) ns.innerHTML = `<button class="ibtn outline privbtn" data-a="priv" aria-pressed="${priv}" aria-label="${priv ? "Beträge zeigen" : "Beträge verbergen"}" title="${priv ? "Beträge zeigen" : "Beträge verbergen"}">${ic(priv ? "eyeoff" : "eye", 18)}</button><button class="ibtn outline bellbtn ${un ? "has" : ""}" data-a="notif-open" aria-label="Hinweise${un ? ` (${un} neu)` : ""}" title="Hinweise">${ic("bell", 18)}${un ? `<span class="badge">${un > 9 ? "9+" : un}</span>` : ""}</button>`;
  document.title = `${un ? `(${un}) ` : ""}Social Solve Management`;
  renderMilkeeDown();
  const tt = byId("topTimer");
  if (tt) tt.innerHTML = MKD.timer?.active && MKD.timer.startedAt ? `<button class="top-timer" data-a="go" data-to="zeit" title="Timer in MILKEE"><span class="pulse"></span><span data-elapsed="${esc(MKD.timer.startedAt)}">${elapsed(MKD.timer.startedAt)}</span></button>` : "";
  renderSync();
}

/* ---------- Privatmodus: Beträge standardmässig unscharf, Auge oben schaltet um ---------- */

function setPrivate(on: boolean) {
  document.documentElement.classList.toggle("priv", on);
  renderShell();
}
document.documentElement.classList.add("priv");
// Länger weg (anderer Tab, anderes Fenster, Mac-App im Hintergrund): wieder verbergen
let awaySince = 0;
const goneAway = () => {
  awaySince ||= Date.now();
};
const cameBack = () => {
  if (awaySince && Date.now() - awaySince > 3 * 60_000 && LOADED) setPrivate(true);
  awaySince = 0;
};
window.addEventListener("blur", goneAway);
window.addEventListener("focus", cameBack);
document.addEventListener("visibilitychange", () => (document.hidden ? goneAway() : cameBack()));

/* ---------- Systemstatus: Störungen oben sichtbar, Details in den Einstellungen ---------- */

let HEALTH: HealthStatus | null = null;
async function loadHealth() {
  if (!LOADED) return;
  try {
    const before = JSON.stringify(HEALTH);
    HEALTH = await api.healthStatus();
    renderHealthChip();
    if (JSON.stringify(HEALTH) !== before && (UI.view === "uebersicht" || (UI.view === "einstellungen" && UI.set === "verbindungen"))) render();
  } catch {
    /* Hinweis ist ein Zusatz */
  }
}
function renderHealthChip() {
  let el = byId("hlChip");
  const n = HEALTH?.issues.length ?? 0;
  if (!n) return el?.remove();
  if (!el) {
    el = document.createElement("span");
    el.id = "hlChip";
    byId("topTimer")?.before(el);
  }
  el.innerHTML = `<button class="hl-chip" data-a="health-open" title="${esc(HEALTH!.issues.map((i) => i.title).join(" · "))}">${ic("bell", 14)}<span>${n === 1 ? "Störung" : `${n} Störungen`}</span></button>`;
}
function healthBanner() {
  const list = HEALTH?.issues ?? [];
  if (!list.length) return "";
  return `<div class="note-box warn hl-banner">${ic("bell", 16)}<span>${list.map((i) => `<b>${esc(i.title)}.</b> ${esc(i.detail)}`).join("<br>")} <button class="sk-link" data-a="health-open">Status ansehen</button></span></div>`;
}
function healthPanel() {
  const h = HEALTH;
  const when = h?.checkedAt ? new Date(h.checkedAt).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }) : "";
  return `<section class="panel">${panelHead("Systemstatus", "Wird alle 3 Minuten geprüft. Bei einer Störung kommt ein Hinweis und eine Mitteilung aufs Handy, wenn es wieder läuft eine Entwarnung.")}
    ${
      h?.issues.length
        ? `<div class="note-box warn" style="margin-bottom:12px">${ic("bell", 16)}<span>${h.issues.map((i) => `<b>${esc(i.title)}</b><br>${esc(i.detail)}`).join("<br><br>")}</span></div>`
        : ""
    }
    <div class="hl-list">${(h?.checks ?? [])
      .map((c) => `<div class="hl-row"><span class="hl-dot ${c.ok ? "ok" : "bad"}"></span><b>${esc(c.label)}</b><span class="muted small">${esc(c.detail)}</span></div>`)
      .join("") || `<p class="muted small" style="margin:0">Die erste Prüfung läuft gleich.</p>`}</div>
    <div class="add-row" style="margin-top:12px;align-items:center"><button class="btn sm" data-a="health-check">${ic("refresh", 14)} Jetzt prüfen</button>${when ? `<span class="small dim">Zuletzt geprüft ${when}</span>` : ""}</div></section>`;
}

/** MILKEE gestört: oben ein ruhiger Hinweis statt Fehlermeldungen; das Tool zeigt den letzten Stand */
function renderMilkeeDown() {
  let el = byId("mkDown");
  if (!milkeeDown.since) return el?.remove();
  if (!el) {
    el = document.createElement("span");
    el.id = "mkDown";
    byId("topTimer")?.before(el);
  }
  const d = new Date(milkeeDown.since);
  const today = d.toDateString() === new Date().toDateString();
  const when = `${today ? "" : `${d.getDate()}.${d.getMonth() + 1}. `}${d.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}`;
  el.innerHTML = `<span class="mk-down" title="MILKEE ist gerade in Wartung oder gestört (bei MILKEE, nicht im Tool). Du siehst den letzten Stand von ${esc(when)}. Ändern in MILKEE (Rechnungen, Timer, Buchungen) geht wieder, sobald MILKEE zurück ist – das Tool merkt es von selbst.">${ic("cloud", 15)}<span><b>MILKEE in Wartung</b><small>Stand ${esc(when)}</small></span></span>`;
}
milkeeDown.onChange = () => renderMilkeeDown();

/* ============ Bausteine ============ */

const pageHead = (eyebrow: string, title: string, lede: string, acts = "") =>
  `<header class="page-head"><div class="min0">${eyebrow ? `<div class="eyebrow">${eyebrow}</div>` : ""}<h1 class="h1">${title}</h1>${lede ? `<p class="lede">${lede}</p>` : ""}</div>${acts ? `<div class="acts">${acts}</div>` : ""}</header>`;
const emptyBox = (title: string, text: string, btn = "") => `<div class="empty"><b>${title}</b><span>${text}</span>${btn}</div>`;
function clientOptions(sel: string, none?: string) {
  return (
    (none !== undefined ? `<option value="">${none}</option>` : "") +
    S.clients
      .filter((c) => c.status !== "archiv" || c.id === sel)
      .map((c) => `<option value="${esc(c.id)}" ${c.id === sel ? "selected" : ""}>${esc(c.name)}</option>`)
      .join("")
  );
}
function upcomingList(items: Upcoming[]) {
  if (!items.length) return emptyBox("Nichts fällig", "In den nächsten 30 Tagen steht nichts an.");
  return items
    .map((i) => {
      const d = new Date(i.due);
      const n = daysFrom(i.due);
      const c = C(i.cid);
      const st = i.state;
      // abgegeben: kein Countdown, sondern «✓ v2 abgegeben»; Feedback zurück: Hinweis plus Countdown
      const chips = st?.waiting ? `<span class="chip ok">${ic("check", 12)} ${esc(st.label)}</span>` : `${st ? `<span class="chip ${st.cls} hide-m">${esc(st.label)}</span>` : ""}<span class="chip ${relCls(n)}">${rel(n)}</span>`;
      return `<button class="row ${st?.waiting ? "waiting" : ""}" data-a="go" data-to="${esc(i.to)}" ${i.ctab ? `data-ctab="${i.ctab}"` : ""}><div class="when"><b>${d.getDate()}</b><span>${WD[d.getDay()]}</span></div><div class="grow"><div class="rt ellip">${esc(i.title)}</div><div class="rs ellip">${cdot(c)} ${esc(c?.name ?? "")} · ${esc(i.sub || i.kind)}</div></div><span class="row-chips">${chips}</span></button>`;
    })
    .join("");
}
function notesBlock(cid: string) {
  const sorted = [...(S.notes[cid] ?? [])].sort((a, b) => Number(a.done) - Number(b.done) || b.at - a.at);
  const id = esc(cid);
  return `<form class="add-row" data-form="note" data-cid="${id}" style="margin-bottom:12px"><select class="in" id="nk-${id}" style="width:auto" aria-label="Art"><option value="note">Notiz</option><option value="todo">To-do</option></select><input class="in" id="ni-${id}" placeholder="Kurz notieren und Enter drücken" style="flex:1" autocomplete="off"></form>
  ${
    sorted.length
      ? sorted
          .map(
            (n) =>
              n.kind === "todo"
                ? todoRow(cid, n)
                : `<div class="note"><span class="dim" style="margin-top:2px">${ic("note", 16)}</span><p>${esc(n.text)}<br><span class="small dim">${fmtDate(new Date(n.at).toISOString())}</span></p><button class="ibtn" data-a="note-del" data-cid="${id}" data-id="${esc(n.id)}" aria-label="Löschen">${ic("x", 15)}</button></div>`,
          )
          .join("")
      : emptyBox("Noch keine Notizen", "Halte hier fest, was du bei diesem Kunden nicht vergessen willst.")
  }`;
}

/* ============ To-dos mit Tag ============ */

const todayDay = () => isoDay(new Date());
const dayDate = (day: string) => new Date(`${day}T12:00:00`);
/** «Heute», «Morgen», «Do, 30. Okt.» */
function dayLabel(day: string) {
  const n = daysFrom(`${day}T12:00:00`);
  if (n === 0) return "Heute";
  if (n === 1) return "Morgen";
  if (n === -1) return "Gestern";
  const d = dayDate(day);
  return `${WD[d.getDay()]}, ${d.getDate()}. ${MON[d.getMonth()]}`;
}
const timeSpan = (from?: string | null, to?: string | null) => (from ? `${from}${to ? `–${to}` : ""}` : "");
const whenText = (n: Pick<Note, "day" | "from" | "to">) => (n.day ? [dayLabel(n.day), timeSpan(n.from, n.to)].filter(Boolean).join(" · ") : "");
/** mit Tag zuerst (früheste oben), dann ohne Tag (neueste oben) */
const todoSort = (a: Note, b: Note) => (a.day ?? "9999").localeCompare(b.day ?? "9999") || (a.from ?? "").localeCompare(b.from ?? "") || b.at - a.at;

function todoRow(cid: string, n: Note, showClient = false) {
  const c = C(cid);
  const late = !!n.day && !n.done && n.day < todayDay();
  const when = `<button class="when-chip ${n.day ? (late ? "late" : "set") : ""}" data-a="todo-when" data-cid="${esc(cid)}" data-id="${esc(n.id)}" title="${n.day ? "Tag oder Zeit ändern" : "Sagen, wann du es machst"}">${ic("cal", 13)} ${n.day ? esc(whenText(n)) : "Wann?"}</button>`;
  const who = showClient && c ? `<a class="small dim" href="#kunde-${esc(cid)}">${cdot(c)} ${esc(c.name)}</a>` : "";
  const proj = n.project ? S.projects.find((p) => p.id === n.project) : undefined;
  const pj = proj ? `<button class="small dim todo-proj" data-a="proj-open" data-id="${esc(proj.id)}">${ic("board", 12)} ${esc(proj.title)}</button>` : "";
  return `<div class="note todo ${n.done ? "done" : ""}"><button class="check ${n.done ? "on" : ""}" data-a="note-done" data-cid="${esc(cid)}" data-id="${esc(n.id)}" aria-label="${n.done ? "Wieder offen" : "Erledigt"}">${n.done ? ic("check", 14) : ""}</button><div class="todo-b"><p>${esc(n.text)}</p><div class="todo-meta">${when}${who}${pj}</div></div><button class="ibtn" data-a="note-del" data-cid="${esc(cid)}" data-id="${esc(n.id)}" aria-label="Löschen">${ic("x", 15)}</button></div>`;
}

/** Neues To-do: Text, optional Tag und Von–Bis (über die eigene Auswahl). Ohne festen Kunden mit Auswahl. */
function todoForm(cid: string | null, p: string, day = "") {
  const pick = cid === null ? `<select class="in" id="${p}-c" aria-label="Kunde">${clientOptions(UI.lastTodoCid || S.clients.find((c) => c.status !== "archiv")?.id || "")}</select>` : "";
  return `<form class="todo-add" data-form="todo" data-cid="${cid === null ? "" : esc(cid)}" data-p="${p}">${pick}<input class="in todo-t" id="${p}-t" placeholder="Neues To-do …" autocomplete="off"><input type="hidden" id="${p}-d" value="${esc(day)}"><input type="hidden" id="${p}-f"><input type="hidden" id="${p}-u"><button type="button" class="when-chip ${day ? "set" : ""}" id="${p}-w" data-a="wp-form" data-k="${p}">${ic("cal", 13)} ${day ? esc(dayLabel(day)) : "Wann?"}</button><button class="btn pri sm" type="submit" aria-label="To-do hinzufügen">${ic("plus", 15)}</button></form>`;
}

function todoList(cid: string) {
  const all = (S.notes[cid] ?? []).filter((n) => n.kind === "todo");
  const open = all.filter((n) => !n.done).sort(todoSort);
  const done = all.filter((n) => n.done).sort((a, b) => b.at - a.at);
  return (open.length ? open.map((n) => todoRow(cid, n)).join("") : `<p class="small dim" style="margin:10px 0 0">Keine offenen To-dos.</p>`) + (done.length ? `<details class="todo-done"><summary class="small dim">${done.length} erledigt</summary>${done.slice(0, 20).map((n) => todoRow(cid, n)).join("")}</details>` : "");
}

const findNote = (cid: string, id: string) => (S.notes[cid] ?? []).find((n) => n.id === id);
const exchangeHint = () => (MSI?.connected && MSI.calendarName ? `Erscheint auch in Exchange im Kalender «${esc(MSI.calendarName)}».` : "");

function todoWhenModal(cid: string, id: string) {
  const n = findNote(cid, id);
  if (!n) return;
  wpStart(n.day ?? todayDay(), n.from, n.to);
  openModal(`${mhead("Wann machst du das?")}<p class="muted" style="margin:0">${esc(n.text)} · ${esc(C(cid)?.name ?? "")}</p>
  <form data-form="todo-when" data-cid="${esc(cid)}" data-id="${esc(id)}" class="stack" style="gap:14px">${wpHtml()}
    <p class="help" style="margin:0">${exchangeHint()}</p>
    <div class="modal-foot"><button type="button" class="btn ghost" data-a="close">Abbrechen</button><button class="btn pri" type="submit">Speichern</button></div>
  </form>`, "wp-modal");
  wpPlace();
}

function todoNewModal(day = "") {
  if (!S.clients.length) return toast("Zuerst einen Kunden erstellen.");
  wpStart(day, null, null);
  openModal(`${mhead("Neues To-do")}<form class="stack" data-form="todo" data-wp="1" data-p="nt" style="gap:12px"><input class="in" id="nt-t" placeholder="Was ist zu tun?" autocomplete="off"><select class="in" id="nt-c" aria-label="Kunde" data-a="nt-client">${clientOptions(UI.lastTodoCid || S.clients.find((c) => c.status !== "archiv")?.id || "")}</select><select class="in" id="nt-p" aria-label="Projekt (optional)">${projectOptions(UI.lastTodoCid || S.clients.find((c) => c.status !== "archiv")?.id || "")}</select>${wpHtml()}<p class="help" style="margin:0">${exchangeHint()}</p><div class="modal-foot"><button type="button" class="btn ghost" data-a="close">Abbrechen</button><button class="btn pri" type="submit">Hinzufügen</button></div></form>`, "wp-modal");
  wpPlace();
}

/** Offene Projekte eines Kunden für ein To-do (optional) */
function projectOptions(cid: string) {
  const ps = activeProjects(cid);
  return `<option value="">${ps.length ? "Ohne Projekt" : "Kein offenes Projekt"}</option>${ps.map((p) => `<option value="${esc(p.id)}">${esc(p.title)}</option>`).join("")}`;
}

/** «Wann?» an einem Formular in der Seite: Auswahl im Fenster, Ergebnis zurück ins Formular */
function wpFormModal(p: string) {
  wpStart(val(`${p}-d`) || todayDay(), val(`${p}-f`) || null, val(`${p}-u`) || null);
  openModal(`${mhead("Wann machst du das?")}${wpHtml()}<div class="modal-foot"><button type="button" class="btn ghost" data-a="close">Abbrechen</button><button type="button" class="btn pri" data-a="wp-apply" data-k="${esc(p)}">Übernehmen</button></div>`, "wp-modal");
  wpPlace();
}

/** Tag und Zeit aus versteckten Feldern lesen (von der Auswahl gesetzt) */
function readWhen(d: string, f: string, u: string): Pick<Note, "day" | "from" | "to"> {
  return d ? { day: d, from: f || null, to: f && u ? u : null } : { day: null, from: null, to: null };
}

/* ---------- Eigene Auswahl für Tag und Zeit (statt der Felder des Browsers) ---------- */

/** Höhe einer Zeile im Zeit-Rad, gleich wie im CSS */
const WH = 40;
const two = (n: number) => String(n).padStart(2, "0");
const WP = { day: "", month: "", timed: false, from: "09:00", to: "10:00", edit: "from" as "from" | "to", quiet: 0, needDay: false };
const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const fromMin = (m: number) => `${two(Math.floor(Math.min(Math.max(m, 0), 1435) / 60))}:${two(Math.min(Math.max(m, 0), 1435) % 60)}`;
/** auf 5 Minuten runden (die Räder haben 5-Minuten-Schritte) */
const round5 = (t: string) => fromMin(Math.round(toMin(t) / 5) * 5);

function wpStart(day: string, from: string | null | undefined, to: string | null | undefined, needDay = false) {
  WP.needDay = needDay;
  WP.day = day;
  WP.month = `${(day || todayDay()).slice(0, 8)}01`;
  WP.timed = !!from;
  WP.from = from ? round5(from) : "09:00";
  WP.to = to ? round5(to) : fromMin(toMin(WP.from) + 60);
  WP.edit = "from";
}

/** Ergebnis der Auswahl; null, wenn «bis» nicht nach «von» liegt */
function wpValue(): Pick<Note, "day" | "from" | "to"> | null {
  if (!WP.day) return { day: null, from: null, to: null };
  if (!WP.timed) return { day: WP.day, from: null, to: null };
  if (WP.to <= WP.from) return toast("«Bis» muss nach «Von» sein."), null;
  return { day: WP.day, from: WP.from, to: WP.to };
}

const wpHtml = () => `<div class="wp" id="wp">${wpInner()}</div>`;

function wpInner() {
  const today = todayDay();
  const m = dayDate(WP.month);
  const first = new Date(m.getFullYear(), m.getMonth(), 1, 12);
  const start = addDays(isoDay(first), -((first.getDay() + 6) % 7));
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i))
    .map((d) => `<button type="button" class="wp-d ${dayDate(d).getMonth() !== m.getMonth() ? "out" : ""} ${d === today ? "today" : ""} ${d === WP.day ? "on" : ""} ${d < today ? "past" : ""}" data-a="wp-day" data-k="${d}">${dayDate(d).getDate()}</button>`)
    .join("");
  const wd = dayDate(today).getDay();
  const quick: [string, string][] = [
    ["Heute", today],
    ["Morgen", addDays(today, 1)],
    ["Übermorgen", addDays(today, 2)],
    ["Nächster Montag", addDays(today, ((8 - wd) % 7) || 7)],
    ["In 1 Woche", addDays(today, 7)],
    ["Ohne Tag", ""],
  ];
  const sum = WP.day ? whenText({ day: WP.day, from: WP.timed ? WP.from : null, to: WP.timed ? WP.to : null }) : "Ohne Tag";
  const dur = toMin(WP.to) - toMin(WP.from);
  const cur = WP.edit === "from" ? WP.from : WP.to;
  const wheel = (k: "h" | "m", vals: string[], v: string) => `<div class="wheel" data-wheel="${k}">${vals.map((x) => `<button type="button" class="wh ${x === v ? "on" : ""}" data-a="wp-wh" data-k="${k}" data-v="${x}">${x}</button>`).join("")}</div>`;
  const time = WP.timed
    ? `<div class="wp-tabs"><button type="button" class="wp-tab ${WP.edit === "from" ? "on" : ""}" data-a="wp-edit" data-k="from"><span>Von</span><b>${WP.from}</b></button><span class="dim">–</span><button type="button" class="wp-tab ${WP.edit === "to" ? "on" : ""} ${WP.to <= WP.from ? "bad" : ""}" data-a="wp-edit" data-k="to"><span>Bis</span><b>${WP.to}</b></button></div>
      <div class="wp-wheels">${wheel("h", Array.from({ length: 24 }, (_, i) => two(i)), cur.slice(0, 2))}<span class="wp-colon">:</span>${wheel("m", Array.from({ length: 12 }, (_, i) => two(i * 5)), cur.slice(3, 5))}</div>
      <div class="wp-chips">${[30, 60, 90, 120, 180, 240].map((d) => `<button type="button" class="wp-chip ${d === dur ? "on" : ""}" data-a="wp-dur" data-k="${d}">${d < 60 ? `${d} Min` : d % 60 ? `${Math.floor(d / 60)}½ Std` : `${d / 60} Std`}</button>`).join("")}</div>`
    : "";
  return `<div class="wp-sum ${WP.day ? "" : "none"}">${ic("cal", 15)}<b>${esc(sum)}</b></div>
  <div class="wp-chips">${quick.filter(([, d]) => d || !WP.needDay).map(([l, d]) => `<button type="button" class="wp-chip ${d === WP.day ? "on" : ""}" data-a="wp-day" data-k="${d}">${l}</button>`).join("")}</div>
  ${
    WP.day
      ? `<div class="wp-cal"><div class="wp-head"><button type="button" class="ibtn sm" data-a="wp-month" data-k="-1" aria-label="Voriger Monat">${ic("left", 15)}</button><b>${MONL[m.getMonth()]} ${m.getFullYear()}</b><button type="button" class="ibtn sm" data-a="wp-month" data-k="1" aria-label="Nächster Monat"><span style="display:inline-flex;transform:rotate(180deg)">${ic("left", 15)}</span></button></div>
    <div class="wp-grid">${["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((w) => `<span>${w}</span>`).join("")}${cells}</div></div>
  <div class="wp-mode"><button type="button" class="${WP.timed ? "" : "on"}" data-a="wp-timed" data-k="0">Ganzer Tag</button><button type="button" class="${WP.timed ? "on" : ""}" data-a="wp-timed" data-k="1">Mit Uhrzeit</button></div>
  ${time}`
      : ""
  }`;
}

/** Räder auf den gewählten Wert drehen (ohne dass das Drehen selbst etwas ändert) */
function wpPlace() {
  WP.quiet = Date.now() + 250;
  document.querySelectorAll<HTMLElement>("#wp .wheel").forEach((w) => {
    const i = [...w.children].findIndex((c) => c.classList.contains("on"));
    if (i >= 0) w.scrollTop = i * WH;
  });
}
function wpRender() {
  const el = byId("wp");
  if (!el) return;
  el.innerHTML = wpInner();
  wpPlace();
}

/** Stunde oder Minute setzen; beim Ändern von «von» bleibt die Dauer gleich */
function wpSetTime(part: "h" | "m", v: string) {
  const cur = WP.edit === "from" ? WP.from : WP.to;
  const next = part === "h" ? `${v}:${cur.slice(3, 5)}` : `${cur.slice(0, 2)}:${v}`;
  if (WP.edit === "from") {
    const dur = Math.max(toMin(WP.to) - toMin(WP.from), 15);
    WP.from = next;
    WP.to = fromMin(toMin(next) + dur);
  } else WP.to = next;
}

/** Rad mit Finger oder Trackpad gedreht: Wert in der Mitte übernehmen */
let wpScrollT: ReturnType<typeof setTimeout> | undefined;
document.addEventListener(
  "scroll",
  (e) => {
    const w = e.target as HTMLElement;
    if (!(w instanceof HTMLElement) || !w.classList.contains("wheel") || !w.closest("#wp")) return;
    clearTimeout(wpScrollT);
    wpScrollT = setTimeout(() => {
      if (Date.now() < WP.quiet) return;
      const items = w.children;
      const item = items[Math.min(Math.max(Math.round(w.scrollTop / WH), 0), items.length - 1)] as HTMLElement | undefined;
      if (!item || item.classList.contains("on")) return;
      wpSetTime(w.dataset.wheel as "h" | "m", item.dataset.v ?? "00");
      wpRender();
    }, 140);
  },
  true,
);

/* ============ Kalender ============ */

const CALV = {
  /** erster Tag des gezeigten Monats */
  month: "",
  sel: "",
  key: "",
  at: 0,
  data: null as CalendarResponse | null,
  loading: false,
  show: { todo: true, mine: true, others: false, events: true },
};
try {
  Object.assign(CALV.show, JSON.parse(localStorage.getItem("cal-show") ?? "{}"));
} catch {
  /* ohne gespeicherte Auswahl */
}

function calRange() {
  const m = dayDate(CALV.month);
  const first = new Date(m.getFullYear(), m.getMonth(), 1, 12);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));
  const from = isoDay(start);
  return { from, to: addDays(from, 41), month: first.getMonth() };
}

async function loadCalendar(force = false) {
  const { from, to } = calRange();
  const key = `${from}|${to}`;
  if (CALV.loading || (!force && CALV.key === key && Date.now() - CALV.at < 120_000)) return;
  CALV.loading = true;
  try {
    CALV.data = await api.calendar(from, to);
    CALV.key = key;
    CALV.at = Date.now();
    MSI = CALV.data.ms;
  } catch (e) {
    if (!onAuthLost(e)) toast(e instanceof ApiError ? e.message : "Kalender nicht geladen");
  } finally {
    CALV.loading = false;
  }
  if (UI.view === "kalender") render();
}

type CalItem = { kind: "todo"; cid: string; n: Note } | { kind: "deadline"; d: CalDeadline } | { kind: "event"; e: CalEvent };

/** Alles pro Tag: To-dos direkt aus dem Tool (sofort aktuell), Deadlines und Termine vom Server */
function calItems(from: string, to: string) {
  const by = new Map<string, CalItem[]>();
  const add = (day: string, it: CalItem) => {
    if (day < from || day > to) return;
    const l = by.get(day) ?? [];
    l.push(it);
    by.set(day, l);
  };
  if (CALV.show.todo) for (const c of S.clients) for (const n of S.notes[c.id] ?? []) if (n.kind === "todo" && n.day) add(n.day, { kind: "todo", cid: c.id, n });
  for (const d of CALV.data?.deadlines ?? []) if ((d.mine && CALV.show.mine) || (!d.mine && CALV.show.others)) add(d.due, { kind: "deadline", d });
  if (CALV.show.events)
    for (const e of CALV.data?.events ?? []) {
      const s = e.start.slice(0, 10);
      // ganztägig über mehrere Tage: an jedem Tag zeigen (Ende ist exklusiv)
      const last = e.allDay ? addDays(e.end.slice(0, 10) || s, -1) : e.end.slice(0, 10) || s;
      for (let d = s, i = 0; d <= last && i < 42; d = addDays(d, 1), i++) add(d, { kind: "event", e });
    }
  const t = (it: CalItem) => (it.kind === "todo" ? (it.n.from ?? "") : it.kind === "event" ? (it.e.allDay ? "" : it.e.start.slice(11, 16)) : "");
  const order = { deadline: 0, event: 1, todo: 2 };
  for (const l of by.values()) l.sort((a, b) => t(a).localeCompare(t(b)) || order[a.kind] - order[b.kind]);
  return by;
}

function calChip(it: CalItem) {
  if (it.kind === "todo") {
    const c = C(it.cid);
    return `<span class="cal-i todo ${it.n.done ? "done" : ""}" style="--c:${esc(c?.color || "var(--accent)")}">${it.n.from ? `<b>${it.n.from}</b> ` : ""}${esc(it.n.text)}</span>`;
  }
  if (it.kind === "deadline") return `<span class="cal-i dline ${it.d.mine ? "mine" : ""} ${it.d.done ? "done" : ""}" style="--c:${esc(C(it.d.clientId)?.color || "var(--warn)")}">${ic("film", 11)} ${esc(it.d.video.replace(/_+/g, " "))}</span>`;
  return `<span class="cal-i ev">${it.e.allDay ? "" : `<b>${it.e.start.slice(11, 16)}</b> `}${esc(it.e.subject)}</span>`;
}

function calRow(it: CalItem) {
  if (it.kind === "todo") return todoRow(it.cid, it.n, true);
  if (it.kind === "deadline") {
    const d = it.d;
    const c = C(d.clientId);
    const p = S.projects.find((x) => x.client === d.clientId && x.sheetKey === d.key);
    const open = p ? `data-a="proj-open" data-id="${esc(p.id)}"` : `data-a="go" data-to="kunde-${esc(d.clientId)}" data-ctab="table"`;
    return `<button class="cal-row" ${open}><span class="cal-ic dline" style="--c:${esc(c?.color || "var(--warn)")}">${ic("film", 15)}</span><span class="min0 grow"><b class="ellip">Deadline: ${esc(d.video.replace(/_+/g, " "))}</b><small class="ellip">${cdot(c)} ${esc(c?.name ?? "")}${d.section ? ` · ${esc(d.section)}` : ""}${d.stand ? ` · ${esc(d.stand)}` : ""}${d.mine ? "" : ` · bei ${esc(d.todo)}`}</small></span>${d.done ? `<span class="chip ok">${ic("check", 12)} fertig</span>` : ""}</button>`;
  }
  const e = it.e;
  const time = e.allDay ? "Ganzer Tag" : `${e.start.slice(11, 16)}–${e.end.slice(11, 16)}`;
  const inner = `<span class="cal-ic ev">${ic("cal", 15)}</span><span class="min0 grow"><b class="ellip">${esc(e.subject || "Termin")}</b><small class="ellip">${time}${e.location ? ` · ${esc(e.location)}` : ""} · ${esc(e.calendar)}</small></span>`;
  if (e.editable) return `<button class="cal-row" data-a="ev-edit" data-id="${esc(e.id)}" title="Termin bearbeiten">${inner}${ic("edit", 14)}</button>`;
  return e.webLink ? `<a class="cal-row" href="${esc(safeUrl(e.webLink))}" target="_blank" rel="noopener" title="In Outlook öffnen">${inner}${ic("ext", 14)}</a>` : `<div class="cal-row">${inner}</div>`;
}

function vKalender() {
  if (!CALV.month) CALV.month = `${todayDay().slice(0, 8)}01`;
  if (!CALV.sel) CALV.sel = todayDay();
  const { from, to, month } = calRange();
  void loadCalendar();
  const by = calItems(from, to);
  const today = todayDay();
  const ms = CALV.data?.ms ?? MSI;
  const m = dayDate(CALV.month);
  const exch =
    ms?.connected && ms.calendarId
      ? `<span class="chip ${ms.error ? "warn" : "ok"}" title="${esc(ms.error ?? (ms.lastSync ? `Zuletzt abgeglichen ${new Date(ms.lastSync).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}` : ""))}">${ic(ms.error ? "x" : "check", 12)} Exchange · ${esc(ms.calendarName)}</span><button class="btn sm" data-a="cal-sync">${ic("refresh", 14)} Abgleichen</button>`
      : ms
        ? `<a class="btn sm" href="#einstellungen">${ic("cloud", 14)} Mit Exchange verbinden</a>`
        : "";
  const head = pageHead("Arbeit", "Kalender", "To-dos, Video-Deadlines und deine Termine aus Exchange an einem Ort.", `${exch}${ms?.connected && ms.calendarId ? `<button class="btn sm" data-a="ev-new" data-k="${esc(CALV.sel)}">${ic("cal", 14)} Termin</button>` : ""}<button class="btn pri sm" data-a="todo-new" data-k="${esc(CALV.sel)}">${ic("plus", 14)} To-do</button>`);
  const tog = (k: keyof typeof CALV.show, label: string) => `<button class="seg-b ${CALV.show[k] ? "on" : ""}" data-a="cal-show" data-k="${k}">${label}</button>`;
  const days: string[] = [];
  for (let i = 0; i < 42; i++) days.push(addDays(from, i));
  const grid = days
    .map((d) => {
      const l = by.get(d) ?? [];
      const out = dayDate(d).getMonth() !== month;
      return `<button class="cal-d ${out ? "out" : ""} ${d === today ? "today" : ""} ${d === CALV.sel ? "sel" : ""}" data-a="cal-day" data-k="${d}"><span class="cal-n">${dayDate(d).getDate()}</span><span class="cal-l">${l.slice(0, 3).map(calChip).join("")}${l.length > 3 ? `<span class="cal-more">+${l.length - 3}</span>` : ""}</span>${l.length ? `<span class="cal-dots">${l.slice(0, 4).map((it) => `<i class="${it.kind}"></i>`).join("")}</span>` : ""}</button>`;
    })
    .join("");
  // Agenda: gewählter Tag und die 6 Tage danach
  const agenda = [0, 1, 2, 3, 4, 5, 6]
    .map((i) => addDays(CALV.sel, i))
    .map((d) => {
      const l = (d >= from && d <= to ? by : calItems(d, d)).get(d) ?? [];
      if (!l.length && d !== CALV.sel) return "";
      return `<div class="ag-day ${d === today ? "today" : ""}"><div class="ag-h"><b>${dayLabel(d)}</b><span class="small dim">${d === today || daysFrom(`${d}T12:00:00`) === 1 ? `${WD[dayDate(d).getDay()]}, ${dayDate(d).getDate()}. ${MON[dayDate(d).getMonth()]}` : ""}</span><button class="ibtn sm" data-a="todo-new" data-k="${d}" title="To-do an diesem Tag" aria-label="To-do an diesem Tag">${ic("plus", 14)}</button></div>${l.length ? l.map(calRow).join("") : `<p class="small dim" style="margin:4px 0 0">Nichts geplant.</p>`}</div>`;
    })
    .join("");
  const undated = S.clients.flatMap((c) => (S.notes[c.id] ?? []).filter((n) => n.kind === "todo" && !n.done && !n.day).map((n) => ({ c, n })));
  return `${head}
  <div class="cal-bar"><div class="cal-nav"><button class="ibtn outline" data-a="cal-month" data-k="-1" aria-label="Voriger Monat">${ic("left", 16)}</button><h2 class="h2">${MONL[m.getMonth()]} ${m.getFullYear()}</h2><button class="ibtn outline" data-a="cal-month" data-k="1" aria-label="Nächster Monat"><span style="display:inline-flex;transform:rotate(180deg)">${ic("left", 16)}</span></button><button class="btn ghost sm" data-a="cal-month" data-k="0">Heute</button>${CALV.loading ? `<span class="small dim">lädt …</span>` : ""}</div>
    <div class="seg">${tog("todo", "To-dos")}${tog("mine", "Meine Deadlines")}${tog("others", "Andere Deadlines")}${tog("events", "Termine")}</div></div>
  <div class="cal-wrap">
    <section class="panel flush cal-grid-p"><div class="cal-wd">${["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((w) => `<span>${w}</span>`).join("")}</div><div class="cal-grid">${grid}</div></section>
    <div class="col-stack"><section class="panel cal-ag">${agenda}</section>
    ${undated.length ? `<section class="panel"><div class="panel-head"><h2 class="h2">Ohne Tag</h2><span class="small dim">${undated.length}</span></div>${undated.slice(0, 12).map(({ c, n }) => todoRow(c.id, n, true)).join("")}</section>` : ""}</div>
  </div>`;
}

/** Termin wie in Outlook: Titel, Ort, Tag, ganzer Tag oder von–bis */
function eventModal(id = "", day = "") {
  const e = id ? CALV.data?.events.find((x) => x.id === id) : undefined;
  if (id && !e) return toast("Diesen Termin gibt es nicht mehr.");
  if (e) wpStart(e.start.slice(0, 10), e.allDay ? null : e.start.slice(11, 16), e.allDay ? null : e.end.slice(11, 16), true);
  else wpStart(day || todayDay(), "09:00", "10:00", true);
  const cal = e?.calendar ?? CALV.data?.ms.calendarName ?? "";
  openModal(`${mhead(e ? "Termin bearbeiten" : "Neuer Termin")}<form class="stack" data-form="event" data-id="${esc(id)}" style="gap:12px"><input class="in" id="ev-s" placeholder="Titel, z. B. Dreh bei Arbopark" value="${esc(e?.subject ?? "")}" autocomplete="off"><input class="in" id="ev-l" placeholder="Ort (optional)" value="${esc(e?.location ?? "")}" autocomplete="off">${wpHtml()}<p class="help" style="margin:0">Im Exchange-Kalender «${esc(cal)}», wie in Outlook.</p><div class="modal-foot">${e ? `<button type="button" class="btn ghost" data-a="ev-del" data-id="${esc(id)}">${ic("trash", 14)} Löschen</button>${e.webLink ? `<a class="btn ghost" href="${esc(safeUrl(e.webLink))}" target="_blank" rel="noopener">${ic("ext", 14)} Outlook</a>` : ""}` : ""}<button type="button" class="btn ghost" data-a="close">Abbrechen</button><button class="btn pri" type="submit">${e ? "Speichern" : "Eintragen"}</button></div></form>`, "wp-modal");
  wpPlace();
}

/* ============ Microsoft 365 (Exchange-Kalender) ============ */

let MSI: MsStatus | null = null;
let MSCALS: MsCalendar[] | null = null;
function msCard() {
  if (!isAdmin()) return "";
  if (MSI === null) {
    MSI = { configured: false, tenantId: "", clientId: "", connected: false, account: "", calendarId: "", calendarName: "", showIds: [], lastSync: null, error: null, redirectUri: `${location.origin}/api/ms/callback` };
    void api.msSettings().then(
      (i) => {
        MSI = i;
        if (UI.view === "einstellungen") render();
      },
      () => undefined,
    );
  }
  const s = MSI;
  if (s.connected && MSCALS === null) {
    MSCALS = [];
    void api.msCalendars().then(
      (r) => {
        MSCALS = r.calendars;
        if (UI.view === "einstellungen") render();
      },
      (e: unknown) => toast(e instanceof ApiError ? e.message : "Kalender nicht geladen"),
    );
  }
  const chip = s.connected && s.calendarId ? (s.error ? `<span class="chip warn">Fehler</span>` : `<span class="chip ok">Verbunden</span>`) : s.connected ? `<span class="chip warn">Kalender wählen</span>` : `<span class="chip">Nicht verbunden</span>`;
  const guide = `<details class="ms-guide" ${s.configured ? "" : "open"}><summary class="small">So richtest du es einmalig ein (ca. 10 Minuten)</summary><ol class="small">
    <li>Auf <a href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener">entra.microsoft.com → App-Registrierungen</a> mit deinem Konto anmelden und <b>«Neue Registrierung»</b> wählen.</li>
    <li>Name z. B. <b>Social Solve Management</b>, Kontotyp <b>«Nur Konten in diesem Organisationsverzeichnis»</b>. Umleitungs-URI: Plattform <b>Web</b>, Adresse <code>${esc(s.redirectUri)}</code> <button type="button" class="btn ghost sm" data-a="copy" data-v="${esc(s.redirectUri)}">${ic("copy", 13)}</button>. Registrieren.</li>
    <li>In der Übersicht <b>«Anwendungs-ID (Client)»</b> und <b>«Verzeichnis-ID (Mandant)»</b> kopieren und unten eintragen.</li>
    <li><b>«Zertifikate &amp; Geheimnisse»</b> → «Neuer geheimer Clientschlüssel» (24 Monate) → den <b>Wert</b> kopieren und unten eintragen. Er wird verschlüsselt gespeichert und nie wieder angezeigt.</li>
    <li><b>«API-Berechtigungen»</b> → «Berechtigung hinzufügen» → Microsoft Graph → <b>Delegierte Berechtigungen</b>: <code>Calendars.ReadWrite</code> und <code>offline_access</code> (User.Read ist schon da).</li>
    <li>Speichern, dann <b>«Mit Microsoft verbinden»</b> und den Kalender <b>Kunden Termine</b> wählen.</li></ol></details>`;
  const appForm = `<form data-form="ms-app" class="nc-form"><div class="nc-two"><input class="in" id="ms-tenant" placeholder="Verzeichnis-ID (Mandant)" value="${esc(s.tenantId)}" autocomplete="off"><input class="in" id="ms-client" placeholder="Anwendungs-ID (Client)" value="${esc(s.clientId)}" autocomplete="off"></div><input class="in" id="ms-secret" type="password" placeholder="${s.configured ? "Geheimer Clientschlüssel (nur zum Ändern)" : "Geheimer Clientschlüssel (Wert)"}" autocomplete="new-password"><div class="add-row"><button class="btn sm ${s.configured ? "" : "pri"}" type="submit">${s.configured ? "Speichern" : "Weiter"}</button>${s.configured ? `<a class="btn sm ${s.connected ? "" : "pri"}" href="/api/ms/connect">${ic("lock", 14)} ${s.connected ? "Neu anmelden" : "Mit Microsoft verbinden"}</a><button type="button" class="btn ghost sm" data-a="ms-remove">${ic("trash", 14)} Trennen</button>` : ""}</div></form>`;
  const cals = MSCALS ?? [];
  const calForm =
    s.connected && cals.length
      ? `<form data-form="ms-cal" class="nc-form" style="margin-top:12px"><label class="flabel" for="ms-cal">To-dos und Deadlines eintragen in</label><select class="in" id="ms-cal">${s.calendarId ? "" : `<option value="">Kalender wählen …</option>`}${cals
          .filter((c) => c.canEdit)
          .map((c) => `<option value="${esc(c.id)}" ${c.id === s.calendarId ? "selected" : ""}>${esc(c.name)}</option>`)
          .join("")}</select><span class="flabel">Termine im Tool auch anzeigen aus</span><div class="ms-show">${cals
          .filter((c) => c.id !== s.calendarId)
          .map((c) => `<label class="chk"><input type="checkbox" name="ms-show" value="${esc(c.id)}" ${s.showIds.includes(c.id) ? "checked" : ""}> ${esc(c.name)}</label>`)
          .join("")}</div><div class="add-row"><button class="btn sm pri" type="submit">${s.calendarId ? "Speichern" : "Kalender verbinden"}</button>${s.calendarId ? `<button type="button" class="btn ghost sm" data-a="cal-sync">${ic("refresh", 14)} Jetzt abgleichen</button>` : ""}</div></form>`
      : "";
  const state = s.connected
    ? `Angemeldet als <b>${esc(s.account)}</b>.${s.calendarId ? ` To-dos mit Tag und deine Video-Deadlines stehen im Kalender <b>${esc(s.calendarName)}</b>; verschiebst du ein To-do in Outlook, zieht das Tool mit.${s.lastSync ? ` Zuletzt abgeglichen ${new Date(s.lastSync).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}.` : ""}` : " Jetzt noch den Kalender wählen."}`
    : "Verbindet den Kalender im Tool mit deinem Exchange (Microsoft 365): To-dos mit Tag und deine Video-Deadlines erscheinen in «Kunden Termine», deine Termine aus Exchange im Kalender des Tools.";
  return `<div class="panel icard" style="padding:18px"><div style="display:flex;gap:12px;align-items:center"><span class="lic">${ic("cal", 20)}</span><h3 class="h3" style="flex:1">Microsoft 365 · Kalender</h3>${chip}</div>
    <p>${state}</p>${s.error ? `<div class="note-box warn">${ic("x", 16)}<span>${esc(s.error)}</span></div>` : ""}
    ${calForm}${s.connected ? `<details style="margin-top:12px"><summary class="small dim">App-Registrierung</summary>${appForm}</details>` : appForm}${s.connected ? "" : guide}</div>`;
}

/* ============ Übersicht ============ */

/** Offene Punkte aus den Kommentaren der Kunden, über alle Videos: abhaken direkt hier */
function feedbackPanel() {
  const groups: { cid: string; video: string; section: string; url: string; items: VideoFeedback["comments"] }[] = [];
  for (const [cid, byVideo] of Object.entries(FEEDBACK)) {
    for (const [key, f] of Object.entries(byVideo)) {
      const items = openComments(f);
      if (!items.length) continue;
      const it = LIVE[cid]?.items.find((x) => itemKey(x) === key);
      groups.push({ cid, video: it?.video ?? key.split("|")[1] ?? key, section: it?.section ?? "", url: f.url, items });
    }
  }
  if (!groups.length) return "";
  const total = groups.reduce((a, g) => a + g.items.length, 0);
  return `<section class="panel fbp"><div class="panel-head"><h2 class="h2">Feedback zu Videos</h2><span class="small dim">${total} offen · abhaken nur für dich</span></div>${groups
    .slice(0, 6)
    .map((g) => {
      const c = C(g.cid);
      return `<div class="fbp-g"><div class="fbp-h"><a href="#kunde-${esc(g.cid)}" data-a="go" data-to="kunde-${esc(g.cid)}" data-ctab="table" class="min0"><b class="ellip">${esc(g.video.replace(/_+/g, " "))}</b><span class="small dim">${cdot(c)} ${esc(c?.name ?? "")}${g.section ? ` · ${esc(g.section)}` : ""}</span></a>${g.url ? `<a class="ibtn sm" href="${esc(safeUrl(g.url))}" target="_blank" rel="noopener" title="Im Video ansehen">${ic("play", 14)}</a>` : ""}</div><ul class="vfb-l">${g.items.map((x) => fbItem(g.cid, x, fbWhen)).join("")}</ul></div>`;
    })
    .join("")}</section>`;
}

function vUebersicht() {
  const now = new Date();
  const h = now.getHours();
  const greet = h < 11 ? "Guten Morgen" : h < 18 ? "Hallo" : "Guten Abend";
  const first = firstName();
  if (!S.clients.length)
    return pageHead("Willkommen", "Noch ist alles leer.", "Leg los mit deinem ersten Kunden. Alles wird sicher gespeichert.", `<button class="btn pri" data-a="client-new">${ic("plus", 16)} Kunde erstellen</button>`);
  const items = upcoming();
  const ap = activeProjects();
  const soon = ap.filter((p) => p.due && daysFrom(p.due) <= 14).length;
  const todo = todoItems(items);
  const week = todo.filter((i) => daysFrom(i.due) <= 7).length;
  const waiting = items.length - todo.length;
  const todos = S.clients.flatMap((c) => openTodos(c.id).map((n) => ({ c, n }))).sort((a, b) => todoSort(a.n, b.n));
  // «Als Nächstes» ist, was bei dir liegt, nicht was schon abgegeben ist
  const next = todo[0];
  const lede = next ? `Als Nächstes: <b>${esc(next.title)}</b>, ${rel(daysFrom(next.due))}.${waiting ? ` ${waiting} ${waiting === 1 ? "Video wartet" : "Videos warten"} auf Feedback.` : ""}` : waiting ? `Alles abgegeben, ${waiting} ${waiting === 1 ? "Video wartet" : "Videos warten"} auf Feedback.` : "Alles im grünen Bereich.";
  return `${pageHead(`${WDL[now.getDay()]}, ${now.getDate()}. ${MONL[now.getMonth()]}`, `${greet}${first ? `, ${esc(first)}` : ""}.`, lede)}
  ${healthBanner()}
  <div class="quick"><button class="btn" data-a="project-new">${ic("board", 16)} Projekt</button><button class="btn" data-a="client-new">${ic("users", 16)} Kunde</button><a class="btn" href="https://app.milkee.ch" target="_blank" rel="noopener">${ic("ext", 15)} MILKEE</a></div>
  <div class="stats ${MK.configured ? "four" : ""}">
    ${finStat()}
    <button class="stat" data-a="go" data-to="projekte"><span>Aktive Projekte</span><b>${ap.length}</b><small>${soon ? `${soon} mit Deadline in 14 Tagen` : "keine Deadline in 14 Tagen"}</small></button>
    <div class="stat"><span>Fällig in 7 Tagen</span><b>${week}</b><small>${waiting ? `bei dir · ${waiting} abgegeben` : "Projekte und Videos"}</small></div>
    <div class="stat acc"><span>Offene To-dos</span><b>${todos.length}</b><small>über alle Kunden</small></div>
  </div>
  <div class="grid-2">
    <section class="panel"><div class="panel-head"><h2 class="h2">Als Nächstes</h2><span class="small dim">nächste 30 Tage${waiting ? ` · ${waiting} abgegeben` : ""}</span></div>${upcomingList(items)}</section>
    <div class="col-stack">${feedbackPanel()}
    <section class="panel"><div class="panel-head"><h2 class="h2">Offene To-dos</h2>${todos.length ? `<span class="small dim">${todos.length}</span>` : ""}</div>
      ${todoForm(null, "ht")}${todos.length ? todos.slice(0, 8).map(({ c, n }) => todoRow(c.id, n, true)).join("") + (todos.length > 8 ? `<a class="btn ghost sm" href="#kalender" style="margin-top:8px">Alle im Kalender</a>` : "") : emptyBox("Keine offenen To-dos", "Oben eintragen, mit Kunde und Tag. Sie erscheinen auch im Kalender.")}</section></div>
  </div>`;
}

/* ============ Kunden ============ */

function vKunden() {
  if (MK.configured && YEAR_REV.state === "idle") void loadYearRevenue();
  const q = UI.clientQ.toLowerCase();
  const list = S.clients
    .filter((c) => (UI.clientFilter === "alle" || (UI.clientFilter === "aktiv" ? c.status !== "archiv" : c.status === "archiv")) && (!q || `${c.name} ${c.contact} ${c.email} ${c.city}`.toLowerCase().includes(q)))
    .sort((a, b) => a.name.localeCompare(b.name));
  const rows = list
    .map((c) => {
      const td = openTodos(c.id).length;
      const ap = activeProjects(c.id).length;
      const mk = mkCustomerOf(c);
      const rev = mk ? YEAR_REV.map.get(mk) : undefined;
      return `<tr class="click" data-a="go" data-to="kunde-${esc(c.id)}"><td><div class="namecell"><div class="mono" style="--c:${esc(c.color)}">${esc(c.short)}</div><div class="min0"><div class="rt ellip">${esc(c.name)}</div><div class="rs ellip">${esc(c.contact || c.email || "")}</div></div></div></td><td class="hide-m muted">${esc(c.city) || "–"}</td>${MK.configured ? `<td class="r tnum hide-m ${rev ? "" : "dim"}">${YEAR_REV.state === "ok" ? (rev ? chf0(rev) : mk ? "–" : '<span class="small">nicht verknüpft</span>') : "…"}</td>` : ""}<td class="r tnum ${ap ? "" : "dim"}">${ap || "–"}</td><td class="r tnum ${td ? "" : "dim"}">${td || "–"}</td></tr>`;
    })
    .join("");
  return `${pageHead("Arbeit", "Kunden", "", `<button class="btn pri" data-a="client-new">${ic("plus", 16)} Kunde erstellen</button>`)}
  <div class="filters"><div class="seg">${[["aktiv", "Aktiv"], ["archiv", "Archiviert"], ["alle", "Alle"]].map(([k, l]) => `<button class="${UI.clientFilter === k ? "on" : ""}" data-a="cfilter" data-k="${k}">${l}</button>`).join("")}</div><input class="in sm" id="client-q" placeholder="Suchen" value="${esc(UI.clientQ)}" style="margin-left:auto;width:220px" autocomplete="off"></div>
  <section class="panel flush">${list.length ? `<div class="tbl-wrap"><table class="tbl"><thead><tr><th>Kunde</th><th class="hide-m">Ort</th>${MK.configured ? `<th class="r hide-m">Umsatz ${new Date().getFullYear()}</th>` : ""}<th class="r">Aktive Projekte</th><th class="r">Offene To-dos</th></tr></thead><tbody>${rows}</tbody></table></div>` : `<div style="padding:24px">${emptyBox("Keine Kunden gefunden", q ? "Versuch einen anderen Suchbegriff." : "Erstelle deinen ersten Kunden.", `<button class="btn pri sm" data-a="client-new">${ic("plus", 14)} Kunde erstellen</button>`)}</div>`}</section>`;
}

function vKunde() {
  const c = C(UI.cid);
  if (!c) return emptyBox("Kunde nicht gefunden", "Vielleicht wurde er gelöscht.", `<a class="btn sm" href="#kunden">Zur Kundenliste</a>`);
  const tabs: [string, string][] = [["overview", "Übersicht"], ...(MK.configured ? ([["finance", "Finanzen"]] as [string, string][]) : []), ...c.tabs.filter((t) => TABS[t]).map((t): [string, string] => [t, TABS[t]!.name])];
  if (!tabs.some((t) => t[0] === UI.ctab)) UI.ctab = "overview";
  const cnt: Record<string, number> = { projects: activeProjects(c.id).length, notes: (S.notes[c.id] ?? []).filter((n) => !n.done).length };
  const addr = [c.street, [c.zip, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  let body = "";
  if (UI.ctab === "overview") body = clientOverview(c);
  if (UI.ctab === "table") body = excelTab(c);
  if (UI.ctab === "projects") body = clientProjects(c);
  if (UI.ctab === "notes") body = `<section class="panel">${notesBlock(c.id)}</section>`;
  if (UI.ctab === "finance") body = clientFinance(c);
  return `<a class="back" href="#kunden">${ic("left", 15)} Alle Kunden</a>
  <section class="c-head"><div class="mono lg" style="--c:${esc(c.color)}">${esc(c.short)}</div>
    <div class="min0"><h1 class="h1" style="margin:0">${esc(c.name)}</h1><div class="c-meta">${c.contact ? `<span>${esc(c.contact)}</span>` : ""}${c.email ? `<span>${esc(c.email)}</span>` : ""}${addr ? `<span>${esc(addr)}</span>` : ""}${c.status === "archiv" ? '<span class="chip warn">Archiviert</span>' : ""}</div></div>
    <div class="acts"><button class="btn pri" data-a="project-new" data-cid="${esc(c.id)}">${ic("plus", 16)} Projekt</button>
    <details class="menu"><summary class="ibtn outline" aria-label="Weitere Aktionen">${ic("more", 18)}</summary><div class="menu-list"><button data-a="client-edit">${ic("edit", 16)} Kunde bearbeiten</button>${MK.configured ? `<button data-a="mk-link">${ic("link", 16)} ${mkCustomerOf(c) ? "MILKEE-Verknüpfung ändern" : "Mit MILKEE verknüpfen"}</button>` : ""}<button data-a="tabs-edit">${ic("blocks", 16)} Bereiche anpassen</button><hr><button data-a="client-archive">${ic("folder", 16)} ${c.status === "archiv" ? "Wieder aktivieren" : "Archivieren"}</button></div></details></div>
  </section>
  <div class="tabs">${tabs.map(([k, l]) => `<button class="${UI.ctab === k ? "on" : ""}" data-a="ctab" data-k="${k}">${l}${cnt[k] ? `<span>${cnt[k]}</span>` : ""}</button>`).join("")}</div>
  ${body}`;
}

function clientOverview(c: Client) {
  const items = upcoming(c.id, 60);
  const next = upcoming(c.id, 3650)[0];
  const links = c.links
    .map((l, i) => {
      const u = esc(safeUrl(l.url));
      const icon = /sharepoint|onedrive|1drv/i.test(l.url) ? "cloud" : /drive\.google|docs\.google/i.test(l.url) ? "drive" : "link";
      return `<div class="linkrow"><span class="lic">${ic(icon, 17)}</span><a class="grow min0" href="${u}" target="_blank" rel="noopener"><b class="ellip" style="display:block;font-weight:500">${esc(l.label || hostOf(l.url))}</b><span class="rs ellip" style="display:block">${esc(hostOf(safeUrl(l.url)))}</span></a><a class="ibtn" href="${u}" target="_blank" rel="noopener" aria-label="Öffnen">${ic("ext", 15)}</a><button class="ibtn" data-a="link-del" data-i="${i}" aria-label="Link entfernen">${ic("x", 15)}</button></div>`;
    })
    .join("");
  return `${openWorkCard(c)}${earnCard(c.id)}<section class="panel"><div class="panel-head"><h2 class="h2">To-dos</h2>${openTodos(c.id).length ? `<span class="small dim">${openTodos(c.id).length} offen</span>` : ""}</div>${todoForm(c.id, `ct-${esc(c.id)}`)}${todoList(c.id)}</section><div class="stats"><div class="stat"><span>Aktive Projekte</span><b>${activeProjects(c.id).length}</b></div><div class="stat"><span>Offene To-dos</span><b>${openTodos(c.id).length}</b></div><div class="stat"><span>Nächste Deadline</span><b>${next ? fmtDate(next.due) : "–"}</b>${next ? `<small class="ellip">${esc(next.title)}</small>` : ""}</div></div>
  <div class="grid-half">
    <section class="panel"><div class="panel-head"><h2 class="h2">Kontakt</h2><button class="btn ghost sm" data-a="client-edit">${ic("edit", 14)} Bearbeiten</button></div>
      <dl class="dl"><dt>Ansprechperson</dt><dd>${esc(c.contact) || '<span class="dim">–</span>'}</dd><dt>E-Mail</dt><dd>${c.email ? `<button class="btn ghost sm" style="padding:0;height:auto;color:var(--fg)" data-a="copy" data-v="${esc(c.email)}">${esc(c.email)} ${ic("copy", 13)}</button>` : '<span class="dim">–</span>'}</dd><dt>Telefon</dt><dd>${esc(c.phone) || '<span class="dim">–</span>'}</dd><dt>Adresse</dt><dd>${c.street ? `${esc(c.street)}<br>${esc(`${c.zip} ${c.city}`)}` : '<span class="dim">–</span>'}</dd></dl></section>
    <section class="panel"><div class="panel-head"><h2 class="h2">Links & Ordner</h2></div>
      ${links || '<p class="muted" style="margin:0 0 12px">Hinterlege Drive-, SharePoint- oder Dropbox-Ordner, damit du sie hier mit einem Klick öffnest.</p>'}
      <form class="add-row" data-form="link-add" style="margin-top:14px"><input class="in" id="ln-label" placeholder="Name, z. B. Rohmaterial" style="flex:1 1 140px" autocomplete="off"><input class="in" id="ln-url" placeholder="https://…" style="flex:2 1 200px" autocomplete="off"><button class="btn" type="submit">${ic("plus", 14)} Hinzufügen</button></form></section>
  </div>
  ${sectionsPanel(c)}
  <section class="panel"><div class="panel-head"><h2 class="h2">Als Nächstes</h2></div>${upcomingList(items)}</section>
  ${clientScripts(c.id)}`;
}

function excelTab(c: Client) {
  const t = S.tables[c.id];
  if (!t?.url)
    return `<section class="panel"><div class="panel-head">${ic("sheet")}<h2 class="h2">Tabelle verbinden</h2></div>
    <p class="muted" style="margin:0 0 16px">Füg den Link zu einer Google-Tabelle ein. Social Solve Management liest sie über den eigenen Server und aktualisiert sich jede Minute. Teile die Tabelle vorher in Google Sheets mit ${esc(SA_EMAIL || "dem Google-Dienstkonto")} als «Bearbeiter».</p>
    <form class="add-row" data-form="xl-link"><input class="in" id="xl-url" placeholder="https://docs.google.com/spreadsheets/d/…" style="flex:3 1 280px" autocomplete="off"><input class="in" id="xl-name" placeholder="Name (optional)" style="flex:1 1 160px" autocomplete="off"><button class="btn pri" type="submit">Verbinden</button></form></section>`;
  if (t.source !== "gsheet")
    return `<section class="panel"><div class="panel-head">${ic("sheet")}<h2 class="h2">${esc(t.name || "Excel-Tabelle")}</h2><a class="btn sm" href="${esc(safeUrl(t.url))}" target="_blank" rel="noopener">${ic("ext", 14)} Öffnen</a><button class="btn ghost sm" data-a="xl-reset">Link ändern</button></div><div class="note-box warn">${ic("refresh", 16)}<span>Excel-Dateien aus OneDrive brauchen die Microsoft-365-Verbindung. Mit einer Google-Tabelle funktioniert die Live-Ansicht schon jetzt.</span></div></section>`;
  return liveTab(c, t);
}

function liveTab(c: Client, t: SheetTable) {
  const L: Live = LIVE[c.id] ?? { state: "loading", items: [], tabs: [] };
  const mode = UI.liveMode;
  const mins = L.at ? Math.round((Date.now() - L.at) / 60000) : 0;
  const tsTxt = L.at ? `aktualisiert ${mins < 1 ? "gerade eben" : `vor ${mins} Min.`}` : "";
  const head = `<div class="panel-head" style="flex-wrap:wrap">${ic("sheet")}<div class="min0" style="flex:1"><h2 class="h2">${esc(L.title || t.name || "Google-Tabelle")}</h2><div class="small muted" style="display:flex;gap:8px;align-items:center;margin-top:2px">${L.state === "ok" ? `<i class="live-dot"></i>Live aus Google Sheets · ${tsTxt}` : L.state === "stale" ? `<i class="live-dot stale"></i>Letzter Stand · ${tsTxt}` : "Google Sheets"}</div></div>
    <div class="seg"><button class="${mode === "plan" ? "on" : ""}" data-a="live-mode" data-k="plan">Plan</button><button class="${mode === "raw" ? "on" : ""}" data-a="live-mode" data-k="raw">Original</button></div>
    ${t.uploadUrl ? `<a class="btn sm" href="${esc(safeUrl(t.uploadUrl))}" target="_blank" rel="noopener">${ic("download", 14)} Hochladen</a>` : ""}<a class="btn sm" href="${esc(safeUrl(t.url))}" target="_blank" rel="noopener">${ic("ext", 14)} In Sheets öffnen</a>
    <details class="menu"><summary class="ibtn outline" aria-label="Weitere Aktionen">${ic("more", 18)}</summary><div class="menu-list"><button data-a="live-refresh">${ic("refresh", 16)} Jetzt neu laden</button><button data-a="upload-edit">${ic("link", 16)} Upload-Link ${t.uploadUrl ? "ändern" : "festlegen"}</button>${MK.configured ? `<button data-a="billing-setup">${ic("receipt", 16)} Abrechnung (MILKEE)</button>` : ""}<hr><button class="dng" data-a="xl-reset">${ic("x", 16)} Verbindung entfernen</button></div></details></div>`;
  let body = "";
  if (L.state === "loading") body = `<div class="loading" style="padding:40px 0">Tabelle wird geladen …</div>`;
  else if (L.state === "error") body = liveError(L.err ?? null);
  else
    body =
      (L.state === "stale" ? `<div class="note-box warn" style="margin-bottom:16px">${ic("refresh", 16)}<span>Google antwortet gerade nicht. Du siehst den letzten Stand, es wird automatisch wieder versucht.</span></div>` : "") +
      driveNote(c.id) +
      (mode === "raw" ? liveRaw(L) : livePlan(L));
  return `<section class="panel">${head}${body}</section>`;
}

function liveError(err: ApiError | null) {
  const sa = SA_EMAIL || "der Adresse des Google-Dienstkontos";
  const code = err?.code ?? "";
  const msgs: Record<string, [string, string]> = {
    not_shared: ["Kein Zugriff auf die Tabelle", `Teile die Tabelle in Google Sheets mit ${esc(sa)} als «Bearbeiter». Danach erscheint sie hier innert einer Minute.`],
    not_found: ["Tabelle nicht gefunden", "Prüf den Link. Vielleicht wurde die Tabelle verschoben oder gelöscht."],
    config: ["Google-Schlüssel fehlt", isAdmin() ? "Lade unter Einstellungen → Verbindungen die Schlüsseldatei des Google-Dienstkontos hoch." : "Ein Admin muss unter Einstellungen → Verbindungen den Google-Schlüssel hochladen."],
    forbidden: ["Keine Berechtigung", "Melde dich neu an."],
    auth: ["Anmeldung abgelaufen", "Melde dich neu an."],
    parse: ["Tabelle nicht lesbar", "Die Antwort von Google hatte ein unerwartetes Format."],
    google_error: ["Google meldet einen Fehler", "Versuch es gleich nochmals."],
  };
  const msg = msgs[code] ?? ["Verbindung gerade nicht möglich", "In einer Minute wird es automatisch wieder versucht."];
  const detail = err?.message && !["not_shared", "auth"].includes(code) ? `<br><span class="small dim">${esc(String(err.message).slice(0, 160))}</span>` : "";
  return emptyBox(msg[0], msg[1] + detail, `<button class="btn sm" data-a="live-refresh">${ic("refresh", 14)} Nochmals versuchen</button>`);
}

/** Farbe des Stands: offen, in Arbeit (v1…), Revision, fertig. */
function standTone(stand: string): "open" | "work" | "review" | "done" {
  if (!stand) return "open";
  if (/^(final|fertig|erledigt|geliefert|done)\b/i.test(stand)) return "done";
  if (/revision|feedback|korrektur|review/i.test(stand)) return "review";
  return "work";
}
/** Auswahl für Stand oder «Dran» als Pille. Bleibt ein natives select (Tastatur, Handy). */
function liveSel(it: LiveItem, f: "stand" | "todo", all: LiveItem[]) {
  const col = f === "stand" ? it.standCol : it.todoCol;
  if (col < 0) return "";
  const known = all.flatMap((x) => (f === "stand" ? x.standOpts : x.todoOpts));
  const opts = [...new Set([...(f === "stand" ? it.standOpts : it.todoOpts), ...known, it[f]].filter(Boolean))];
  if (!opts.length) return "";
  const cls = f === "stand" ? `pill-sel tone-${standTone(it.stand)}` : "pill-sel ghost";
  const label = f === "stand" ? `Stand von ${esc(it.video)}` : `Wer ist dran bei ${esc(it.video)}`;
  return `<label class="${cls}" title="${f === "stand" ? "Stand ändern" : "Wer ist dran?"}">${f === "todo" ? ic("users", 13) : '<i class="tone-dot"></i>'}<select data-a="live-set" data-f="${f}" data-key="${esc(it.key)}" aria-label="${label}">${f === "stand" ? `<option value="" ${!it.stand ? "selected" : ""}>Offen</option>` : ""}${opts.map((o) => `<option value="${esc(o)}" ${o === it[f] ? "selected" : ""}>${esc(o)}</option>`).join("")}</select>${ic("chevd", 13)}</label>`;
}
const linkBtn = (url: string, icon: string, label: string, title: string) =>
  url ? `<a class="ibtn outline sm" href="${esc(url)}" target="_blank" rel="noopener" aria-label="${label}" title="${esc(title || label)}">${ic(icon, 15)}</a>` : "";
function dueChip(it: LiveItem) {
  if (isDone(it) || !it.due) return "";
  // abgegebene Version (wartet auf Feedback) ist nicht überfällig
  const st = videoState(it);
  if (st?.waiting) return `<span class="chip ok">${ic("check", 12)} ${esc(st.label)}</span>`;
  const n = daysFrom(it.due);
  return n <= 7 ? `<span class="chip ${relCls(n)}">${n < 0 ? "überfällig" : rel(n)}</span>` : `<span class="small dim">${rel(n)}</span>`;
}
/** Titel eines Videos: führt direkt ins Projekt, falls es eines dazu gibt */
function videoTitle(cid: string, it: LiveItem, cls: string, tag = "div") {
  const p = projOfItem(cid, it);
  if (!p) return `<${tag} class="${cls}">${esc(it.video)}</${tag}>`;
  return `<button type="button" class="vtitle ${cls}" data-a="proj-open" data-id="${esc(p.id)}" title="Projekt öffnen"><span>${esc(it.video)}</span>${ic("right", 14)}</button>`;
}
function vRow(it: LiveItem, all: LiveItem[], extra = "") {
  const d = it.due ? new Date(it.due) : null;
  const n = it.due ? daysFrom(it.due) : null;
  const late = n != null && n < 0 && !isDone(it);
  const p = projOfItem(UI.cid, it);
  const when = d ? `<b>${d.getDate()}</b><span>${MONS[d.getMonth()]}</span>` : "<b>–</b>";
  return `<div class="vrow ${isDone(it) ? "is-done" : ""} ${p ? "has-proj" : ""}">${p ? `<button type="button" class="when ${late ? "late" : ""}" data-a="proj-open" data-id="${esc(p.id)}" aria-label="Projekt öffnen">${when}</button>` : `<div class="when ${late ? "late" : ""}">${when}</div>`}
    <div class="min0">${videoTitle(UI.cid, it, "rt ellip")}<div class="vmeta">${it.section ? `<span class="ellip">${esc(it.section)}</span>` : ""}${dueChip(it)}${extra}</div></div>
    <div class="vacts vgrid"><span class="vc-stand">${liveSel(it, "stand", all)}</span><span class="vc-todo">${liveSel(it, "todo", all)}</span><span class="vc-db">${driveBtn(UI.cid, it)}</span><span class="vlinks">${linkBtn(it.files, "folder", "Dateien öffnen", it.filesLabel)}${linkBtn(sectionLink(UI.cid, it.section) ? safeUrl(sectionLink(UI.cid, it.section)) : "", "film", "B-Roll öffnen", `B-Roll ${it.section}`)}${linkBtn(it.videoUrl, "play", "Video öffnen", it.videoLabel)}</span></div>${feedbackBlock(UI.cid, it)}</div>`;
}
function livePlan(L: Live) {
  const t = (x: LiveItem) => (x.due ? new Date(x.due).getTime() : Infinity);
  const by = (a: LiveItem, b: LiveItem) => t(a) - t(b);
  const open = L.items.filter((it) => !isDone(it));
  // abgegebene Versionen (warten auf Feedback) gehören nicht zu «für dich»
  const forMe = (it: LiveItem) => isMine(it) && !videoState(it)?.waiting;
  const mine = open.filter(forMe).sort(by);
  const them = open.filter((it) => !forMe(it)).sort(by);
  const done = L.items.filter(isDone).sort(by).reverse();
  if (!L.items.length) return emptyBox("Keine Videos gefunden", "Sobald in der Tabelle Videos mit Deadline stehen, erscheinen sie hier.");
  const nx = mine[0];
  const n = nx?.due ? daysFrom(nx.due) : null;
  const up = S.tables[UI.cid]?.uploadUrl;
  const all = L.items;
  const bill = billingCtx(UI.cid, L);
  const row = (it: LiveItem) => vRow(it, all, bill ? earlyCell(it, bill) : "");
  const urg = n == null ? "" : n < 0 ? "late" : n <= 2 ? "soon" : "";
  const hero = nx
    ? `<div class="vnext ${urg}"><div class="vcount"><b>${n == null ? "–" : n < 0 ? -n : n}</b><span>${n == null ? "offen" : n < 0 ? (n === -1 ? "Tag drüber" : "Tage drüber") : n === 0 ? "heute" : n === 1 ? "Tag" : "Tage"}</span></div>
      <div class="min0"><div class="lbl">Als Nächstes für dich</div>${videoTitle(UI.cid, nx, "vh3", "h3")}<div class="vmeta">${nx.section ? `<span>${esc(nx.section)}</span>` : ""}<span>${ic("cal", 13)} ${nx.due ? fmtD(nx.due) : esc(nx.dueText) || "ohne Deadline"}</span>${bill ? earlyCell(nx, bill) : ""}</div></div>
      <div class="vacts">${liveSel(nx, "stand", all)}${nx.files ? `<a class="btn sm" href="${esc(nx.files)}" target="_blank" rel="noopener">${ic("folder", 14)} Dateien</a>` : ""}${driveBtn(UI.cid, nx, true)}${sectionLink(UI.cid, nx.section) ? `<a class="btn sm" href="${esc(safeUrl(sectionLink(UI.cid, nx.section)))}" target="_blank" rel="noopener">${ic("film", 14)} B-Roll</a>` : ""}${nx.videoUrl ? `<a class="btn sm" href="${esc(safeUrl(nx.videoUrl))}" target="_blank" rel="noopener">${ic("play", 14)} Video</a>` : ""}${up ? `<a class="btn sm pri" href="${esc(safeUrl(up))}" target="_blank" rel="noopener">${ic("download", 14)} Hochladen</a>` : ""}</div>${feedbackBlock(UI.cid, nx, true)}</div>`
    : `<div class="note-box">${ic("check", 16)}<span>Bei dir ist gerade nichts offen.</span></div>`;
  const group = (title: string, items: LiveItem[], cls = "") => (items.length ? `<div class="vgroup">${title}<span class="chip ${cls}">${items.length}</span></div><div class="vlist">${items.map(row).join("")}</div>` : "");
  const doneRow = (it: LiveItem) => vRow(it, all, bill ? billCell(it, bill) : "");
  return `${earnCard(UI.cid)}${hero}${bill ? billBanner(bill) : ""}<div class="vplan">
    ${group("Danach für dich", mine.slice(1))}
    ${group("Wartet auf andere", them)}
    ${done.length ? `<details class="vdone" ${bill && bill.unbilled.length ? "open" : ""}><summary class="vgroup">${ic("chev", 14)} Fertig <span class="chip ok">${done.length}</span></summary><div class="vlist">${done.map(doneRow).join("")}</div></details>` : ""}</div>
    <p class="help" style="margin:18px 0 0">Stand und «Dran» änderst du direkt hier. Die Änderung wird sofort in Google Sheets gespeichert.</p>`;
}
function liveRaw(L: Live) {
  const tabs = L.tabs;
  if (!tabs.length) return emptyBox("Tabelle leer", "");
  const ti = Math.min(UI.liveTabIdx, tabs.length - 1);
  const tb = tabs[ti]!;
  const w = Math.max(1, ...tb.grid.map((r) => r.length));
  const rows = tb.grid
    .map((r, i) => {
      const isHead = r.some((c) => /^deadline$/i.test(c.t));
      const cells = Array.from({ length: w }, (_, j) => {
        const c = r[j] ?? { t: "", l: "" };
        const h = isHead || (j === 0 && r.slice(1).every((x) => !x.t || /^to do$/i.test(x.t)) && c.t);
        return `<td class="${h ? "h" : ""}">${c.l ? `<a href="${esc(c.l)}" target="_blank" rel="noopener">${esc(c.t || "Link")}</a>` : esc(c.t)}</td>`;
      }).join("");
      return `<tr><td class="n">${i + 1}</td>${cells}</tr>`;
    })
    .join("");
  return `${tabs.length > 1 ? `<div class="seg" style="margin-bottom:12px">${tabs.map((x, i) => `<button class="${i === ti ? "on" : ""}" data-a="live-tab" data-k="${i}">${esc(x.title)}</button>`).join("")}</div>` : `<div class="small muted" style="margin-bottom:10px">Blatt «${esc(tb.title)}», 1:1 wie in Google Sheets</div>`}<div class="gsheet"><table><tbody>${rows}</tbody></table></div>`;
}

function clientProjects(c: Client) {
  const ps = S.projects.filter((p) => p.client === c.id);
  return `<div class="panel-head" style="padding:0 0 12px"><h2 class="h2">Projekte</h2><button class="btn sm" data-a="project-new" data-cid="${esc(c.id)}">${ic("plus", 14)} Projekt</button></div>${ps.length ? projectSections(ps, false) : emptyBox("Noch keine Projekte", "Projekte zeigen dir Phase und Deadline auf einen Blick.")}`;
}

/* ============ Projekte ============ */

/* ---------- Verdienst, Ordner pro Abschnitt, Projektdetails ---------- */

const secKey = (s: string) => s.trim().toLowerCase();
function sectionLinks(c: Client | undefined): Record<string, string> {
  const m = c?.sectionLinks;
  return m && typeof m === "object" ? (m as Record<string, string>) : {};
}
const sectionLink = (cid: string, section: string) => (section ? (sectionLinks(C(cid))[secKey(section)] ?? "") : "");
const projOfItem = (cid: string, it: LiveItem) => S.projects.find((p) => p.client === cid && p.sheetKey === itemKey(it));
const itemOfProject = (p: Project) => (typeof p.sheetKey === "string" ? LIVE[p.client]?.items.find((it) => itemKey(it) === p.sheetKey) : undefined);

/** Verdienst aus der Live-Tabelle: alle Videos × Pauschale, «Final» zählt als verdient. */
/** Schon abgegeben (v1, v2 …), aber noch nicht final */
const delivered = (it: LiveItem) => !isDone(it) && /^(?:v|version\s*)\d{1,2}\b/i.test(it.stand.trim());
/** Noch nicht final, aber schon verrechnet (Knopf in der Live-Tabelle) */
const billedBeforeFinal = (cid: string, it: LiveItem) => !isDone(it) && ["Zu verrechnen", ...DONE_PHASES].includes(projOfItem(cid, it)?.phase ?? "");
function earnings(cid: string) {
  const price = billingOf(C(cid))?.pricePerVideo ?? 0;
  const items = LIVE[cid]?.items ?? [];
  if (!price || !items.length) return null;
  const finals = items.filter(isDone);
  // von Hand verrechnet, obwohl noch nicht final: zählt als verdient
  const early = items.filter((it) => billedBeforeFinal(cid, it));
  const billed = [...finals, ...early].filter((it) => DONE_PHASES.includes(projOfItem(cid, it)?.phase ?? "")).length;
  // schon einmal abgegeben (v1, v2 …), aber noch nicht final und nicht verrechnet
  const given = items.filter((it) => delivered(it) && !billedBeforeFinal(cid, it)).length;
  return { price, total: items.length, done: finals.length + early.length, final: finals.length, billed, given };
}
function earnCard(cid: string) {
  const e = earnings(cid);
  if (!e) return "";
  const pct = (n: number) => `${(n / e.total) * 100}%`;
  const todo = e.total - e.done - e.given;
  const n = (k: number) => `${k} Video${k === 1 ? "" : "s"}`;
  return `<section class="earn"><div class="earn-head"><span class="flabel">Verdienst mit allen Videos · ${chf0(e.price)} pro Video</span><span class="small dim">${e.final} von ${e.total} Videos final${e.given ? ` · ${e.given} abgegeben` : ""}</span></div>
    <div class="earn-sum tnum"><b>${chf0(e.done * e.price)}</b><span>/ ${chf0(e.total * e.price)}</span></div>
    <div class="earn-bar"><i class="b" style="width:${pct(e.billed)}"></i><i class="o" style="width:${pct(e.done - e.billed)}"></i><i class="a" style="width:${pct(e.given)}"></i></div>
    <div class="earn-legend"><span><i class="b"></i>Verrechnet ${chf0(e.billed * e.price)}</span><span><i class="o"></i>Zu verrechnen ${chf0((e.done - e.billed) * e.price)}</span>${e.given ? `<span><i class="a"></i>Abgegeben (v1 …), wartet auf Feedback ${chf0(e.given * e.price)} · ${n(e.given)}</span>` : ""}<span><i></i>Noch zu machen ${chf0(todo * e.price)} · ${n(todo)}</span></div></section>`;
}
const earnChip = (cid: string) => {
  const e = earnings(cid);
  return e ? `<span class="chip acc" title="Verdient (Final) / alle Videos der Live-Tabelle">${chf0(e.done * e.price)} / ${chf0(e.total * e.price)}</span>` : "";
};

function sectionsPanel(c: Client) {
  const items = LIVE[c.id]?.items ?? [];
  const secs = [...new Set(items.map((it) => it.section.trim()).filter(Boolean))];
  if (!secs.length) return "";
  const links = sectionLinks(c);
  return `<section class="panel"><div class="panel-head"><h2 class="h2">B-Roll-Ordner pro Abschnitt</h2></div><p class="muted" style="margin:-6px 0 14px;font-size:13.5px">Einmal hinterlegt, erscheint der Ordner bei jedem Video dieses Abschnitts in der Live-Tabelle und in den Projekten.</p>
    <div class="secrows">${secs
      .map((sec) => {
        const u = links[secKey(sec)] ?? "";
        return `<div class="secrow"><b class="ellip">${esc(sec)}</b><input class="in sm" data-a="sec-link" data-k="${esc(sec)}" value="${esc(u)}" placeholder="Link zum B-Roll-Ordner einfügen …" autocomplete="off" aria-label="B-Roll-Ordner ${esc(sec)}">${u ? `<a class="ibtn outline sm" href="${esc(safeUrl(u))}" target="_blank" rel="noopener" title="Ordner öffnen" aria-label="Ordner öffnen">${ic("ext", 15)}</a>` : `<span class="ibtn sm"></span>`}</div>`;
      })
      .join("")}</div></section>`;
}

/* ---------- Drehbuch-PDFs aus den Drive-Ordnern ---------- */

/** undefined = nicht geladen, null = lädt */
const DRIVE: Record<string, DriveScriptsResponse | null | undefined> = {};
async function loadDrive(cid: string, refresh = false) {
  if (DRIVE[cid] === null) return;
  DRIVE[cid] = null;
  try {
    DRIVE[cid] = await api.driveScripts(cid, refresh);
  } catch (e) {
    if (onAuthLost(e)) return;
    DRIVE[cid] = { items: [], folders: { ok: 0, total: 0 }, error: { code: e instanceof ApiError ? e.code : "network", message: (e as Error).message } };
  }
  if (UI.view === "kunde" && UI.cid === cid) render();
  const open = document.querySelector<HTMLElement>("#overlay .pd[data-id]");
  if (open && P(open.dataset.id ?? "")?.client === cid) projectDetail(open.dataset.id ?? "");
}
function driveOf(cid: string, it: LiveItem): DriveScriptInfo | undefined {
  if (!S.tables[cid]?.id || !it.files) return undefined;
  if (DRIVE[cid] === undefined) void loadDrive(cid);
  return DRIVE[cid]?.items.find((x) => x.itemKey === itemKey(it));
}
/** Knopf «Drehbuch» (ein PDF oder gewählt) oder «Drehbuch wählen» (mehrere PDFs). */
function driveBtn(cid: string, it: LiveItem, big = false) {
  const d = driveOf(cid, it);
  if (!d || d.state === "declined") return "";
  if (d.file) return `<a class="btn sm ${big ? "" : "dbtn"}" href="${esc(safeUrl(d.file.url))}" target="_blank" rel="noopener" title="${esc(d.file.name)}">${ic("offer", 14)} Drehbuch</a>`;
  return `<button class="btn sm warnb" data-a="drive-choose" data-cid="${esc(cid)}" data-k="${esc(d.itemKey)}" title="${d.candidates.length} PDFs im Ordner">${ic("offer", 14)} Drehbuch wählen</button>`;
}
function driveNote(cid: string) {
  const e = DRIVE[cid]?.error;
  if (!e) return "";
  return `<div class="note-box warn" style="margin-bottom:16px">${ic("offer", 16)}<span><b>Drehbücher aus den Ordnern:</b> ${esc(e.message)}${e.code === "api_disabled" ? ` Einschalten unter <a href="https://console.cloud.google.com/apis/library/drive.googleapis.com" target="_blank" rel="noopener">Google Cloud → Google Drive API</a>, danach hier neu laden.` : ""}</span></div>`;
}
function driveChooseModal(cid: string, key: string) {
  const d = DRIVE[cid]?.items.find((x) => x.itemKey === key);
  const it = LIVE[cid]?.items.find((x) => itemKey(x) === key);
  if (!d) return;
  openModal(
    `${mhead("Welches PDF ist das Drehbuch?")}<p class="muted" style="margin:0">Im Ordner von «${esc(it?.video ?? key)}» liegen ${d.candidates.length} PDFs. Wähle das Drehbuch aus, es erscheint dann beim Video.</p>
    <div class="stack" style="gap:8px">${d.candidates
      .map(
        (f) => `<div class="dpick ${d.file?.id === f.id ? "on" : ""}"><button class="opt" data-a="drive-pick" data-cid="${esc(cid)}" data-k="${esc(key)}" data-id="${esc(f.id)}"><span class="lic">${ic("offer", 16)}</span><span class="min0"><b class="ellip" style="display:block">${esc(f.name)}</b><small class="dim">${f.modified ? `geändert ${fmtDate(f.modified)}` : "PDF"}</small></span>${d.file?.id === f.id ? `<span class="chip ok" style="margin-left:auto">gewählt</span>` : ""}</button><a class="ibtn outline sm" href="${esc(safeUrl(f.url))}" target="_blank" rel="noopener" title="Ansehen" aria-label="Ansehen">${ic("ext", 15)}</a></div>`,
      )
      .join("")}
    <button class="opt" data-a="drive-pick" data-cid="${esc(cid)}" data-k="${esc(key)}" data-id=""><span class="lic">${ic("x", 16)}</span><span><b>Keines davon ist ein Drehbuch</b><small class="dim" style="display:block">Es wird erst wieder gefragt, wenn ein neues PDF im Ordner liegt.</small></span></button></div>`,
  );
}

const AUTO: Record<string, AutoVideo[] | null | undefined> = {};
async function loadAuto(cid: string) {
  if (AUTO[cid] === null) return;
  AUTO[cid] = null;
  try {
    AUTO[cid] = (await api.automation(cid)).items;
  } catch (e) {
    if (onAuthLost(e)) return;
    AUTO[cid] = [];
  }
  const open = document.querySelector<HTMLElement>("#overlay .pd[data-id]");
  if (open && P(open.dataset.id ?? "")?.client === cid) projectDetail(open.dataset.id ?? "");
  else if (UI.view === "kunde" && UI.cid === cid) render();
}

function mkStatus(p: Project): string {
  const cfg = billingOf(C(p.client));
  if (cfg?.auto && typeof p.sheetKey === "string") {
    const list = AUTO[p.client];
    if (list === undefined) void loadAuto(p.client);
    if (!list) return `<span class="dim small">Wird geladen …</span>`;
    const v = list.find((x) => x.projectId === p.id || x.itemKey === p.sheetKey);
    if (v?.error) return `<div class="note-box warn">${ic("x", 16)}<span>${esc(v.error)}</span></div>`;
    const proj = v?.milkeeProjectId ?? p.milkeeProjectId;
    const nr = proj ? ` · MILKEE-Projekt ${esc(String(proj))}` : "";
    const st = v?.status ?? (p.phase === "Zu verrechnen" ? "final" : DONE_PHASES.includes(p.phase) ? "billed" : "open");
    if (st === "billed")
      return `<div class="pd-mk ok">${ic("check", 16)}<span><b>Verrechnet</b>${v?.invoiceNumber ? `Rechnung ${esc(v.invoiceNumber)}` : "Rechnung versendet"}${v?.billedAt ? ` am ${fmtDate(v.billedAt)}` : ""} · Projekt in MILKEE archiviert</span></div>`;
    if (st === "final")
      return `<div class="pd-mk warn">${ic("receipt", 16)}<span><b>Zu verrechnen · ${chf0(cfg.pricePerVideo)}</b>Offener Posten in MILKEE, wird beim nächsten Rechnungschreiben für ${esc(cfg.milkeeCustomerName)} vorgeschlagen${nr}</span></div>`;
    if (st === "legacy") return `<div class="pd-mk">${ic("check", 16)}<span><b>Ohne MILKEE erledigt</b>Wurde vor der Automatik abgeschlossen.</span></div>`;
    return `<div class="pd-mk">${ic("clock", 16)}<span><b>In Arbeit</b>Sobald das Video in der Tabelle auf «Final» steht, entsteht in MILKEE ein offener Posten über ${chf0(cfg.pricePerVideo)}${nr}</span></div>`;
  }
  if (p.milkeeProjectId) return `<div class="pd-mk">${ic("link", 16)}<span><b>Mit MILKEE verknüpft</b>MILKEE-Projekt ${esc(String(p.milkeeProjectId))}. Zeiten erfasst du unter «Zeiterfassung».</span></div>`;
  return `<span class="muted small">Nicht mit MILKEE verknüpft.</span>`;
}

/** offenes Projekt-Fenster (zum Nachführen, z. B. nach dem Abhaken eines Kommentars) */
let PD_OPEN = "";
function projectDetail(pid: string) {
  const p = P(pid);
  if (!p) return;
  PD_OPEN = pid;
  const c = C(p.client);
  const it = itemOfProject(p);
  const up = S.tables[p.client]?.uploadUrl ?? "";
  const broll = it ? sectionLink(p.client, it.section) : "";
  const btn = (url: string, icon: string, label: string, pri = false) => (url ? `<a class="btn sm ${pri ? "pri" : ""}" href="${esc(safeUrl(url))}" target="_blank" rel="noopener">${ic(icon, 15)} ${label}</a>` : "");
  const n = p.due ? daysFrom(p.due) : null;
  const live = it
    ? `<div class="pd-sec"><span class="flabel">Aus der Live-Tabelle</span><dl class="dl"><dt>Abschnitt</dt><dd>${esc(it.section) || '<span class="dim">–</span>'}</dd><dt>Video</dt><dd>${esc(it.video)}</dd><dt>Stand</dt><dd>${it.stand ? `<span class="chip ${isDone(it) ? "ok" : ""}">${esc(it.stand)}</span>` : '<span class="dim">–</span>'}</dd><dt>Dran</dt><dd>${esc(it.todo) || '<span class="dim">niemand Bestimmtes</span>'}</dd><dt>Deadline</dt><dd>${it.due ? `${fmtD(it.due)} <span class="chip ${relCls(daysFrom(it.due))}">${rel(daysFrom(it.due))}</span>` : esc(it.dueText) || '<span class="dim">–</span>'}</dd></dl>
      <div class="pd-btns">${driveBtn(p.client, it, true)}${btn(it.files, "folder", "Dateien")}${btn(broll, "film", "B-Roll")}${btn(it.videoUrl, "play", "Video")}${btn(up, "download", "Hochladen", true)}<button class="btn ghost sm" data-a="go" data-to="kunde-${esc(p.client)}" data-ctab="table">${ic("sheet", 15)} In der Live-Tabelle</button></div>
      ${(() => {
        const d = driveOf(p.client, it);
        return d && d.candidates.length > 1 && d.state !== "choose" ? `<p class="help" style="margin:8px 0 0">${d.state === "declined" ? "Keines der PDFs im Ordner ist als Drehbuch markiert." : `Drehbuch: ${esc(d.file?.name ?? "")}.`} <button class="linkish" data-a="drive-choose" data-cid="${esc(p.client)}" data-k="${esc(d.itemKey)}">Anderes PDF wählen</button></p>` : "";
      })()}
      ${feedbackBlock(p.client, it, true)}
      ${!broll && it.section ? `<p class="help" style="margin:10px 0 0">Für «${esc(it.section)}» ist noch kein B-Roll-Ordner hinterlegt. <button class="linkish" data-a="go" data-to="kunde-${esc(p.client)}">Beim Kunden hinterlegen</button></p>` : ""}</div>`
    : typeof p.sheetKey === "string"
      ? `<div class="pd-sec"><span class="flabel">Aus der Live-Tabelle</span><p class="muted small" style="margin:0">${LIVE[p.client]?.state === "loading" ? "Tabelle wird geladen …" : "Dieses Video steht nicht mehr in der Live-Tabelle."}</p></div>`
      : "";
  openModal(
    `<div class="pd" data-id="${esc(p.id)}">${mhead(esc(p.title))}
    <div class="pd-top">${cdot(c)}<a href="#kunde-${esc(p.client)}" data-a="go" data-to="kunde-${esc(p.client)}" class="pd-client">${esc(c?.name ?? "")}</a><select class="in sm" data-a="phase" data-id="${esc(p.id)}" aria-label="Phase">${PHASES.map((ph) => `<option ${ph === p.phase ? "selected" : ""}>${ph}</option>`).join("")}</select>${p.due && n != null ? `<span class="chip ${relCls(n)}">fällig ${fmtDate(p.due)}</span>` : ""}</div>
    ${live}
    <div class="pd-sec"><span class="flabel">MILKEE</span>${mkStatus(p)}</div></div>`,
    "wide",
  );
}

/** Deadline in Projektlisten: abgegebene oder fertige Videos nicht als fällig zeigen */
function projDue(p: Project) {
  if (!p.due || DONE_PHASES.includes(p.phase)) return "";
  const it = itemOfProject(p);
  if (p.phase === "Zu verrechnen" && (!it || isDone(it))) return `<div class="rs"><span class="chip ok">${ic("check", 12)} fertig</span></div>`;
  const st = it ? videoState(it) : undefined;
  if (st?.waiting) return `<div class="rs"><span class="chip ok">${ic("check", 12)} ${esc(st.label)}</span>${it?.todo ? ` bei ${esc(it.todo)}` : ""}</div>`;
  const n = daysFrom(p.due);
  return `<div class="rs">fällig ${fmtDate(p.due)}${n < 0 ? ` <span class="chip bad">überfällig</span>` : ""}</div>`;
}
function projTable(ps: Project[], showClient = true) {
  const rows = ps
    .map((p) => {
      const c = C(p.client);
      return `<tr><td><div class="rt"><button class="plink" data-a="proj-open" data-id="${esc(p.id)}">${esc(p.title)}</button>${p.milkeeProjectId ? ' <span class="chip acc mk-chip" title="Mit MILKEE verknüpft">MILKEE</span>' : ""}</div>${projDue(p)}</td>${showClient ? `<td class="hide-m"><a href="#kunde-${esc(p.client)}" style="text-decoration:none">${cdot(c)} ${esc(c?.name ?? "")}</a></td>` : ""}<td><select class="in sm" data-a="phase" data-id="${esc(p.id)}" aria-label="Phase">${PHASES.map((ph) => `<option ${ph === p.phase ? "selected" : ""}>${ph}</option>`).join("")}</select></td></tr>`;
    })
    .join("");
  return `<div class="tbl-wrap"><table class="tbl"><thead><tr><th>Projekt</th>${showClient ? '<th class="hide-m">Kunde</th>' : ""}<th>Phase</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}
/** Betrag eines Projekts, das zu verrechnen ist (Pauschale des Kunden), sonst 0. */
const projectValue = (p: Project) => (p.auto ? (billingOf(C(p.client))?.pricePerVideo ?? 0) : 0);
function projectSections(ps: Project[], showClient = true) {
  const work = ps.filter((p) => !DONE_PHASES.includes(p.phase) && p.phase !== "Zu verrechnen");
  const toBill = ps.filter((p) => p.phase === "Zu verrechnen");
  const done = ps.filter((p) => DONE_PHASES.includes(p.phase));
  const sum = toBill.reduce((a, p) => a + projectValue(p), 0);
  return `${work.length ? `<section class="panel flush">${projTable(work, showClient)}</section>` : ""}
  ${toBill.length ? `<section class="panel flush"><div class="panel-head"><h2 class="h2">Zu verrechnen</h2><span class="chip warn">${toBill.length}</span>${sum ? `<span class="small dim" style="margin-left:auto">${chf0(sum)} · in MILKEE beim Rechnungschreiben übernehmen</span>` : ""}</div>${projTable(toBill, showClient)}</section>` : ""}
  ${done.length ? `<details class="panel flush pfolder"><summary class="panel-head">${ic("folder", 18)}<h2 class="h2">Verrechnet und abgeschlossen</h2><span class="chip ok">${done.length}</span></summary>${projTable(done, showClient)}</details>` : ""}`;
}
/** Projekte nach Kunde gruppiert: pro Kunde eine Karte, erledigte im Ordner darunter. */
function projectsByClient(ps: Project[]) {
  const byDue = (a: Project, b: Project) => (a.due ? new Date(a.due).getTime() : Infinity) - (b.due ? new Date(b.due).getTime() : Infinity) || a.title.localeCompare(b.title);
  const groups = S.clients
    .map((c) => {
      const mine = ps.filter((p) => p.client === c.id);
      return { c, work: mine.filter((p) => isOpenWork(p)).sort(byDue), toBill: mine.filter((p) => p.phase === "Zu verrechnen").sort(byDue), done: mine.filter((p) => DONE_PHASES.includes(p.phase)) };
    })
    .filter((g) => g.work.length || g.toBill.length || g.done.length)
    .sort((a, b) => Number(!(b.work.length + b.toBill.length)) - Number(!(a.work.length + a.toBill.length)) || a.c.name.localeCompare(b.c.name));
  const active = groups.filter((g) => g.work.length || g.toBill.length);
  const doneGroups = groups.filter((g) => g.done.length);
  const doneCount = doneGroups.reduce((n, g) => n + g.done.length, 0);
  const card = (g: (typeof groups)[number]) => {
    const sum = g.toBill.reduce((a, p) => a + projectValue(p), 0);
    return `<section class="panel flush cgroup"><div class="panel-head"><a class="cg-name" href="#kunde-${esc(g.c.id)}"><span class="mono" style="--c:${esc(g.c.color)}">${esc(g.c.short)}</span><b>${esc(g.c.name)}</b></a>
      <span class="cg-meta">${earnChip(g.c.id)}${g.work.length ? `<span class="chip">${g.work.length} offen</span>` : ""}${g.toBill.length ? `<span class="chip warn">${g.toBill.length} zu verrechnen${sum ? ` · ${chf0(sum)}` : ""}</span>` : ""}${g.done.length ? `<span class="chip ok">${g.done.length} verrechnet</span>` : ""}</span></div>
      ${projTable([...g.work, ...g.toBill], false)}</section>`;
  };
  return `${active.map(card).join("") || emptyBox("Alles erledigt", "Keine offenen Projekte.")}
  ${doneCount ? `<details class="panel flush pfolder"><summary class="panel-head">${ic("folder", 18)}<h2 class="h2">Verrechnet und abgeschlossen</h2><span class="chip ok">${doneCount}</span></summary>${doneGroups.map((g) => `<div class="cg-sub">${cdot(g.c)} ${esc(g.c.name)}</div>${projTable(g.done, false)}`).join("")}</details>` : ""}`;
}
function vProjekte() {
  const ps = S.projects.filter((p) => !UI.projClient || p.client === UI.projClient);
  const done = ps.filter((p) => DONE_PHASES.includes(p.phase));
  const board = `<div class="board" id="board">${WORK_PHASES.map((ph) => {
    const items = ps.filter((p) => p.phase === ph);
    return `<div class="col" data-phase="${ph}"><div class="col-head">${ph}<span>${items.length}</span></div>${items.map((p) => {
      const c = C(p.client);
      return `<article class="pcard" draggable="true" data-id="${esc(p.id)}" data-a="proj-open"><div class="who">${cdot(c)}<span class="ellip">${esc(c?.name ?? "")}</span></div><h4>${esc(p.title)}</h4></article>`;
    }).join("")}</div>`;
  }).join("")}</div>${done.length ? `<details class="panel flush pfolder" style="margin-top:16px"><summary class="panel-head">${ic("folder", 18)}<h2 class="h2">Verrechnet und abgeschlossen</h2><span class="chip ok">${done.length}</span></summary>${projTable(done)}</details>` : ""}`;
  return `${pageHead("Arbeit", "Projekte", "", `<button class="btn pri" data-a="project-new">${ic("plus", 16)} Projekt erstellen</button>`)}
  <div class="filters"><div class="seg"><button class="${UI.projView === "clients" ? "on" : ""}" data-a="projview" data-k="clients" title="Nach Kunde">${ic("users", 14)} Nach Kunde</button><button class="${UI.projView === "list" ? "on" : ""}" data-a="projview" data-k="list" title="Nach Phase">${ic("list", 14)} Nach Phase</button><button class="${UI.projView === "board" ? "on" : ""}" data-a="projview" data-k="board" title="Board">${ic("board", 14)} Board</button></div><select class="in sm" data-a="projclient" aria-label="Kunde filtern">${clientOptions(UI.projClient, "Alle Kunden")}</select></div>
  ${ps.length ? (UI.projView === "board" ? board : UI.projView === "clients" ? projectsByClient(ps) : projectSections(ps)) : emptyBox("Keine Projekte", "Erstelle ein Projekt, um Phase und Deadline im Blick zu behalten.")}`;
}

/* ============ Einstellungen ============ */

interface FieldOpts {
  full?: boolean;
  type?: string;
  ph?: string;
  help?: string;
  options?: [string, string][];
  auto?: string;
  step?: string;
}
function fld(id: string, label: string, v: string, o: FieldOpts = {}) {
  const input = o.options
    ? `<select class="in" id="${id}">${o.options.map(([value, lb]) => `<option value="${esc(value)}" ${v === value ? "selected" : ""}>${esc(lb)}</option>`).join("")}</select>`
    : `<input class="in" id="${id}" value="${esc(v)}" ${o.type ? `type="${o.type}"` : ""} ${o.step ? `step="${o.step}" min="0" inputmode="decimal"` : ""} ${o.ph ? `placeholder="${esc(o.ph)}"` : ""} autocomplete="${o.auto ?? "off"}">`;
  return `<div class="field ${o.full ? "full" : ""}"><label for="${id}">${label}</label>${input}${o.help ? `<span class="help">${o.help}</span>` : ""}</div>`;
}
const panelHead = (title: string, lede: string, extra = "") =>
  `<div class="panel-head" style="flex-wrap:wrap"><div class="min0" style="flex:1"><h2 class="h2">${title}</h2>${lede ? `<p class="muted" style="margin:4px 0 0;font-size:13.5px">${lede}</p>` : ""}</div>${extra}</div>`;

function sets(): [string, string, string][] {
  return [["sicherheit", "Sicherheit", "lock"], ["allgemein", "Allgemein", "gear"], ...(isAdmin() ? ([["benutzer", "Benutzer", "users"], ["api", "API-Zugang", "key"]] as [string, string, string][]) : []), ["verbindungen", "Verbindungen", "link"], ["daten", "Daten", "db"]];
}

function vEinstellungen() {
  const c = CO();
  if (!sets().some(([k]) => k === UI.set)) UI.set = "sicherheit";
  let body = "";
  if (UI.set === "allgemein")
    body = `<section class="panel">${panelHead("Allgemein", "Name für die Begrüssung auf der Übersicht.")}<form data-form="settings"><div class="form-grid">${fld("st-owner", "Firmeninhaber:in", c.owner)}</div><div class="form-foot"><button class="btn pri" type="submit">${ic("check", 16)} Speichern</button></div></form></section>${socialPanel()}`;
  if (UI.set === "verbindungen") body = connectionsPanel();
  if (UI.set === "sicherheit")
    body = `<section class="panel">${panelHead("Sicherheit", "Anmeldung mit Passwort und dem Code aus deiner Authenticator-App. Die Daten liegen auf dem eigenen Server.")}<dl class="dl"><dt>Angemeldet als</dt><dd>${esc(ME?.user.email ?? "")}</dd><dt>Rolle</dt><dd>${isAdmin() ? "Admin" : "Mitglied"}</dd><dt>2-Faktor</dt><dd><span class="chip ok">${ic("check", 13)} aktiv</span></dd><dt>Automatisch abmelden</dt><dd>nach 12 Stunden ohne Aktivität</dd></dl><div class="form-foot" style="justify-content:flex-start"><button class="btn" data-a="logout">${ic("lock", 16)} Abmelden</button></div></section>
  <section class="panel">${panelHead("Passwort ändern", "Mindestens 14 Zeichen. Am besten ein Passwort aus deinem Passwort-Manager.")}<form data-form="password" class="form-grid">${fld("pw-cur", "Aktuelles Passwort", "", { type: "password", full: true, auto: "current-password" })}${fld("pw-new", "Neues Passwort", "", { type: "password", auto: "new-password" })}${fld("pw-new2", "Neues Passwort wiederholen", "", { type: "password", auto: "new-password" })}<div class="form-foot full" style="margin-top:4px"><button class="btn pri" type="submit">Passwort ändern</button></div></form></section>`;
  if (UI.set === "benutzer") body = usersPanel();
  if (UI.set === "api") body = tokensPanel();
  if (UI.set === "daten")
    body = `<section class="panel">${panelHead("Daten", "")}<dl class="dl"><dt>Speicherort</dt><dd>SQLite-Datenbank auf dem eigenen Server. Zugriff nur mit Passwort und 2-Faktor.</dd><dt>Kunden</dt><dd class="tnum">${S.clients.length}</dd><dt>Projekte</dt><dd class="tnum">${S.projects.length}</dd></dl>
    <div class="form-foot" style="justify-content:flex-start;flex-wrap:wrap"><button class="btn" data-a="export">${ic("download", 16)} Sicherung herunterladen</button>${isAdmin() ? `<label class="btn" style="cursor:pointer">${ic("folder", 16)} Sicherung importieren<input type="file" accept="application/json,.json" hidden data-a="import-file"></label>` : ""}</div>
    <p class="help" style="margin:12px 0 0">Die Sicherung enthält alle Kundendaten. Bewahre sie gut auf und lade sie nie auf GitHub hoch.</p></section>`;
  return `${pageHead("", "Einstellungen", "")}<div class="settings"><nav class="snav" aria-label="Einstellungen">${sets().map(([k, l, i]) => `<button class="${UI.set === k ? "on" : ""}" data-a="set-sec" data-k="${k}">${ic(i, 17)}${l}</button>`).join("")}</nav><div class="stack">${body}</div></div>`;
}

/* ---------- Benutzerverwaltung (nur Admins) ---------- */

async function loadUsers() {
  try {
    USERS = (await api.users()).users;
  } catch (e) {
    if (!onAuthLost(e)) toast((e as Error).message);
    USERS = [];
  }
  if (UI.view === "einstellungen" && UI.set === "benutzer") render();
}
function usersPanel() {
  if (USERS === null) {
    void loadUsers();
    return `<section class="panel"><div class="loading" style="padding:40px 0">Benutzer werden geladen …</div></section>`;
  }
  const meId = ME?.user.id;
  const rows = USERS.map((u) => {
    const self = u.id === meId;
    const status = u.disabled ? '<span class="chip bad">deaktiviert</span>' : !u.totpEnabled ? '<span class="chip warn">2FA fehlt</span>' : u.mustChangePassword ? '<span class="chip warn">Startpasswort</span>' : '<span class="chip ok">aktiv</span>';
    const menu = self
      ? '<span class="small dim">du</span>'
      : `<details class="menu"><summary class="ibtn outline" aria-label="Aktionen für ${esc(u.email)}">${ic("more", 18)}</summary><div class="menu-list">
        <button data-a="user-role" data-id="${esc(u.id)}">${ic("shield", 16)} ${u.role === "admin" ? "Zum Mitglied machen" : "Zum Admin machen"}</button>
        <button data-a="user-reset-pw" data-id="${esc(u.id)}">${ic("key", 16)} Neues Startpasswort</button>
        <button data-a="user-reset-2fa" data-id="${esc(u.id)}">${ic("refresh", 16)} 2-Faktor zurücksetzen</button>
        <button data-a="user-toggle" data-id="${esc(u.id)}">${ic("lock", 16)} ${u.disabled ? "Aktivieren" : "Deaktivieren"}</button>
        <hr><button class="dng" data-a="user-del" data-id="${esc(u.id)}">${ic("trash", 16)} Löschen</button></div></details>`;
    return `<tr><td><div class="rt ellip">${esc(u.name || u.email)}</div><div class="rs ellip">${esc(u.email)}</div></td><td class="hide-m">${u.role === "admin" ? "Admin" : "Mitglied"}</td><td>${status}</td><td class="hide-m tnum muted">${u.lastSeenAt ? fmtDate(u.lastSeenAt) : "–"}</td><td class="r">${menu}</td></tr>`;
  }).join("");
  return `<section class="panel flush">${panelHead("Benutzer", "Wer Zugang zu Social Solve Management hat. Neue Benutzer erhalten ein Startpasswort und richten bei der ersten Anmeldung 2-Faktor und ein eigenes Passwort ein.", `<button class="btn pri sm" data-a="user-new">${ic("plus", 14)} Benutzer</button>`)}
  <div class="tbl-wrap"><table class="tbl"><thead><tr><th>Name</th><th class="hide-m">Rolle</th><th>Status</th><th class="hide-m">Zuletzt aktiv</th><th></th></tr></thead><tbody>${rows}</tbody></table></div></section>`;
}
function userModal() {
  openModal(
    `${mhead("Benutzer erstellen")}<form data-form="user" class="stack" style="gap:14px"><div class="form-grid">${fld("us-email", "E-Mail", "", { full: true, type: "email" })}${fld("us-name", "Name", "")}${fld("us-role", "Rolle", "member", { options: [["member", "Mitglied"], ["admin", "Admin"]] })}</div><p class="help" style="margin:0">Admins können Benutzer verwalten. Mitglieder sehen und bearbeiten alle Kunden und Projekte.</p>${mfoot("Erstellen")}</form>`,
  );
}
function passwordModal(email: string, password: string, intro: string) {
  openModal(
    `${mhead("Startpasswort")}<p class="muted" style="margin:0">${intro} Gib es <b>${esc(email)}</b> auf sicherem Weg weiter (nicht per E-Mail). Es wird nur jetzt angezeigt.</p><code class="secret" style="font-size:18px;text-align:center;user-select:all">${esc(password)}</code><div class="modal-foot"><button class="btn" data-a="copy" data-v="${esc(password)}">${ic("copy", 16)} Kopieren</button><button class="btn pri" data-a="close">Fertig</button></div>`,
  );
}
async function userAction(act: string, id: string) {
  const u = USERS?.find((x) => x.id === id);
  if (!u) return;
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok);
    } catch (e) {
      if (!onAuthLost(e)) toast((e as Error).message);
    }
    await loadUsers();
  };
  if (act === "user-role") await run(() => api.updateUser(id, { role: u.role === "admin" ? "member" : "admin" }), "Rolle geändert");
  if (act === "user-toggle") await run(() => api.updateUser(id, { disabled: !u.disabled }), u.disabled ? "Benutzer aktiviert" : "Benutzer deaktiviert und abgemeldet");
  if (act === "user-reset-2fa")
    confirmModal(`Die 2-Faktor-Anmeldung von ${esc(u.email)} wird zurückgesetzt. Bei der nächsten Anmeldung richtet die Person sie neu ein.`, "Zurücksetzen", () => run(() => api.reset2fa(id), "2-Faktor zurückgesetzt"));
  if (act === "user-del") confirmModal(`${esc(u.email)} wird gelöscht und kann sich nicht mehr anmelden. Kunden und Projekte bleiben erhalten.`, "Löschen", () => run(() => api.deleteUser(id), "Benutzer gelöscht"));
  if (act === "user-reset-pw")
    confirmModal(`${esc(u.email)} erhält ein neues Startpasswort und wird abgemeldet.`, "Neues Passwort", async () => {
      try {
        const r = await api.resetPassword(id);
        passwordModal(u.email, r.password, "Das neue Startpasswort ist erstellt.");
      } catch (e) {
        if (!onAuthLost(e)) toast((e as Error).message);
      }
      await loadUsers();
    });
}

/* ---------- Verbindungen ---------- */

async function loadGoogle() {
  try {
    GOOGLE = await api.google();
  } catch (e) {
    if (!onAuthLost(e)) toast((e as Error).message);
    GOOGLE = { configured: !!SA_EMAIL, email: SA_EMAIL, source: null };
  }
  if (UI.view === "einstellungen" && UI.set === "verbindungen") render();
}
function connectionsPanel() {
  if (isAdmin() && GOOGLE === null) void loadGoogle();
  const on = !!SA_EMAIL;
  const admin = isAdmin()
    ? `<div class="filters" style="margin-top:4px"><label class="btn sm ${on ? "" : "pri"}" style="cursor:pointer">${ic("download", 14)} ${on ? "Schlüssel ersetzen" : "Schlüsseldatei hochladen"}<input type="file" accept="application/json,.json" hidden data-a="google-file"></label>${GOOGLE?.source === "upload" ? `<button class="btn ghost sm" data-a="google-remove">${ic("trash", 14)} Entfernen</button>` : ""}</div>
      <p class="help" style="margin:8px 0 0">Die JSON-Datei des Google-Dienstkontos (Google Cloud → IAM → Dienstkonten → Schlüssel). Sie wird geprüft, verschlüsselt auf dem Server gespeichert und nie wieder angezeigt.${GOOGLE?.source === "file" ? " Aktuell kommt der Schlüssel aus einer Datei auf dem Server." : ""}</p>`
    : "";
  return `${healthPanel()}<section class="panel">${panelHead("Verbindungen", "Was Social Solve Management mit anderen Diensten verbindet.")}<div class="igrid">
    <div class="panel icard" style="padding:18px"><div style="display:flex;gap:12px;align-items:center"><span class="lic">${ic("sheet", 20)}</span><h3 class="h3" style="flex:1">Google Sheets</h3><span class="chip ${on ? "ok" : "warn"}">${on ? "Verbunden" : "Schlüssel fehlt"}</span></div><p>${on ? `Liest und schreibt Kundentabellen über den eigenen Server. Teile Tabellen in Google Sheets als «Bearbeiter» mit <b style="user-select:all">${esc(SA_EMAIL)}</b>.` : "Für die Live-Tabellen braucht Social Solve Management den Schlüssel eines Google-Dienstkontos."}</p>${admin}</div>
    ${milkeeCard()}${msCard()}${nextcloudCard()}${apnsCard()}</section>`;
}
function afterGoogleChange(info: GoogleInfo, msg: string) {
  GOOGLE = info;
  SA_EMAIL = info.email;
  toast(msg);
  Object.keys(POLL).forEach((cid) => {
    const L = LIVE[cid];
    if (L && !L.items.length) L.state = "loading";
    void fetchSheet(cid);
  });
  render();
}
function uploadGoogleKey(file: File) {
  if (file.size > 20_000) return toast("Die Datei ist zu gross für eine Schlüsseldatei.");
  const r = new FileReader();
  r.onload = () => {
    toast("Schlüssel wird bei Google geprüft …");
    void api.uploadGoogle(String(r.result)).then(
      (info) => afterGoogleChange(info, "Google Sheets ist verbunden"),
      (err: unknown) => {
        if (!onAuthLost(err)) toast((err as Error).message);
      },
    );
  };
  r.readAsText(file);
}

/* ---------- MILKEE: Verbindung, Finanzen, Abrechnung ---------- */

const chfPlain = (n: number) => `CHF ${n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const chf0Plain = (n: number) => `CHF ${Math.round(n).toLocaleString("de-CH")}`;
/** Beträge in der Seite: im Privatmodus unscharf (in Meldungen und Diagrammen die *Plain-Form) */
const chf = (n: number) => sens(chfPlain(n));
const chf0 = (n: number) => sens(chf0Plain(n));
function billingOf(c: Client | undefined): ClientBilling | null {
  const b = c?.billing as Partial<ClientBilling> | undefined;
  return b && Number(b.milkeeCustomerId) > 0 && Number(b.pricePerVideo) > 0
    ? { milkeeCustomerId: Number(b.milkeeCustomerId), milkeeCustomerName: String(b.milkeeCustomerName ?? ""), pricePerVideo: Number(b.pricePerVideo), auto: b.auto === true }
    : null;
}

/** Profil, unter dem die Social-Media-Vorschau der Abgaben erscheint */
let SOCIAL: { name: string; handle: string; line: string } | null = null;
function socialPanel() {
  if (SOCIAL === null) {
    SOCIAL = { name: "", handle: "", line: "" };
    void api.socialSettings().then(
      (b) => {
        SOCIAL = b;
        if (UI.view === "einstellungen") render();
      },
      () => undefined,
    );
  }
  const s = SOCIAL;
  return `<section class="panel">${panelHead("Social-Media-Vorschau", "So erscheint dein Profil, wenn Kunden in einer Abgabe die Instagram-, TikTok- oder LinkedIn-Ansicht einschalten.")}
    <div class="soc-prev"><img src="/social-avatar.png" alt="" width="56" height="56"><div><b>${esc(s.name || "Social Solve")}</b><span>@${esc(s.handle || "socialsolve.ch")}</span><small>${esc(s.line)}</small></div></div>
    <form data-form="social"><div class="form-grid">${fld("so-name", "Name", s.name)}${fld("so-handle", "Benutzername (Instagram/TikTok)", s.handle, { ph: "socialsolve.ch" })}${fld("so-line", "Zeile unter dem Namen (LinkedIn)", s.line, { full: true, ph: "z. B. Videoproduktion für Social Media · St. Gallen" })}</div>
    ${isAdmin() ? `<div class="form-foot"><button class="btn pri" type="submit">${ic("check", 16)} Speichern</button></div>` : ""}</form></section>`;
}
function nextcloudCard() {
  if (!isAdmin()) return "";
  if (NCI === null) {
    NCI = { configured: false, url: "", user: "", root: "" };
    void api.nextcloudSettings().then(
      (i) => {
        NCI = i;
        if (UI.view === "einstellungen") render();
      },
      () => undefined,
    );
  }
  const on = NCI.configured;
  return `<div class="panel icard" style="padding:18px"><div style="display:flex;gap:12px;align-items:center"><span class="lic">${ic("cloud", 20)}</span><h3 class="h3" style="flex:1">Nextcloud</h3><span class="chip ${on ? "ok" : ""}">${on ? "Verbunden" : "Nicht verbunden"}</span></div>
    <p>${on ? `Videos der Abgaben werden in <b>${esc(NCI.url.replace(/^https?:\/\//, ""))}</b> gespeichert, Konto <b>${esc(NCI.user)}</b>, Ordner <b>${esc(NCI.root)}</b>/Kunde/Titel. Kunden sehen die Nextcloud nie.` : "Speichert die Videos der Abgaben in deiner Nextcloud statt auf dem Server. Am besten mit einem eigenen Konto, das nur den Abgaben-Ordner sieht, und einem App-Passwort."}</p>
    <form data-form="nextcloud" class="nc-form">
      <input class="in" id="nc-url" placeholder="https://files.davidoldani.ch" value="${esc(NCI.url || "https://files.davidoldani.ch")}" autocomplete="off">
      <div class="nc-two"><input class="in" id="nc-user" placeholder="Benutzer, z. B. management" value="${esc(NCI.user)}" autocomplete="off"><input class="in" id="nc-root" placeholder="Ordner" value="${esc(NCI.root || "Abgaben")}" autocomplete="off"></div>
      <input class="in" id="nc-pass" type="password" placeholder="${on ? "App-Passwort (nur zum Ändern)" : "App-Passwort aus der Nextcloud"}" autocomplete="new-password">
      <div class="add-row"><button class="btn sm ${on ? "" : "pri"}" type="submit">${on ? "Speichern" : "Verbinden"}</button>${on ? `<button type="button" class="btn ghost sm" data-a="nextcloud-remove">${ic("trash", 14)} Trennen</button>` : ""}</div>
    </form>
    <p class="help" style="margin:8px 0 0">App-Passwort: in der Nextcloud mit dem Konto anmelden → Einstellungen → Sicherheit → «Neues App-Passwort erstellen». Es wird geprüft, verschlüsselt gespeichert und nie wieder angezeigt.</p></div>`;
}
function apnsCard() {
  if (!isAdmin()) return "";
  if (APNS === null) {
    APNS = { configured: false, keyId: "", teamId: "", bundleId: "" };
    void api.apnsSettings().then(
      (i) => {
        APNS = i;
        if (UI.view === "einstellungen") render();
      },
      () => undefined,
    );
  }
  const on = APNS.configured;
  const n = APNS.devices ?? 0;
  return `<div class="panel icard" style="padding:18px"><div style="display:flex;gap:12px;align-items:center"><span class="lic">${ic("bell", 20)}</span><h3 class="h3" style="flex:1">iPhone-App (Push)</h3><span class="chip ${on ? "ok" : ""}">${on ? "Eingerichtet" : "Nicht eingerichtet"}</span></div>
    <p>${on ? `Neue Hinweise (Abgaben, Änderungen in Kundentabellen, Automatik) gehen als Push-Mitteilung an ${n === 1 ? "1 iPhone" : `${n} iPhones`}. Key-ID <b>${esc(APNS.keyId)}</b>, Team <b>${esc(APNS.teamId)}</b>.` : "Damit Hinweise als Push-Mitteilung aufs iPhone kommen, braucht der Server einen APNs-Schlüssel aus dem Apple-Developer-Konto."}</p>
    <form data-form="apns" class="nc-form">
      <div class="nc-two"><input class="in" id="ap-key" placeholder="Key-ID (10 Zeichen)" value="${esc(APNS.keyId)}" autocomplete="off"><input class="in" id="ap-team" placeholder="Team-ID (10 Zeichen)" value="${esc(APNS.teamId)}" autocomplete="off"></div>
      <label class="btn sm" style="cursor:pointer;align-self:flex-start">${ic("download", 14)} .p8-Datei wählen<input type="file" id="ap-file" accept=".p8" hidden></label>
      <div class="add-row"><button class="btn sm ${on ? "" : "pri"}" type="submit">${on ? "Ersetzen" : "Einrichten"}</button>${on ? `<button type="button" class="btn ghost sm" data-a="apns-test">${ic("send", 14)} Testmitteilung</button><button type="button" class="btn ghost sm" data-a="apns-remove">${ic("trash", 14)} Entfernen</button>` : ""}</div>
    </form>
    <p class="help" style="margin:8px 0 0">developer.apple.com → Certificates, IDs &amp; Profiles → Keys → «+» → «Apple Push Notifications service (APNs)» → herunterladen. Die Datei wird verschlüsselt auf dem Server gespeichert und nie wieder angezeigt. Nicht per Mail oder Chat verschicken.</p></div>`;
}
function milkeeCard() {
  const on = MK.configured;
  const admin = isAdmin()
    ? `<form data-form="milkee" class="add-row" style="margin-top:4px"><input class="in" id="mk-token" type="password" placeholder="${on ? "Neues Token (optional)" : "API-Token aus MILKEE"}" autocomplete="off" style="flex:1 1 180px"><button class="btn sm ${on ? "" : "pri"}" type="submit">${on ? "Ersetzen" : "Verbinden"}</button>${on ? `<button type="button" class="btn ghost sm" data-a="milkee-remove">${ic("trash", 14)} Trennen</button>` : ""}</form>
      <p class="help" style="margin:8px 0 0">In MILKEE: Einstellungen → API → API Tokens → Token erstellen. Das Token wird geprüft, verschlüsselt auf dem Server gespeichert und nie wieder angezeigt. Nicht per Mail oder Chat verschicken.</p>`
    : "";
  return `<div class="panel icard" style="padding:18px"><div style="display:flex;gap:12px;align-items:center"><span class="lic">${ic("receipt", 20)}</span><h3 class="h3" style="flex:1">MILKEE</h3><span class="chip ${on ? "ok" : ""}">${on ? "Verbunden" : "Nicht verbunden"}</span></div><p>${on ? `Buchhaltung von <b>${esc(MK.company)}</b>. Social Solve Management zeigt Einnahmen und offene Rechnungen und legt für fertige Videos Rechnungsentwürfe an. Alles andere bleibt in MILKEE.` : "Verbinde MILKEE, um Einnahmen und offene Rechnungen zu sehen und fertige Videos direkt zu verrechnen."}</p>${admin}<a class="btn ghost sm" href="https://app.milkee.ch" target="_blank" rel="noopener" style="align-self:flex-start">${ic("ext", 14)} MILKEE öffnen</a></div>`;
}

async function loadFin(refresh = false) {
  if (!MK.configured) return;
  FIN = { ...FIN, state: FIN.data ? FIN.state : "loading" };
  try {
    FIN = { state: "ok", data: await api.milkeeOverview(refresh) };
  } catch (e) {
    if (onAuthLost(e)) return;
    FIN = { ...FIN, state: FIN.data ? "ok" : "error", err: (e as Error).message };
    if (FIN.data) toast((e as Error).message);
  }
  if (UI.view === "finanzen" || UI.view === "uebersicht") render();
}
function finStat() {
  if (!MK.configured) return "";
  if (FIN.state === "idle") void loadFin();
  const d = FIN.data;
  if (!d) return `<button class="stat" data-a="go" data-to="finanzen"><span>Offene Rechnungen</span><b>…</b><small>aus MILKEE</small></button>`;
  return `<button class="stat" data-a="go" data-to="finanzen"><span>Offene Rechnungen</span><b>${chf0(d.openTotal)}</b><small>${d.open.length} offen${d.overdueCount ? ` · <span class="amt-out">${d.overdueCount} überfällig</span>` : ""}</small></button>`;
}

function finChart(d: MilkeeOverview) {
  const W = 640;
  const H = 200;
  const top = 16;
  const base = 168;
  const max = Math.max(1, ...d.income, ...d.expense);
  const nice = (() => {
    const p = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / p) * p;
  })();
  const y = (v: number) => base - ((base - top) * v) / nice;
  const step = W / 12;
  const bw = Math.min(16, step * 0.28);
  const now = new Date();
  let g = `<line class="grid" x1="0" x2="${W}" y1="${y(nice)}" y2="${y(nice)}"/><line class="grid" x1="0" x2="${W}" y1="${y(nice / 2)}" y2="${y(nice / 2)}"/><line class="zero" x1="0" x2="${W}" y1="${base}" y2="${base}"/>`;
  d.income.forEach((inc, i) => {
    const cx = step * i + step / 2;
    const out = d.expense[i] ?? 0;
    const future = d.year === now.getFullYear() && i > now.getMonth();
    if (inc) g += `<rect x="${cx - bw - 1}" y="${y(inc)}" width="${bw}" height="${base - y(inc)}" rx="3" fill="var(--ok)"><title>${MONS[i]}: Einnahmen ${chf(inc)}</title></rect>`;
    if (out) g += `<rect x="${cx + 1}" y="${y(out)}" width="${bw}" height="${base - y(out)}" rx="3" fill="var(--bad)" opacity=".75"><title>${MONS[i]}: Ausgaben ${chf(out)}</title></rect>`;
    g += `<text x="${cx}" y="${H - 8}" text-anchor="middle" ${future ? 'opacity=".45"' : ""}>${MONS[i]}</text>`;
  });
  g += `<text class="sens" x="${W}" y="${y(nice) - 4}" text-anchor="end">${chf0Plain(nice)}</text>`;
  return `<div class="legend"><span><i class="dot" style="--c:var(--ok)"></i>Einnahmen</span><span><i class="dot" style="--c:var(--bad)"></i>Ausgaben</span></div><svg class="chart" viewBox="0 0 ${W} ${H}" width="100%" role="img" aria-label="Einnahmen und Ausgaben ${d.year} pro Monat">${g}</svg>`;
}

/** Fertige Videos aller Kunden mit Abrechnung, die noch nicht verrechnet sind. */
function toBill() {
  const out: { c: Client; items: LiveItem[]; price: number }[] = [];
  for (const c of S.clients) {
    const b = billingOf(c);
    const L = LIVE[c.id];
    if (!b || !L) continue;
    const rows = BILLED[c.id];
    if (rows === undefined) void loadBilled(c.id);
    if (!rows) continue;
    const items = b.auto
      ? L.items.filter((it) => S.projects.some((p) => p.client === c.id && p.sheetKey === itemKey(it) && p.phase === "Zu verrechnen"))
      : L.items.filter((it) => isDone(it) && !rows.some((x) => x.itemKey === itemKey(it)));
    if (items.length) out.push({ c, items, price: b.pricePerVideo });
  }
  return out;
}

/* ---------- Auswertung (Buchhaltung) ---------- */

type RangeKey = "year" | "12m" | "quarter" | "lastyear" | "3y";
const RANGE_LABEL: Record<RangeKey, string> = { year: "Dieses Jahr", "12m": "Letzte 12 Monate", quarter: "Dieses Quartal", lastyear: "Letztes Jahr", "3y": "Letzte 3 Jahre" };
const isoDay = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
function rangeOf(k: RangeKey) {
  const now = new Date();
  const y = now.getFullYear();
  const to = isoDay(now);
  if (k === "lastyear") return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31`, label: String(y - 1) };
  if (k === "quarter") {
    const q = Math.floor(now.getMonth() / 3);
    return { from: `${y}-${pad2(q * 3 + 1)}-01`, to, label: `Q${q + 1} ${y}` };
  }
  if (k === "12m") return { from: isoDay(new Date(y, now.getMonth() - 11, 1)), to, label: "letzte 12 Monate" };
  if (k === "3y") return { from: `${y - 2}-01-01`, to, label: `${y - 2}–${y}` };
  return { from: `${y}-01-01`, to, label: String(y) };
}
const rangeSeg = (keys: RangeKey[], cur: RangeKey, act: string) => `<div class="seg">${keys.map((k) => `<button class="${cur === k ? "on" : ""}" data-a="${act}" data-k="${k}">${RANGE_LABEL[k]}</button>`).join("")}</div>`;
const pct = (n: number) => `${Math.round(n * 100)} %`;
/** Veränderung zum Vorjahreszeitraum */
function delta(now: number, before: number) {
  if (!before) return now ? '<span class="dlt up">neu</span>' : "";
  const d = (now - before) / Math.abs(before);
  const r = Math.round(d * 100);
  return `<span class="dlt ${r > 0 ? "up" : r < 0 ? "down" : ""}">${r > 0 ? "▲" : r < 0 ? "▼" : "±"} ${Math.abs(r)} %</span>`;
}
/** Eigener Kunde zu einem MILKEE-Kunden */
const clientOfMk = (mkId: number | null) => (mkId ? S.clients.find((c) => mkCustomerOf(c) === mkId) : undefined);
function custLink(mkId: number | null, name: string) {
  const c = clientOfMk(mkId);
  return c ? `<a class="lnk" href="#kunde-${esc(c.id)}" data-a="go" data-to="kunde-${esc(c.id)}" data-ctab="finance">${esc(c.name)}</a>` : esc(name || "–");
}

let INS: { key: string; state: "idle" | "loading" | "ok" | "error"; data?: MkInsights; err?: string } = { key: "", state: "idle" };
async function loadInsights(refresh = false) {
  if (!MK.configured) return;
  const r = rangeOf(MKUI.finRange);
  const key = `${r.from}|${r.to}`;
  if (INS.state === "loading" && INS.key === key && !refresh) return;
  INS = { key, state: "loading", ...(INS.key === key && INS.data ? { data: INS.data } : {}) };
  try {
    const data = await api.mkInsights(r.from, r.to, refresh);
    if (INS.key !== key) return;
    INS = { key, state: "ok", data };
  } catch (e) {
    if (onAuthLost(e)) return;
    INS = { key, state: "error", err: (e as Error).message };
  }
  if (["finanzen", "offerten", "kunden"].includes(UI.view)) render();
}
/** Umsatz dieses Jahres pro MILKEE-Kunde (für die Kundenliste) */
let YEAR_REV: { state: "idle" | "loading" | "ok"; map: Map<number, number> } = { state: "idle", map: new Map() };
async function loadYearRevenue() {
  if (!MK.configured || YEAR_REV.state !== "idle") return;
  YEAR_REV = { state: "loading", map: new Map() };
  try {
    const r = rangeOf("year");
    const d = await api.mkInsights(r.from, r.to);
    YEAR_REV = { state: "ok", map: new Map(d.customers.filter((c) => c.id).map((c) => [c.id!, c.invoiced])) };
  } catch {
    YEAR_REV = { state: "ok", map: new Map() };
  }
  if (UI.view === "kunden") render();
}

/** Balken pro Monat: Einnahmen, Ausgaben und (als Linie) fakturiert */
function monthChart(months: { key: string; invoiced: number; income?: number; expense?: number }[], opts: { cash: boolean }) {
  const W = 640;
  const H = 210;
  const top = 18;
  const base = 176;
  const vals = months.flatMap((m) => [m.invoiced, opts.cash ? (m.income ?? 0) : 0, opts.cash ? (m.expense ?? 0) : 0]);
  const max = Math.max(1, ...vals);
  const nice = (() => {
    const p = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / p) * p;
  })();
  const y = (v: number) => base - ((base - top) * v) / nice;
  const step = W / months.length;
  const bw = Math.max(3, Math.min(14, step * (opts.cash ? 0.26 : 0.5)));
  const years = new Set(months.map((m) => m.key.slice(0, 4))).size > 1;
  const every = months.length > 18 ? 3 : months.length > 12 ? 2 : 1;
  let g = `<line class="grid" x1="0" x2="${W}" y1="${y(nice)}" y2="${y(nice)}"/><line class="grid" x1="0" x2="${W}" y1="${y(nice / 2)}" y2="${y(nice / 2)}"/><line class="zero" x1="0" x2="${W}" y1="${base}" y2="${base}"/>`;
  const pts: string[] = [];
  months.forEach((m, i) => {
    const cx = step * i + step / 2;
    const mo = Number(m.key.slice(5, 7)) - 1;
    const lab = `${MONS[mo]}${years && (mo === 0 || i === 0) ? ` ${m.key.slice(2, 4)}` : ""}`;
    if (opts.cash) {
      if (m.income) g += `<rect x="${cx - bw - 1}" y="${y(m.income)}" width="${bw}" height="${base - y(m.income)}" rx="3" fill="var(--ok)"><title>${lab}: Einnahmen ${chf(m.income)}</title></rect>`;
      if (m.expense) g += `<rect x="${cx + 1}" y="${y(m.expense)}" width="${bw}" height="${base - y(m.expense)}" rx="3" fill="var(--bad)" opacity=".75"><title>${lab}: Ausgaben ${chf(m.expense)}</title></rect>`;
      pts.push(`${cx},${y(m.invoiced)}`);
    } else if (m.invoiced) g += `<rect x="${cx - bw / 2}" y="${y(m.invoiced)}" width="${bw}" height="${base - y(m.invoiced)}" rx="3" fill="var(--accent)"><title>${lab}: fakturiert ${chf(m.invoiced)}</title></rect>`;
    if (i % every === 0) g += `<text x="${cx}" y="${H - 8}" text-anchor="middle">${lab}</text>`;
  });
  if (opts.cash && pts.length > 1) g += `<polyline points="${pts.join(" ")}" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" opacity=".9"/>${months.map((m, i) => `<circle cx="${step * i + step / 2}" cy="${y(m.invoiced)}" r="2.6" fill="var(--accent)"><title>${MONS[Number(m.key.slice(5, 7)) - 1]}: fakturiert ${chf(m.invoiced)}</title></circle>`).join("")}`;
  g += `<text class="sens" x="${W}" y="${y(nice) - 5}" text-anchor="end">${chf0Plain(nice)}</text>`;
  const legend = opts.cash
    ? `<div class="legend"><span><i class="dot" style="--c:var(--accent)"></i>Fakturiert</span><span><i class="dot" style="--c:var(--ok)"></i>Einnahmen</span><span><i class="dot" style="--c:var(--bad)"></i>Ausgaben</span></div>`
    : `<div class="legend"><span><i class="dot" style="--c:var(--accent)"></i>Fakturiert pro Monat</span></div>`;
  return `${legend}<svg class="chart" viewBox="0 0 ${W} ${H}" width="100%" role="img" aria-label="Verlauf pro Monat">${g}</svg>`;
}
/** Waagrechte Balken mit Anteil (Kunden, Kosten) */
const shareRow = (label: string, sub: string, value: number, share: number, color = "var(--accent)") =>
  `<div class="srow"><div class="srow-top"><span class="min0 ellip">${label}</span><b class="tnum">${chf0(value)}</b></div><div class="sbar"><i style="width:${Math.max(1.5, share * 100)}%;background:${color}"></i></div>${sub ? `<small>${sub}</small>` : ""}</div>`;

function vFinanzen() {
  const head = (acts = "") => pageHead("Buchhaltung", "Auswertung", MK.configured ? `Alles aus MILKEE${MK.company ? ` (${esc(MK.company)})` : ""}. Rechnungen und Offerten schreibst du in MILKEE.` : "", acts);
  if (!MK.configured)
    return `${head()}${emptyBox("MILKEE ist noch nicht verbunden", isAdmin() ? "Verbinde MILKEE unter Einstellungen → Verbindungen." : "Ein Admin muss MILKEE unter Einstellungen → Verbindungen verbinden.", isAdmin() ? `<button class="btn pri sm" data-a="go-connections">${ic("link", 14)} Zu den Verbindungen</button>` : "")}`;
  const r = rangeOf(MKUI.finRange);
  if (INS.key !== `${r.from}|${r.to}` || INS.state === "idle") void loadInsights();
  if (FIN.state === "idle") void loadFin();
  const acts = `<button class="btn sm" data-a="fin-refresh">${ic("refresh", 14)} Aktualisieren</button><a class="btn sm" href="https://app.milkee.ch" target="_blank" rel="noopener">${ic("ext", 14)} MILKEE öffnen</a>`;
  const bar = `<div class="filters fin-bar">${rangeSeg(["year", "quarter", "12m", "lastyear", "3y"], MKUI.finRange, "fin-range")}<span class="small dim">${fmtDate(`${r.from}T12:00`)} – ${fmtDate(`${r.to}T12:00`)} · verglichen mit dem Vorjahr</span></div>`;
  const d = INS.data && INS.key === `${r.from}|${r.to}` ? INS.data : null;
  if (!d)
    return `${head(acts)}${bar}${INS.state === "error" ? emptyBox("MILKEE antwortet nicht", esc(INS.err ?? ""), `<button class="btn sm" data-a="fin-refresh">${ic("refresh", 14)} Nochmals versuchen</button>`) : `<div class="loading" style="padding:60px 0">Auswertung wird aus MILKEE berechnet …</div>`}`;
  const t = d.totals;
  const bills = toBill();
  const billSum = bills.reduce((a, b) => a + b.items.length * b.price, 0);
  const stamp = new Date(d.fetchedAt);
  const ps = d.proposals;
  const stats = `<div class="stats four">
    <div class="stat acc"><span>Umsatz (fakturiert)</span><b>${chf0(t.invoiced)}</b><small>${delta(t.invoiced, d.prev.invoiced)} ${t.invoiceCount} ${t.invoiceCount === 1 ? "Rechnung" : "Rechnungen"} · Ø ${chf0(d.avgInvoice)}</small></div>
    <div class="stat ${t.profit >= 0 ? "ok" : "bad"}"><span>Gewinn</span><b>${chf0(t.profit)}</b><small>${delta(t.profit, d.prev.profit)} ${d.margin != null ? `Marge ${pct(d.margin)}` : "noch keine Einnahmen"}</small></div>
    <button class="stat ${d.overdueCount ? "bad" : ""}" data-a="go" data-to="rechnungen"><span>Offene Forderungen</span><b>${chf0(d.openTotal)}</b><small>${d.overdueCount ? `<span class="amt-out">${d.overdueCount} überfällig · ${chf0(d.overdueTotal)}</span>` : "nichts überfällig"}</small></button>
    <button class="stat" data-a="go" data-to="offerten"><span>Offerten offen</span><b>${chf0(ps.sent.sum)}</b><small>${ps.sent.count} versendet${ps.rate != null ? ` · Quote ${pct(ps.rate)}` : ""}</small></button>
  </div>`;
  const cash = `<div class="fin-cash"><span><i class="dot" style="--c:var(--ok)"></i>Einnahmen <b class="tnum">${chf0(t.income)}</b> ${delta(t.income, d.prev.income)}</span><span><i class="dot" style="--c:var(--bad)"></i>Ausgaben <b class="tnum">${chf0(t.expense)}</b> ${delta(t.expense, d.prev.expense)}</span></div>`;
  const custs = d.customers.slice(0, 8);
  const custRows = custs.length
    ? custs
        .map((c) => {
          const own = clientOfMk(c.id);
          const label = own ? `<a class="lnk" href="#kunde-${esc(own.id)}" data-a="go" data-to="kunde-${esc(own.id)}" data-ctab="finance">${cdot(own)} ${esc(own.name)}</a>` : esc(c.name);
          return shareRow(label, `${pct(c.share)} · ${c.invoices} ${c.invoices === 1 ? "Rechnung" : "Rechnungen"}${c.open ? ` · <span class="amt-out">${chf0(c.open)} offen</span>` : " · bezahlt"}`, c.invoiced, custs[0]!.invoiced ? c.invoiced / custs[0]!.invoiced : 0, own?.color ?? "var(--accent)");
        })
        .join("")
    : emptyBox("Keine Rechnungen", "Im gewählten Zeitraum gibt es in MILKEE keine Rechnungen.");
  const more = d.customers.length > 8 ? `<p class="small dim" style="margin:10px 0 0">und ${d.customers.length - 8} weitere</p>` : "";
  const cats = d.expenseCats.length
    ? d.expenseCats.map((c) => shareRow(esc(c.name), pct(c.share), c.sum, d.expenseCats[0]!.sum ? c.sum / d.expenseCats[0]!.sum : 0, "var(--bad)")).join("")
    : emptyBox("Keine Ausgaben", "Im gewählten Zeitraum ist nichts gebucht.");
  const funnel = [
    ["Entwürfe", ps.draft, "var(--dim)"],
    ["Versendet", ps.sent, "var(--accent)"],
    ["Angenommen", ps.accepted, "var(--ok)"],
    ["Abgelehnt", ps.declined, "var(--bad)"],
  ] as const;
  const fmax = Math.max(1, ...funnel.map(([, v]) => v.sum));
  const open = FIN.data?.open ?? [];
  const status = (o: MilkeeOverview["open"][number]) => (o.status === "open" ? '<span class="chip">Entwurf</span>' : o.overdue ? '<span class="chip bad">überfällig</span>' : /^reminder/.test(o.status) ? '<span class="chip warn">gemahnt</span>' : '<span class="chip acc">versendet</span>');
  return `${head(acts)}${bar}${stats}
  <div class="grid-2">
    <section class="panel"><div class="panel-head"><h2 class="h2">Verlauf</h2>${cash}</div>${monthChart(d.months, { cash: true })}</section>
    <section class="panel"><div class="panel-head"><h2 class="h2">Zu verrechnen</h2>${billSum ? `<span class="small dim">${chf0(billSum)}</span>` : ""}</div>
      ${bills.length ? bills.map(({ c, items, price }) => `<button class="row" data-a="go" data-to="kunde-${esc(c.id)}" data-ctab="table">${cdot(c)}<div class="grow"><div class="rt">${esc(c.name)}</div><div class="rs">${items.length} ${items.length === 1 ? "Video" : "Videos"} · ${chf0(price)} pro Video</div></div><b class="tnum">${chf0(items.length * price)}</b>${ic("chev", 16)}</button>`).join("") : emptyBox("Alles verrechnet", S.clients.some((c) => billingOf(c)) ? "Keine fertigen Videos offen." : "Richte bei einem Kunden in der Live-Tabelle unter ⋯ → «Abrechnung» den Preis pro Video ein.")}</section>
  </div>
  <div class="grid-half">
    <section class="panel"><div class="panel-head"><h2 class="h2">Umsatz nach Kunde</h2><span class="small dim">${chf0(t.invoiced)}</span></div><div class="srows">${custRows}</div>${more}</section>
    <section class="panel"><div class="panel-head"><h2 class="h2">Kosten nach Kategorie</h2><span class="small dim">${chf0(t.expense)}</span></div><div class="srows">${cats}</div></section>
  </div>
  <div class="grid-half">
    <section class="panel"><div class="panel-head"><h2 class="h2">Offerten</h2><button class="btn ghost sm" data-a="go" data-to="offerten">Alle ${ic("chev", 14)}</button></div>
      <div class="srows">${funnel.map(([l, v, col]) => shareRow(l, `${v.count} ${v.count === 1 ? "Offerte" : "Offerten"}`, v.sum, v.sum / fmax, col)).join("")}</div>
      <p class="small dim" style="margin:12px 0 0">${ps.rate != null ? `Abschlussquote <b style="color:var(--fg)">${pct(ps.rate)}</b>: von ${ps.accepted.count + ps.declined.count} entschiedenen Offerten wurden ${ps.accepted.count} angenommen.` : "Noch keine Offerte angenommen oder abgelehnt."}</p></section>
    <section class="panel flush"><div class="panel-head"><h2 class="h2">Offene Rechnungen</h2><button class="btn ghost sm" data-a="go" data-to="rechnungen">Alle ${ic("chev", 14)}</button></div>${
      open.length
        ? `<div class="tbl-wrap"><table class="tbl"><tbody>${open
            .slice(0, 6)
            .map((o) => `<tr><td><div class="rt ellip">${esc(o.customer || "–")}</div><div class="rs ellip">${esc(invNr(o.number) || "–")}${o.due ? ` · zahlbar bis ${fmtDate(o.due)}` : ""}</div></td><td>${status(o)}</td><td class="r tnum" style="font-weight:600">${chf(o.total)}</td></tr>`)
            .join("")}</tbody></table></div>`
        : `<div style="padding:0 24px 24px">${FIN.data ? emptyBox("Alles bezahlt", "In MILKEE sind keine Rechnungen offen.") : mkLoading("Wird geladen …")}</div>`
    }</section>
  </div>
  <p class="help">Stand ${pad2(stamp.getHours())}:${pad2(stamp.getMinutes())} Uhr. Fakturiert zählt Rechnungen nach Rechnungsdatum (ohne Entwürfe und Stornos), Einnahmen und Ausgaben sind die Buchungen in MILKEE. Die Zahlen werden 5 Minuten zwischengespeichert.</p>`;
}

/* ---------- Offene Stunden eines Kunden ---------- */

const OPENW: Record<string, { at: number; data: ReturnType<typeof openWork> | null; err?: boolean }> = {};
async function loadOpenWork(c: Client, mk: number) {
  OPENW[c.id] = { at: Date.now(), data: OPENW[c.id]?.data ?? null };
  try {
    OPENW[c.id] = { at: Date.now(), data: openWork(await api.invoiceSources(mk)) };
  } catch (e) {
    if (onAuthLost(e)) return;
    OPENW[c.id] = { at: Date.now(), data: null, err: true };
  }
  // nur die Karte ersetzen: render() würde ein angefangenes To-do verwerfen
  if (UI.view === "kunde" && UI.cid === c.id) {
    const el = document.getElementById("open-work");
    if (el) el.outerHTML = openWorkCard(c);
  }
}
function openWorkCard(c: Client) {
  const mk = MK.configured ? mkCustomerOf(c) : null;
  if (!mk) return "";
  const st = OPENW[c.id];
  if (!st || Date.now() - st.at > 60_000) void loadOpenWork(c, mk);
  const d = st?.data;
  // bei der Automatik ist ein Posten ein Video zum Pauschalpreis, keine gearbeitete Stunde
  const posten = !!billingOf(c)?.auto;
  const none = st?.err ? "–" : "…";
  const note = st?.err ? "MILKEE antwortet nicht" : "";
  const n = d?.count ?? 0;
  const left = posten ? (d ? String(n) : none) : d ? `${hm(Math.floor(d.minutes / 60), d.minutes % 60)} h` : none;
  return `<div class="stats two" id="open-work"><div class="stat"><span>${posten ? "Offene Posten" : "Offene Stunden"}</span><b>${left}</b><small>${d ? (n ? `${n} ${posten ? "in MILKEE" : n === 1 ? "Eintrag" : "Einträge"}` : "Nichts offen") : note}</small></div><div class="stat acc"><span>Noch nicht verrechnet</span><b>${d ? chf0(d.money) : none}</b><small>${d ? (d.expenses ? `inkl. ${chf0(d.expenses)} Auslagen` : "Zeiten aus MILKEE") : note}</small></div></div>`;
}

/* ---------- Finanzen eines Kunden ---------- */

const CFIN: Record<string, { key: string; data: MkCustomerFinance | null; err?: string }> = {};
async function loadClientFinance(c: Client, refresh = false) {
  const mk = mkCustomerOf(c);
  if (!mk) return;
  const r = rangeOf(MKUI.cfinRange);
  const key = `${mk}|${r.from}|${r.to}`;
  CFIN[c.id] = { key, data: CFIN[c.id]?.key === key ? CFIN[c.id]!.data : null };
  try {
    const data = await api.mkCustomerFinance(mk, r.from, r.to, refresh);
    if (CFIN[c.id]?.key === key) CFIN[c.id] = { key, data };
  } catch (e) {
    if (onAuthLost(e)) return;
    CFIN[c.id] = { key, data: null, err: (e as Error).message };
  }
  if (UI.view === "kunde" && UI.cid === c.id) render();
}
function clientFinance(c: Client) {
  if (!MK.configured) return emptyBox("MILKEE ist noch nicht verbunden", "Verbinde MILKEE unter Einstellungen → Verbindungen.", "");
  const mk = mkCustomerOf(c);
  if (!mk) return emptyBox("Noch nicht mit MILKEE verknüpft", "Verknüpfe den Kunden mit seinem Kunden in MILKEE, dann siehst du hier Umsatz, Rechnungen und Offerten.", `<button class="btn pri sm" data-a="mk-link">${ic("link", 14)} Mit MILKEE verknüpfen</button>`);
  const r = rangeOf(MKUI.cfinRange);
  const key = `${mk}|${r.from}|${r.to}`;
  const st = CFIN[c.id];
  if (!st || st.key !== key) void loadClientFinance(c);
  const bar = `<div class="filters fin-bar">${rangeSeg(["12m", "year", "lastyear", "3y"], MKUI.cfinRange, "cfin-range")}<button class="btn ghost sm" data-a="cfin-refresh" style="margin-left:auto">${ic("refresh", 14)} Aktualisieren</button></div>`;
  const f = st?.key === key ? st.data : null;
  if (!f) return `${bar}${st?.err ? emptyBox("MILKEE antwortet nicht", esc(st.err), "") : mkLoading("Wird aus MILKEE geladen …")}`;
  const p = f.proposals;
  const docs = f.documents.filter((x) => x.date && x.date >= r.from && x.date <= r.to);
  const rows = docs
    .map((x) => {
      const [cls, label] = DOC_ST[x.status] ?? ["", x.status];
      const chip = x.kind === "invoice" && x.overdue && x.status !== "paid" ? '<span class="chip bad">überfällig</span>' : `<span class="chip ${cls}">${label}</span>`;
      return `<tr><td class="tnum"><b style="font-weight:500">${esc(invNr(x.number) || "–")}</b><div class="rs">${x.date ? fmtDate(`${x.date}T12:00`) : ""}</div></td><td><span class="chip ${x.kind === "invoice" ? "" : "acc"}">${x.kind === "invoice" ? "Rechnung" : "Offerte"}</span></td><td><div class="rt ellip" style="max-width:280px">${esc(x.title || "–")}</div></td><td>${chip}</td><td class="r tnum" style="font-weight:600">${chf(x.total)}</td><td class="r"><a class="ibtn" href="/api/milkee/documents/${x.kind}/${x.id}/pdf" target="_blank" rel="noopener" aria-label="PDF öffnen" title="PDF öffnen">${ic("download", 15)}</a></td></tr>`;
    })
    .join("");
  return `${bar}<div class="stats four">
    <div class="stat acc"><span>Umsatz ${esc(r.label)}</span><b>${chf0(f.invoiced)}</b><small>${f.invoiceCount} ${f.invoiceCount === 1 ? "Rechnung" : "Rechnungen"}${f.invoiceCount ? ` · Ø ${chf0(f.avgInvoice)}` : ""}</small></div>
    <div class="stat ok"><span>Davon bezahlt</span><b>${chf0(f.paid)}</b><small>${f.invoiced ? pct(f.paid / f.invoiced) : "–"}</small></div>
    <div class="stat ${f.overdue ? "bad" : ""}"><span>Offen</span><b>${chf0(f.open)}</b><small>${f.overdue ? `<span class="amt-out">${chf0(f.overdue)} überfällig</span>` : "nichts überfällig"}</small></div>
    <div class="stat"><span>Umsatz gesamt</span><b>${chf0(f.allTime)}</b><small>${f.firstInvoice ? `Kunde seit ${fmtDate(`${f.firstInvoice}T12:00`)}` : "noch keine Rechnung"}</small></div>
  </div>
  <div class="grid-2">
    <section class="panel"><div class="panel-head"><h2 class="h2">Verlauf</h2></div>${monthChart(f.months, { cash: false })}</section>
    <section class="panel"><div class="panel-head"><h2 class="h2">Offerten</h2>${p.rate != null ? `<span class="small dim">Quote ${pct(p.rate)}</span>` : ""}</div>
      <div class="srows">${(
        [
          ["Angenommen", p.accepted, "var(--ok)"],
          ["Versendet, offen", p.sent, "var(--accent)"],
          ["Entwürfe", p.draft, "var(--dim)"],
          ["Abgelehnt", p.declined, "var(--bad)"],
        ] as const
      )
        .map(([l, v, col]) => shareRow(l, `${v.count} ${v.count === 1 ? "Offerte" : "Offerten"}`, v.sum, v.sum / Math.max(1, p.accepted.sum, p.sent.sum, p.draft.sum, p.declined.sum), col))
        .join("")}</div><p class="small dim" style="margin:10px 0 0">Über alle Jahre.</p></section>
  </div>
  <section class="panel flush"><div class="panel-head"><h2 class="h2">Rechnungen und Offerten</h2><span class="small dim">${esc(r.label)}</span></div>${
    docs.length
      ? `<div class="tbl-wrap"><table class="tbl"><thead><tr><th>Nummer</th><th>Art</th><th>Titel</th><th>Status</th><th class="r">Betrag</th><th></th></tr></thead><tbody>${rows}</tbody></table></div>`
      : `<div style="padding:0 24px 24px">${emptyBox("Nichts in diesem Zeitraum", "Wähle oben einen längeren Zeitraum.")}</div>`
  }</section>`;
}
const pad2 = (n: number) => String(n).padStart(2, "0");

/* ---------- MILKEE-Spiegel: Hilfen ---------- */

const todayIso = () => toInput(nowIso());
const mkGate = (title: string, lede: string) =>
  MK.configured
    ? ""
    : `${pageHead("MILKEE", title, lede)}${emptyBox("MILKEE ist noch nicht verbunden", isAdmin() ? "Verbinde MILKEE unter Einstellungen → Verbindungen." : "Ein Admin muss MILKEE unter Einstellungen → Verbindungen verbinden.", isAdmin() ? `<button class="btn pri sm" data-a="go-connections">${ic("link", 14)} Zu den Verbindungen</button>` : "")}`;
const mkLoading = (txt: string) => `<div class="loading" style="padding:50px 0">${txt}</div>`;
function mkFail(e: unknown) {
  if (onAuthLost(e)) return;
  MKD.err = (e as Error).message;
  toast((e as Error).message);
}
/** MILKEE-Kunde eines eigenen Kunden (Verknüpfung oder Abrechnung). */
function mkCustomerOf(c: Client | undefined): number | null {
  const m = c?.milkee as { customerId?: number } | undefined;
  return Number(m?.customerId) || billingOf(c)?.milkeeCustomerId || null;
}
function syncPhase(p: Project) {
  const id = Number(p.milkeeProjectId);
  if (!MK.configured || !id) return;
  api.mkUpdateProject(id, { kanbanStatus: p.phase, ...(p.due ? { endDate: toInput(p.due) } : {}) }).catch((e: unknown) => {
    if (!onAuthLost(e)) toast(`MILKEE nicht aktualisiert: ${(e as Error).message}`);
  });
}
async function loadMkProjects() {
  if (MKD.projects === null) return;
  MKD.projects = null;
  try {
    MKD.projects = (await api.mkProjects()).projects ?? [];
  } catch (e) {
    MKD.projects = [];
    mkFail(e);
  }
  render();
}
const mkProjectsReady = () => {
  if (MKD.projects === undefined) void loadMkProjects();
  return MKD.projects ?? null;
};
function mkProjectOptions(sel: number) {
  const ps = mkProjectsReady() ?? [];
  const by = new Map<string, MkProject[]>();
  for (const p of ps) by.set(p.customerName || "Ohne Kunde", [...(by.get(p.customerName || "Ohne Kunde") ?? []), p]);
  return [...by.entries()].map(([cn, list]) => `<optgroup label="${esc(cn)}">${list.map((p) => `<option value="${p.id}" ${p.id === sel ? "selected" : ""}>${esc(p.name)}</option>`).join("")}</optgroup>`).join("");
}
function elapsed(startedAt: string) {
  const t = new Date(startedAt.replace(" ", "T")).getTime();
  const s = Math.max(0, Math.floor((Date.now() - (Number.isNaN(t) ? Date.now() : t)) / 1000));
  return `${Math.floor(s / 3600)}:${pad2(Math.floor(s / 60) % 60)}:${pad2(s % 60)}`;
}
setInterval(() => document.querySelectorAll<HTMLElement>("[data-elapsed]").forEach((el) => (el.textContent = elapsed(el.dataset.elapsed ?? ""))), 1000);
const hm = (h: number, m: number) => `${h}:${pad2(m)}`;
const parseDur = (s: string): [number, number] | null => {
  s = s.trim().replace(",", ".");
  if (!s) return null;
  if (s.includes(":")) {
    const [h, m] = s.split(":");
    const hh = Number(h) || 0;
    const mm = Number(m) || 0;
    return mm < 60 && hh < 24 ? [hh, mm] : null;
  }
  const n = Number(s);
  if (!(n > 0)) return null;
  const total = n <= 12 ? Math.round(n * 60) : Math.round(n);
  return total < 24 * 60 ? [Math.floor(total / 60), total % 60] : null;
};

/* ---------- Automatischer Abgleich mit MILKEE ---------- */

const norm = (x: string) =>
  x
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
let AUTOLINK = false;
/**
 * Verbindet Kunden und Projekte selbständig mit MILKEE, damit nichts doppelt vorkommt:
 * gleicher Kundenname → verknüpfen, gleicher Projektname → verknüpfen,
 * MILKEE-Projekt ohne Gegenstück → hier anlegen.
 */
async function autoLinkMilkee(force = false) {
  if (!MK.configured || (AUTOLINK && !force)) return;
  AUTOLINK = true;
  try {
    const [cs, ps] = await Promise.all([MK_CUSTOMERS ? Promise.resolve(MK_CUSTOMERS) : api.milkeeCustomers().then((r) => r.customers), api.mkProjects().then((r) => r.projects ?? [])]);
    MK_CUSTOMERS = cs;
    MKD.projects = ps;
    let changed = false;
    for (const c of S.clients) {
      if (mkCustomerOf(c)) continue;
      const m = cs.filter((x) => norm(x.name) === norm(c.name));
      if (m.length === 1) {
        c.milkee = { customerId: m[0]!.id, customerName: m[0]!.name };
        changed = true;
      }
    }
    const linked = new Set(S.projects.map((p) => Number(p.milkeeProjectId)).filter(Boolean));
    for (const mp of ps) {
      if (linked.has(mp.id)) continue;
      const client = S.clients.find((c) => mkCustomerOf(c) === mp.customerId);
      if (client && billingOf(client)?.auto) continue; // die Automatik auf dem Server legt diese Projekte selbst an
      const local = S.projects.find((p) => !p.milkeeProjectId && norm(p.title) === norm(mp.name) && (!client || p.client === client.id));
      if (local) local.milkeeProjectId = mp.id;
      else if (client)
        S.projects.push({ id: uid(), client: client.id, title: mp.name, phase: PHASES.includes(mp.kanbanStatus ?? "") ? (mp.kanbanStatus as string) : "Laufend", due: mp.endDate ? fromInput(mp.endDate) : null, milkeeProjectId: mp.id });
      else continue;
      linked.add(mp.id);
      changed = true;
    }
    if (changed) {
      save();
      render();
    }
  } catch (e) {
    AUTOLINK = false;
    onAuthLost(e);
  }
}
function linkModal() {
  const c = C(UI.cid);
  if (!c) return;
  if (MK_CUSTOMERS === null) {
    openModal(`${mhead("Mit MILKEE verknüpfen")}${mkLoading("Kunden werden aus MILKEE geladen …")}`);
    api.milkeeCustomers().then(
      (r) => {
        MK_CUSTOMERS = r.customers;
        linkModal();
      },
      (e: unknown) => {
        closeModal();
        mkFail(e);
      },
    );
    return;
  }
  const cur = mkCustomerOf(c) ?? MK_CUSTOMERS.find((m) => m.name.toLowerCase().includes((c.name.toLowerCase().split(" ")[0] ?? "") || "§"))?.id;
  openModal(`${mhead(`${esc(c.name)} mit MILKEE verknüpfen`)}<p class="muted" style="margin:0">Damit Projekte, Zeiten und Rechnungen dieses Kunden in MILKEE landen.</p><form data-form="mk-link" class="stack" style="gap:14px"><div class="field"><label for="ml-cust">Kunde in MILKEE</label><select class="in" id="ml-cust">${MK_CUSTOMERS.map((m) => `<option value="${m.id}" ${m.id === cur ? "selected" : ""}>${esc(m.name)}</option>`).join("")}</select></div>${mfoot("Verknüpfen")}</form>`);
}

/* ---------- Zeiterfassung (MILKEE) ---------- */

async function loadTimes() {
  if (MKD.times === null) return;
  MKD.times = null;
  const to = todayIso();
  const from = toInput(new Date(Date.now() - MKUI.timeRange * 864e5).toISOString());
  try {
    const [r, t] = await Promise.all([api.mkTimes(from, to, MKUI.timeProject || undefined), api.mkTimer()]);
    MKD.times = { list: r.times ?? [], range: MKUI.timeRange, projectId: MKUI.timeProject };
    MKD.timer = t;
  } catch (e) {
    MKD.times = { list: [], range: MKUI.timeRange, projectId: MKUI.timeProject };
    mkFail(e);
  }
  render();
}
function vZeit() {
  const gate = mkGate("Zeiterfassung", "Zeiten laufen über MILKEE.");
  if (gate) return gate;
  const projects = mkProjectsReady();
  if (MKD.times === undefined || (MKD.times && (MKD.times.range !== MKUI.timeRange || MKD.times.projectId !== MKUI.timeProject))) void loadTimes();
  const head = pageHead("Arbeit", "Zeiterfassung", "Direkt in MILKEE gespeichert. Verrechnet wird in MILKEE.", `<a class="btn sm" href="https://app.milkee.ch" target="_blank" rel="noopener">${ic("ext", 14)} MILKEE öffnen</a>`);
  if (!projects) return `${head}${mkLoading("Projekte werden aus MILKEE geladen …")}`;
  if (!projects.length) return `${head}${emptyBox("Noch keine Projekte in MILKEE", "Lege ein Projekt an (Projekte → Projekt erstellen, mit «Auch in MILKEE anlegen»), dann kannst du Zeiten darauf erfassen.")}`;
  const tm = MKD.timer;
  const timerProj = tm?.active ? projects.find((p) => p.id === tm.projectId) : undefined;
  const timer = tm?.active
    ? `<section class="panel timer-card on"><span class="pulse"></span><div class="grow"><div class="lbl">Timer läuft</div><b class="tnum" data-elapsed="${esc(tm.startedAt ?? "")}">${elapsed(tm.startedAt ?? "")}</b><div class="rs">${esc(timerProj ? `${timerProj.name} · ${timerProj.customerName}` : "Projekt in MILKEE")}</div></div><form data-form="mk-stop" class="add-row" style="flex:1 1 260px"><input class="in" id="ts-desc" placeholder="Was hast du gemacht?" autocomplete="off" style="flex:1"><button class="btn stop" type="submit">${ic("stop", 14)} Stoppen</button></form></section>`
    : `<section class="panel timer-card"><span class="lic">${ic("clock", 18)}</span><div class="grow"><div class="lbl">Timer</div><div class="rs">Startet in MILKEE, läuft auch weiter, wenn du die Seite schliesst.</div></div><form data-form="mk-start" class="add-row" style="flex:1 1 300px"><select class="in" id="ts-proj" style="flex:1">${mkProjectOptions(MKUI.timeProject)}</select><button class="btn pri" type="submit">${ic("play", 12)} Starten</button></form></section>`;
  const t = MKD.times;
  const list = t?.list ?? [];
  const mins = (x: MkTime) => x.hours * 60 + x.minutes;
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const wk = list.filter((x) => x.date >= toInput(weekStart.toISOString()));
  const mo = list.filter((x) => x.date.slice(0, 7) === todayIso().slice(0, 7));
  const openVal = list.filter((x) => x.status === "open" && x.billable).reduce((a, x) => a + x.total, 0);
  const sumH = (l: MkTime[]) => {
    const m = l.reduce((a, x) => a + mins(x), 0);
    return hm(Math.floor(m / 60), m % 60);
  };
  const days = new Map<string, MkTime[]>();
  for (const x of list) days.set(x.date, [...(days.get(x.date) ?? []), x]);
  const st = (s: string) => (s === "paid" ? '<span class="chip ok">bezahlt</span>' : s === "invoiced" ? '<span class="chip acc">verrechnet</span>' : '<span class="chip">offen</span>');
  const rows = [...days.entries()]
    .map(([d, l]) => {
      const n = daysFrom(`${d}T12:00`);
      return `<div class="day-head"><span>${n === 0 ? "Heute" : n === -1 ? "Gestern" : `${WDL[new Date(`${d}T12:00`).getDay()]}, ${fmtDate(`${d}T12:00`)}`}</span><span class="tnum">${sumH(l)}</span></div>${l
        .map(
          (x) =>
            `<div class="row"><div class="grow"><div class="rt ellip">${esc(x.description || "Ohne Beschreibung")}</div><div class="rs ellip">${x.start && x.end ? `${esc(x.start)}–${esc(x.end)} · ` : ""}${esc(x.projectName)}${x.customerName ? ` · ${esc(x.customerName)}` : ""}${x.billable ? "" : " · nicht verrechenbar"}</div></div>${st(x.status)}<span class="tnum dim hide-m small" style="width:84px;text-align:right">${x.total ? chf(x.total) : ""}</span><span class="tnum" style="width:46px;text-align:right">${hm(x.hours, x.minutes)}</span>${x.status === "open" ? `<button class="ibtn" data-a="mk-time-del" data-id="${x.id}" aria-label="Eintrag löschen">${ic("trash", 15)}</button>` : `<span class="ibtn" aria-hidden="true">${ic("lock", 14)}</span>`}</div>`,
        )
        .join("")}`;
    })
    .join("");
  return `${head}
  ${timer}
  <div class="stats"><div class="stat"><span>Diese Woche</span><b>${sumH(wk)} h</b></div><div class="stat"><span>${MONL[now.getMonth()]}</span><b>${sumH(mo)} h</b></div><div class="stat acc"><span>Offen zu verrechnen</span><b>${chf0(openVal)}</b><small>in MILKEE verrechnen</small></div></div>
  <section class="panel"><div class="panel-head"><h2 class="h2">Zeit erfassen</h2></div>
    <form class="time-form" data-form="mk-time"><div class="field"><label for="tm-desc">Was hast du gemacht?</label><input class="in" id="tm-desc" placeholder="z. B. Schnitt Challenge_Valoarena" autocomplete="off"></div>
    <div class="field"><label for="tm-proj">Projekt</label><select class="in" id="tm-proj">${mkProjectOptions(MKUI.timeProject)}</select></div>
    <div class="field"><label for="tm-date">Datum</label><input class="in" type="date" id="tm-date" value="${todayIso()}"></div>
    <div class="field"><label for="tm-from">Von</label><input class="in tnum" type="time" id="tm-from" step="300" aria-label="Gearbeitet von"></div>
    <div class="field"><label for="tm-to">Bis</label><input class="in tnum" type="time" id="tm-to" step="300" aria-label="Gearbeitet bis"></div>
    <div class="field"><label for="tm-dur">Dauer</label><input class="in tnum" id="tm-dur" placeholder="1:30" autocomplete="off" title="Selbst eingeben oder Von und Bis ausfüllen"></div>
    <button class="btn pri" type="submit">${ic("plus", 14)} Erfassen</button></form>
    <label class="toggle" style="margin-top:12px"><input type="checkbox" id="tm-bill" checked><span><b>Verrechenbar</b></span></label></section>
  <section class="panel"><div class="filters" style="margin-bottom:6px"><div class="seg">${[[7, "7 Tage"], [31, "30 Tage"], [92, "3 Monate"]].map(([k, l]) => `<button class="${MKUI.timeRange === k ? "on" : ""}" data-a="mk-range" data-k="${k}">${l}</button>`).join("")}</div><select class="in sm" data-a="mk-tproj" aria-label="Projekt filtern"><option value="0">Alle Projekte</option>${mkProjectOptions(MKUI.timeProject)}</select></div>
    ${t ? (list.length ? rows : emptyBox("Keine Zeiten in diesem Zeitraum", "Erfasse oben eine Zeit oder starte den Timer.")) : mkLoading("Zeiten werden aus MILKEE geladen …")}</section>`;
}

/** Von–Bis im Formular «Zeit erfassen»: Dauer sofort ausrechnen und anzeigen */
document.addEventListener("input", (e) => {
  const id = (e.target as HTMLElement).id;
  if (id !== "tm-from" && id !== "tm-to") return;
  const span = spanMinutes(val("tm-from"), val("tm-to"));
  const dur = byId<HTMLInputElement>("tm-dur");
  if (dur && span !== null) dur.value = `${Math.floor(span / 60)}:${String(span % 60).padStart(2, "0")}`;
});

/* ---------- Rechnungen und Offerten (MILKEE) ---------- */

async function loadDocs() {
  if (MKD.docs === null) return;
  MKD.docs = null;
  const { docKind: kind, docStatus: status, docYear: year } = MKUI;
  try {
    MKD.docs = { kind, status, year, list: (await api.mkDocs(kind, status, year || undefined)).documents ?? [] };
  } catch (e) {
    MKD.docs = { kind, status, year, list: [] };
    mkFail(e);
  }
  render();
}
const DOC_ST: Record<string, [string, string]> = {
  open: ["", "Entwurf"],
  sent: ["acc", "versendet"],
  paid: ["ok", "bezahlt"],
  reminder_one: ["warn", "1. Mahnung"],
  reminder_two: ["warn", "2. Mahnung"],
  reminder_three: ["bad", "3. Mahnung"],
  accepted: ["ok", "angenommen"],
  declined: ["bad", "abgelehnt"],
  confirmation: ["ok", "bestätigt"],
};
function vDocs(kind: "invoice" | "proposal") {
  const gate = mkGate(kind === "invoice" ? "Rechnungen" : "Offerten", kind === "invoice" ? "Rechnungen aus MILKEE." : "Offerten aus MILKEE.");
  if (gate) return gate;
  if (MKUI.docKind !== kind) {
    MKUI.docKind = kind;
    MKUI.docStatus = kind === "invoice" ? "unpaid" : "sent";
  }
  const d = MKD.docs;
  if (d === undefined || (d && (d.kind !== MKUI.docKind || d.status !== MKUI.docStatus || d.year !== MKUI.docYear))) void loadDocs();
  const inv = MKUI.docKind === "invoice";
  const statuses: [string, string][] = inv ? [["unpaid", "Offen"], ["open", "Entwürfe"], ["paid", "Bezahlt"], ["all", "Alle"]] : [["open", "Entwürfe"], ["sent", "Versendet"], ["accepted", "Angenommen"], ["all", "Alle"]];
  const y = new Date().getFullYear();
  const list = d?.list ?? [];
  const total = list.reduce((a, x) => a + x.total, 0);
  const rows = list
    .map((x) => {
      const [cls, label] = DOC_ST[x.status] ?? ["", x.status];
      const late = inv && x.status !== "paid" && x.status !== "open" && (x.overdue || (!!x.due && x.due < todayIso()));
      const chip = late ? '<span class="chip bad">überfällig</span>' : `<span class="chip ${cls}">${label}</span>`;
      const acts = inv
        ? [x.status === "open" ? ["sent", "Als versendet markieren", "send"] : null, x.status !== "paid" && x.status !== "open" ? ["paid", "Als bezahlt markieren", "check"] : null].filter(Boolean)
        : [x.status === "open" ? ["sent", "Als versendet markieren", "send"] : null, x.status === "sent" ? ["accepted", "Als angenommen markieren", "check"] : null, x.status === "sent" ? ["declined", "Als abgelehnt markieren", "x"] : null].filter(Boolean);
      const menu = (acts as string[][]).length
        ? `<details class="menu"><summary class="ibtn" aria-label="Status ändern">${ic("more", 16)}</summary><div class="menu-list">${(acts as string[][]).map(([s, l, i]) => `<button data-a="mk-mark" data-id="${x.id}" data-k="${s}">${ic(i!, 16)} ${l}</button>`).join("")}</div></details>`
        : "";
      return `<tr><td class="tnum">${inv ? `<a class="inv-link" href="#rechnung-${x.id}">${esc(invNr(x.number) || "Entwurf")}</a>` : `<b style="font-weight:500">${esc(invNr(x.number) || "–")}</b>`}<div class="rs">${x.date ? fmtDate(`${x.date}T12:00`) : ""}</div></td><td><div class="rt ellip" style="max-width:240px">${custLink(x.customerId, x.customer)}</div><div class="rs ellip" style="max-width:240px">${esc(x.title)}</div></td><td class="hide-m tnum muted">${x.due ? fmtDate(`${x.due}T12:00`) : "–"}</td><td>${chip}</td><td class="r tnum" style="font-weight:600">${chf(x.total)}</td><td class="r nowrap"><a class="ibtn" href="/api/milkee/documents/${x.kind}/${x.id}/pdf" target="_blank" rel="noopener" aria-label="PDF öffnen" title="PDF öffnen">${ic("download", 15)}</a>${menu}</td></tr>`;
    })
    .join("");
  const ps = INS.data?.proposals;
  const pipe =
    !inv && ps
      ? `<div class="stats four">${[
          ["Entwürfe", ps.draft, ""],
          ["Versendet, offen", ps.sent, "acc"],
          ["Angenommen", ps.accepted, "ok"],
          ["Abgelehnt", ps.declined, "bad"],
        ]
          .map(([l, v, cls]) => `<div class="stat ${cls}"><span>${l}</span><b>${chf0((v as { sum: number }).sum)}</b><small>${(v as { count: number }).count} ${(v as { count: number }).count === 1 ? "Offerte" : "Offerten"} · ${esc(rangeOf(MKUI.finRange).label)}</small></div>`)
          .join("")}</div>`
      : "";
  if (!inv && !INS.data && INS.state === "idle") void loadInsights();
  return `${pageHead("Buchhaltung", inv ? "Rechnungen" : "Offerten", inv ? "Aus MILKEE. Neue Rechnungen schreibst du direkt hier, sie landen sofort in MILKEE. Nummer anklicken zum Öffnen." : `Aus MILKEE.${ps?.rate != null ? ` Abschlussquote ${Math.round(ps.rate * 100)} % (${esc(rangeOf(MKUI.finRange).label)}).` : ""}`, `<a class="btn sm" href="https://app.milkee.ch" target="_blank" rel="noopener">${ic("ext", 14)} MILKEE öffnen</a>` + (inv ? `<button class="btn pri sm" data-a="inv-new">${ic("plus", 14)} Neue Rechnung</button>` : ""))}
  ${pipe}
  <div class="filters"><div class="seg">${statuses.map(([k, l]) => `<button class="${MKUI.docStatus === k ? "on" : ""}" data-a="mk-dstatus" data-k="${k}">${l}</button>`).join("")}</div><select class="in sm" data-a="mk-dyear" aria-label="Jahr"><option value="0">Alle Jahre</option>${[y, y - 1, y - 2].map((v) => `<option value="${v}" ${MKUI.docYear === v ? "selected" : ""}>${v}</option>`).join("")}</select></div>
  <section class="panel flush">${d ? (list.length ? `<div class="tbl-wrap"><table class="tbl"><thead><tr><th>Nummer</th><th>Kunde</th><th class="hide-m">${inv ? "Zahlbar bis" : "Gültig bis"}</th><th>Status</th><th class="r">Betrag</th><th></th></tr></thead><tbody>${rows}</tbody></table></div><div class="tbl-foot">${list.length} ${inv ? (list.length === 1 ? "Rechnung" : "Rechnungen") : list.length === 1 ? "Offerte" : "Offerten"} · Summe <b class="tnum">${chf(total)}</b></div>` : `<div style="padding:24px">${emptyBox("Nichts in dieser Ansicht", "Wechsle den Filter oder das Jahr.")}</div>`) : mkLoading("Wird aus MILKEE geladen …")}</section>`;
}
function paidModal(id: number) {
  openModal(`${mhead("Zahlung erfassen")}<p class="muted" style="margin:0">Die Rechnung wird in MILKEE als bezahlt markiert und auf dein Haupt-Bankkonto gebucht.</p><form data-form="mk-paid" data-id="${id}" class="stack" style="gap:14px">${fld("pd-date", "Bezahlt am", todayIso(), { type: "date" })}${mfoot("Als bezahlt markieren")}</form>`);
}

/* ---------- Buchungen und Belege (MILKEE) ---------- */

async function loadEntries() {
  if (MKD.entries === null) return;
  MKD.entries = null;
  const { entryYear: year, entryType: type } = MKUI;
  try {
    MKD.entries = { year, type, list: (await api.mkEntries(year, type)).entries ?? [] };
  } catch (e) {
    MKD.entries = { year, type, list: [] };
    mkFail(e);
  }
  render();
}
async function loadAccounts() {
  if (MKD.accounts === null) return;
  MKD.accounts = null;
  try {
    MKD.accounts = (await api.mkAccounts()).accounts ?? [];
  } catch (e) {
    MKD.accounts = [];
    mkFail(e);
  }
}
function vBuchungen() {
  const gate = mkGate("Buchungen", "Buchungen und Belege laufen über MILKEE.");
  if (gate) return gate;
  const d = MKD.entries;
  if (d === undefined || (d && (d.year !== MKUI.entryYear || d.type !== MKUI.entryType))) void loadEntries();
  const y = new Date().getFullYear();
  const list = d?.list ?? [];
  const inc = list.filter((e) => e.type === "income").reduce((a, e) => a + e.sum, 0);
  const out = list.filter((e) => e.type === "expense").reduce((a, e) => a + e.sum, 0);
  const rows = list
    .map(
      (e) =>
        `<tr><td class="tnum">${fmtDate(`${e.date}T12:00`)}<div class="rs">${esc(e.number)}</div></td><td><div class="rt ellip" style="max-width:320px">${esc(e.description || "ohne Text")}</div><div class="rs ellip" style="max-width:320px">${esc(e.debit)} ← ${esc(e.credit)}${e.customer ? ` · ${esc(e.customer)}` : ""}</div></td><td class="hide-m">${e.hasFile ? `<span class="chip acc" title="Beleg in MILKEE">${ic("clip", 12)} Beleg</span>` : '<span class="dim small">–</span>'}</td><td class="r tnum ${e.type === "income" ? "amt-in" : e.type === "expense" ? "amt-out" : ""}" style="font-weight:600">${e.type === "income" ? "+" : e.type === "expense" ? "−" : ""} ${chf(e.sum)}</td></tr>`,
    )
    .join("");
  return `${pageHead("Buchhaltung", "Buchungen", "Aus MILKEE. Belege lädst du hier hoch, sie landen direkt in MILKEE.", `<button class="btn" data-a="entry-new" data-k="income">${ic("plus", 14)} Einnahme</button><button class="btn pri" data-a="entry-new" data-k="expense">${ic("clip", 14)} Ausgabe mit Beleg</button>`)}
  <div class="filters"><div class="seg">${[["", "Alle"], ["income", "Einnahmen"], ["expense", "Ausgaben"]].map(([k, l]) => `<button class="${MKUI.entryType === k ? "on" : ""}" data-a="mk-etype" data-k="${k}">${l}</button>`).join("")}</div><select class="in sm" data-a="mk-eyear" aria-label="Jahr">${[y, y - 1, y - 2].map((v) => `<option value="${v}" ${MKUI.entryYear === v ? "selected" : ""}>${v}</option>`).join("")}</select></div>
  ${d ? `<div class="stats"><div class="stat ok"><span>Einnahmen</span><b>${chf0(inc)}</b></div><div class="stat bad"><span>Ausgaben</span><b>${chf0(out)}</b></div><div class="stat acc"><span>Saldo</span><b>${chf0(inc - out)}</b></div></div>` : ""}
  <section class="panel flush">${d ? (list.length ? `<div class="tbl-wrap"><table class="tbl"><thead><tr><th>Datum</th><th>Text</th><th class="hide-m">Beleg</th><th class="r">Betrag</th></tr></thead><tbody>${rows}</tbody></table></div>` : `<div style="padding:24px">${emptyBox("Keine Buchungen", "In diesem Jahr gibt es in MILKEE keine passenden Buchungen.")}</div>`) : mkLoading("Buchungen werden aus MILKEE geladen …")}</section>`;
}
async function entryModal(kind: "expense" | "income") {
  if (!MKD.accounts) {
    openModal(`${mhead(kind === "expense" ? "Ausgabe erfassen" : "Einnahme erfassen")}${mkLoading("Konten werden aus MILKEE geladen …")}`);
    await loadAccounts();
    if (MK_CUSTOMERS === null) MK_CUSTOMERS = (await api.milkeeCustomers().catch(() => ({ customers: [] }))).customers;
    // nach dem Laden neu lesen (TypeScript kennt die Änderung durch loadAccounts nicht)
    if (!(MKD.accounts as MkAccount[] | null | undefined)?.length) return closeModal();
  }
  const acc = MKD.accounts ?? [];
  const banks = acc.filter((a) => a.type === "bank");
  const own = acc.filter((a) => a.type === kind);
  const opt = (l: MkAccount[], sel?: number) => l.map((a) => `<option value="${a.id}" ${a.id === sel ? "selected" : ""}>${a.number} ${esc(a.name)}</option>`).join("");
  openModal(`${mhead(kind === "expense" ? "Ausgabe erfassen" : "Einnahme erfassen")}<form data-form="mk-entry" data-k="${kind}" class="stack" style="gap:14px"><div class="form-grid">
    ${fld("en-desc", "Wofür?", "", { full: true, ph: kind === "expense" ? "z. B. Adobe Creative Cloud Oktober" : "z. B. Barzahlung Dreh" })}
    ${fld("en-sum", "Betrag CHF", "", { type: "number", step: "0.01", ph: "0.00" })}
    ${fld("en-date", "Datum", todayIso(), { type: "date" })}
    <div class="field"><label for="en-acc">${kind === "expense" ? "Aufwandskonto" : "Ertragskonto"}</label><select class="in" id="en-acc">${opt(own)}</select></div>
    <div class="field"><label for="en-bank">${kind === "expense" ? "Bezahlt über" : "Eingegangen auf"}</label><select class="in" id="en-bank">${opt(banks, banks.find((b) => b.primary)?.id)}</select></div>
    <div class="field full"><label for="en-cust">Kunde (optional)</label><select class="in" id="en-cust"><option value="">Kein Kunde</option>${(MK_CUSTOMERS ?? []).map((c) => `<option value="${c.id}">${esc(c.name)}</option>`).join("")}</select></div>
    <div class="field full"><label for="en-file">Beleg (PDF oder Foto, max. 6 MB)</label><input class="in" type="file" id="en-file" accept="application/pdf,image/png,image/jpeg,image/heic,image/webp" style="padding:9px 12px"></div>
  </div><p class="help" style="margin:0">Die Buchung und der Beleg gehen direkt an MILKEE. Hier wird nichts davon gespeichert.</p>${mfoot("In MILKEE buchen")}</form>`);
}

/* ---------- Automatik auslösen ---------- */

async function runAutomation(cid: string, loud: boolean) {
  if (!MK.configured) return;
  if (loud) toast("Abgleich mit MILKEE läuft …");
  try {
    const r = (await api.mkSync(cid)).results[0];
    if (r) {
      const parts = [
        r.created.length ? `${r.created.length} ${r.created.length === 1 ? "Projekt" : "Projekte"} angelegt` : "",
        r.closed.length ? `${r.closed.length} zu verrechnen` : "",
        r.billed?.length ? `${r.billed.length} verrechnet` : "",
      ].filter(Boolean);
      if (parts.length || loud) toast(parts.length ? `MILKEE: ${parts.join(", ")}` : "Alles aktuell");
      if (r.errors.length) toast(`Nicht alles ging: ${r.errors[0]}`);
    }
    delete BILLED[cid];
    MKD.projects = undefined;
    await refreshState();
    void loadBilled(cid);
    if (r?.closed.length || r?.billed?.length) void loadFin(true);
  } catch (e) {
    mkFail(e);
  }
}

/* ---------- Abrechnung pro Video (Live-Tabelle) ---------- */

async function loadBilled(cid: string) {
  if (BILLED[cid] === null) return;
  BILLED[cid] = null;
  try {
    BILLED[cid] = (await api.billed(cid)).billed;
  } catch (e) {
    if (onAuthLost(e)) return;
    BILLED[cid] = [];
  }
  if ((UI.view === "kunde" && UI.cid === cid) || UI.view === "finanzen") render();
}
interface BillCtx {
  cid: string;
  cfg: ClientBilling | null;
  billed: BilledRow[];
  unbilled: LiveItem[];
}
function billingCtx(cid: string, L: Live): BillCtx | null {
  if (!MK.configured) return null;
  const cfg = billingOf(C(cid));
  if (BILLED[cid] === undefined) void loadBilled(cid);
  const billed = BILLED[cid] ?? [];
  const unbilled = BILLED[cid] ? L.items.filter((it) => isDone(it) && !billed.some((b) => b.itemKey === itemKey(it))) : [];
  return { cid, cfg, billed, unbilled };
}
function billBanner(b: BillCtx) {
  if (b.cfg && (b.cfg as ClientBilling).auto)
    return `<div class="vbill">${ic("refresh", 18)}<div class="grow"><b>Automatik aktiv</b><span>Jedes Video, das David zugewiesen ist, ist ein Projekt in MILKEE. Bei «Final» entsteht dort ein offener Posten über ${chf0(b.cfg.pricePerVideo)}, den MILKEE beim nächsten Rechnungschreiben für ${esc(b.cfg.milkeeCustomerName)} vorschlägt. Ist die Rechnung versendet, kommt das Projekt nach «Verrechnet».</span></div>${earlyOpen(b).length ? `<button class="btn sm pri" data-a="bill-early-all">${ic("receipt", 14)} ${earlyOpen(b).length} ${earlyOpen(b).length === 1 ? "abgegebenes" : "abgegebene"} verrechnen</button>` : ""}<button class="btn sm" data-a="mk-sync">${ic("refresh", 14)} Jetzt abgleichen</button></div>`;
  if (!b.cfg) return L_doneCount(b) ? `<div class="vbill muted">${ic("receipt", 18)}<div class="grow"><b>Fertige Videos verrechnen</b><span>Lege einmal fest, welcher MILKEE-Kunde das ist und was ein Video kostet.</span></div><button class="btn sm" data-a="billing-setup">Abrechnung einrichten</button></div>` : "";
  if (!b.unbilled.length) return "";
  const sum = b.unbilled.length * b.cfg.pricePerVideo;
  return `<div class="vbill">${ic("receipt", 18)}<div class="grow"><b>${b.unbilled.length} ${b.unbilled.length === 1 ? "fertiges Video" : "fertige Videos"} noch nicht verrechnet</b><span>${chf0(sum)} · kommt in den Rechnungsentwurf des Monats für ${esc(b.cfg.milkeeCustomerName)} in MILKEE</span></div><button class="btn sm pri" data-a="bill-all">${ic("check", 14)} In MILKEE übernehmen</button></div>`;
}
const L_doneCount = (b: BillCtx) => (LIVE[b.cid]?.items ?? []).filter(isDone).length;
function billCell(it: LiveItem, b: BillCtx) {
  if (!b.cfg) return "";
  if (b.cfg.auto) {
    const p = S.projects.find((x) => x.client === b.cid && x.sheetKey === itemKey(it));
    if (!p) return '<span class="chip">ohne Projekt</span>';
    return p.phase === "Verrechnet" ? `<span class="chip ok">${ic("check", 12)} verrechnet</span>` : p.phase === "Zu verrechnen" ? `<span class="chip warn">zu verrechnen · ${chf0(b.cfg.pricePerVideo)}</span>` : "";
  }
  const row = b.billed.find((x) => x.itemKey === itemKey(it));
  if (row)
    return `<span class="billed">${row.invoiceId ? `<span class="chip ok" title="In MILKEE-Rechnung ${esc(row.invoiceNumber)}">${ic("check", 12)} ${row.invoiceNumber ? esc(invNr(row.invoiceNumber)) : "verrechnet"}</span>` : '<span class="chip">früher verrechnet</span>'}<button class="ibtn sm" data-a="unbill" data-id="${esc(row.id)}" aria-label="Verrechnung zurücknehmen" title="Zurücknehmen">${ic("x", 13)}</button></span>`;
  return `<button class="btn sm" data-a="bill-one" data-key="${esc(itemKey(it))}">${chf0(b.cfg.pricePerVideo)} verrechnen</button>`;
}
/** Abgegebene Videos, die noch nicht verrechnet sind (Automatik) */
const earlyOpen = (b: BillCtx) => (LIVE[b.cid]?.items ?? []).filter((it) => delivered(it) && !billedBeforeFinal(b.cid, it));
/** Knopf bei noch nicht finalen Videos: jetzt verrechnen, der Stand in der Tabelle bleibt */
function earlyCell(it: LiveItem, b: BillCtx) {
  if (!b.cfg) return "";
  if (!b.cfg.auto) return delivered(it) ? billCell(it, b) : "";
  const key = esc(itemKey(it));
  const price = chf0(b.cfg.pricePerVideo);
  const phase = projOfItem(b.cid, it)?.phase ?? "";
  if (DONE_PHASES.includes(phase)) return `<span class="chip ok">${ic("check", 12)} verrechnet</span>`;
  if (phase === "Zu verrechnen") {
    if (AUTO[b.cid] === undefined) void loadAuto(b.cid);
    const early = AUTO[b.cid]?.some((x) => x.itemKey === itemKey(it) && x.earlyAt);
    return `<span class="billed"><span class="chip warn">zu verrechnen · ${price}</span>${early ? `<button class="ibtn sm" data-a="bill-early-undo" data-key="${key}" title="Zurücknehmen (der offene Posten in MILKEE wird entfernt)" aria-label="Zurücknehmen">${ic("x", 13)}</button>` : ""}</span>`;
  }
  if (!delivered(it)) return "";
  return `<button class="btn sm early-btn" data-a="bill-early" data-key="${key}" title="Offener Posten in MILKEE, der Stand in der Tabelle bleibt gleich. Wird das Video später final, wird es nicht nochmals verrechnet.">${ic("receipt", 13)} ${price} verrechnen</button>`;
}
async function billEarlyNow(keys: string[]) {
  const cid = UI.cid;
  try {
    const r = await api.billEarly(cid, keys);
    if (r.errors.length) toast(r.errors[0]!);
    else toast(r.closed.length === 1 ? "Verrechnet: offener Posten in MILKEE. Der Stand in der Tabelle bleibt." : `${r.closed.length} Videos verrechnet. Der Stand in der Tabelle bleibt.`);
  } catch (e) {
    if (!onAuthLost(e)) toast((e as Error).message);
  }
  delete AUTO[cid];
  await refreshState();
  void loadFin(true);
  render();
}
function billingModal() {
  const c = C(UI.cid);
  if (!c) return;
  if (MK_CUSTOMERS === null) {
    openModal(`${mhead("Abrechnung")}<div class="loading" style="padding:30px 0">Kunden werden aus MILKEE geladen …</div>`);
    api.milkeeCustomers().then(
      (r) => {
        MK_CUSTOMERS = r.customers;
        billingModal();
      },
      (e: unknown) => {
        closeModal();
        if (!onAuthLost(e)) toast((e as Error).message);
      },
    );
    return;
  }
  const cfg = billingOf(c);
  const guess = cfg?.milkeeCustomerId ?? mkCustomerOf(c) ?? MK_CUSTOMERS.find((m) => m.name.toLowerCase().includes(c.name.toLowerCase().split(" ")[0] ?? ""))?.id;
  const done = (LIVE[c.id]?.items ?? []).filter(isDone).filter((it) => !(BILLED[c.id] ?? []).some((b) => b.itemKey === itemKey(it)));
  openModal(`${mhead(`Abrechnung für ${esc(c.name)}`)}<p class="muted" style="margin:0">Fertige Videos (Stand «Final») kommen als Position in einen Rechnungsentwurf des Monats in MILKEE. Prüfen und versenden machst du in MILKEE.</p>
  <form data-form="billing" class="stack" style="gap:14px"><div class="form-grid">
    <div class="field full"><label for="bl-cust">Kunde in MILKEE</label><select class="in" id="bl-cust">${MK_CUSTOMERS.map((m) => `<option value="${m.id}" ${m.id === guess ? "selected" : ""}>${esc(m.name)}</option>`).join("")}</select></div>
    ${fld("bl-price", "Preis pro Video (CHF)", String(cfg?.pricePerVideo ?? 80), { type: "number", step: "0.05" })}
  </div>
  <label class="toggle"><input type="checkbox" id="bl-auto" ${cfg ? (cfg.auto ? "checked" : "") : "checked"}><span><b>Automatik</b><small>Jedes Video, das David zugewiesen ist, wird ein Projekt in MILKEE und hier (z. B. «Arbopark_Challenge_Valoarena»). Steht es auf «Final», entsteht im MILKEE-Projekt ein offener Posten über den Preis pro Video. MILKEE schlägt ihn beim Rechnungschreiben vor. Nach dem Versenden kommt das Projekt nach «Verrechnet». Läuft alle 5 Minuten.</small></span></label>
  ${!cfg && done.length ? `<label class="toggle"><input type="checkbox" id="bl-skip" checked><span><b>${done.length} ${done.length === 1 ? "schon fertiges Video" : "schon fertige Videos"} als bereits verrechnet markieren</b><small>Empfohlen, wenn du diese schon in MILKEE abgerechnet hast. Sonst werden sie zum Verrechnen vorgeschlagen.</small></span></label>` : ""}
  ${mfoot("Speichern")}</form>`);
}
async function billItems(items: LiveItem[]) {
  const c = C(UI.cid);
  const cfg = billingOf(c);
  if (!c || !cfg || !items.length) return;
  const sum = items.length * cfg.pricePerVideo;
  confirmModal(
    `${items.length} ${items.length === 1 ? "Video" : "Videos"} (${chf0(sum)}) ${items.length === 1 ? "kommt" : "kommen"} in MILKEE in den Rechnungsentwurf des Monats für <b>${esc(cfg.milkeeCustomerName)}</b>. Prüfen und versenden machst du danach in MILKEE.`,
    "Übernehmen",
    async () => {
      try {
        const r = await api.bill(
          c.id,
          items.map((it) => ({ key: itemKey(it), title: it.video, section: it.section })),
        );
        toast(r.invoice ? `In MILKEE übernommen: Rechnung ${invNr(r.invoice.number) || "Entwurf"} ${r.created ? "neu erstellt" : "ergänzt"}, total ${chfPlain(r.invoice.total)}` : "Schon verrechnet");
        FIN = { ...FIN, state: FIN.data ? "ok" : "idle" };
        void loadFin(true);
      } catch (e) {
        if (!onAuthLost(e)) toast((e as Error).message);
      }
      delete BILLED[c.id];
      void loadBilled(c.id);
    },
  );
}

/* ---------- API-Zugang (nur Admins) ---------- */

async function loadTokens() {
  try {
    TOKENS = (await api.tokens()).tokens;
  } catch (e) {
    if (!onAuthLost(e)) toast((e as Error).message);
    TOKENS = [];
  }
  if (UI.view === "einstellungen" && UI.set === "api") render();
}
function tokensPanel() {
  if (TOKENS === null) {
    void loadTokens();
    return `<section class="panel"><div class="loading" style="padding:40px 0">API-Schlüssel werden geladen …</div></section>`;
  }
  const rows = TOKENS.map((t) => {
    const expired = t.expiresAt && new Date(t.expiresAt).getTime() < Date.now();
    return `<tr><td><div class="rt ellip">${esc(t.name)}</div><div class="rs ellip tnum">${esc(t.prefix)}… · von ${esc(t.createdBy)}</div></td><td>${t.scope === "write" ? '<span class="chip warn">lesen + schreiben</span>' : '<span class="chip">nur lesen</span>'}</td><td class="hide-m tnum muted">${t.lastUsedAt ? fmtDate(t.lastUsedAt) : "nie"}</td><td class="hide-m tnum ${expired ? "amt-out" : "muted"}">${t.expiresAt ? (expired ? "abgelaufen" : fmtDate(t.expiresAt)) : "unbegrenzt"}</td><td class="r"><button class="ibtn" data-a="token-del" data-id="${esc(t.id)}" aria-label="Schlüssel ${esc(t.name)} löschen">${ic("trash", 15)}</button></td></tr>`;
  }).join("");
  return `<section class="panel flush">${panelHead("API-Zugang", "Schlüssel für Programme, z. B. für Claude. Ein Schlüssel kann Kunden, Projekte, Notizen und Tabellen lesen und, wenn erlaubt, ändern. Benutzer, Schlüssel und Anmeldung erreicht er nie.", `<button class="btn pri sm" data-a="token-new">${ic("plus", 14)} Schlüssel</button>`)}
  ${TOKENS.length ? `<div class="tbl-wrap"><table class="tbl"><thead><tr><th>Name</th><th>Rechte</th><th class="hide-m">Zuletzt benutzt</th><th class="hide-m">Gültig bis</th><th></th></tr></thead><tbody>${rows}</tbody></table></div>` : `<div style="padding:0 24px 24px">${emptyBox("Noch keine API-Schlüssel", "Erstelle einen Schlüssel, damit ein Programm auf Social Solve Management zugreifen kann.")}</div>`}</section>
  <section class="panel">${panelHead("So wird ein Schlüssel benutzt", "")}<p class="muted" style="margin:0 0 10px">Im Header jeder Anfrage mitschicken:</p><code class="secret" style="user-select:all">Authorization: Bearer zt_…</code><p class="help" style="margin:12px 0 0">Lesen: <code>GET /api/state</code>. Schreiben: <code>PUT /api/state/clients</code> (ebenso projects, notes, tables, company) mit <code>{"value": …}</code>. Lösch einen Schlüssel sofort, wenn du ihn nicht mehr brauchst oder er in falsche Hände geraten sein könnte.</p></section>`;
}
function tokenModal() {
  openModal(
    `${mhead("API-Schlüssel erstellen")}<form data-form="token" class="stack" style="gap:14px"><div class="form-grid">${fld("tk-name", "Name", "", { full: true, ph: "z. B. Claude" })}${fld("tk-scope", "Rechte", "read", { options: [["read", "Nur lesen"], ["write", "Lesen und schreiben"]] })}${fld("tk-days", "Gültig", "90", { options: [["30", "30 Tage"], ["90", "90 Tage"], ["365", "1 Jahr"], ["0", "Unbegrenzt"]] })}</div><p class="help" style="margin:0">Gib nur so viele Rechte wie nötig. Der Schlüssel wird nach dem Erstellen genau einmal angezeigt.</p>${mfoot("Erstellen")}</form>`,
  );
}
function secretModal(name: string, secret: string) {
  openModal(
    `${mhead("API-Schlüssel")}<p class="muted" style="margin:0">Der Schlüssel «${esc(name)}» ist erstellt. Kopiere ihn jetzt, er wird nicht mehr angezeigt. Nicht per Mail oder Chat verschicken.</p><code class="secret" style="font-size:13px;word-break:break-all;user-select:all">${esc(secret)}</code><div class="modal-foot"><button class="btn" data-a="copy" data-v="${esc(secret)}">${ic("copy", 16)} Kopieren</button><button class="btn pri" data-a="close">Fertig</button></div>`,
  );
}

/* ============ Darstellung & Navigation ============ */

const VIEWS: Record<View, () => string> = { uebersicht: vUebersicht, kunden: vKunden, kunde: vKunde, projekte: vProjekte, kalender: vKalender, skripte: vSkripte, skript: () => vSkript(UI.sid), abgaben: vAbgaben, abgabe: () => vAbgabe(UI.did), zeit: vZeit, finanzen: vFinanzen, rechnungen: () => vDocs("invoice"), offerten: () => vDocs("proposal"), buchungen: vBuchungen, jahresabschluss: vJahresabschluss, rechnung: () => vRechnung(UI.rid), einstellungen: vEinstellungen };
function setRoute(h: string) {
  leaveEditor(h);
  if (h.startsWith("rechnung-")) {
    UI.view = "rechnung";
    UI.rid = h.slice(9);
    return;
  }
  if (h.startsWith("abgabe-")) {
    UI.view = "abgabe";
    UI.did = h.slice(7);
    return;
  }
  if (h.startsWith("skript-")) {
    UI.view = "skript";
    UI.sid = h.slice(7);
    return;
  }
  if (h.startsWith("kunde-")) {
    forgetClientScripts();
    if (UI.cid !== h.slice(6)) UI.ctab = "overview";
    UI.view = "kunde";
    UI.cid = h.slice(6);
  } else UI.view = h in VIEWS ? (h as View) : "uebersicht";
}
function route() {
  setRoute((location.hash || "#uebersicht").slice(1));
  render(true);
}
function render(scrollTop = false) {
  if (!LOADED) return;
  // Im Skript-Editor nicht unter den Fingern neu zeichnen (z. B. beim Abgleich im Hintergrund)
  if (!scrollTop && UI.view === "skript" && editorBusy()) return renderShell();
  const fid = (document.activeElement as HTMLElement | null)?.id;
  must("view").innerHTML = VIEWS[UI.view]();
  must("view").classList.toggle("wide", UI.view === "skript");
  renderShell();
  if (UI.view === "skript") afterPaint();
  if (UI.view === "rechnung") afterRenderInvoice();
  if (UI.view === "projekte" && UI.projView === "board") bindBoard();
  if (scrollTop) window.scrollTo(0, 0);
  else if (fid) {
    const el = byId<HTMLInputElement>(fid);
    if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA") && el.type !== "file") {
      el.focus();
      try {
        el.setSelectionRange(el.value.length, el.value.length);
      } catch {
        /* nicht jedes Feld kennt eine Auswahl */
      }
    }
  }
}
function go(to: string) {
  setRoute(to);
  try {
    history.replaceState(null, "", `#${to}`);
  } catch {
    /* z. B. in eingebetteten Vorschauen */
  }
  render(true);
}
window.addEventListener("hashchange", route);

/* ============ Overlays ============ */

function toast(msg: string) {
  const t = document.createElement("div");
  t.className = "toast";
  t.textContent = msg;
  must("toasts").appendChild(t);
  setTimeout(() => t.remove(), 3600);
}
function openModal(html: string, cls = "") {
  must("overlay").innerHTML = `<div class="scrim" data-a="scrim"><div class="modal ${cls}" role="dialog" aria-modal="true">${html}</div></div>`;
  document.querySelector<HTMLElement>("#overlay input:not([type=checkbox]):not([type=radio]):not([type=file]),#overlay select")?.focus();
}
function closeModal() {
  must("overlay").innerHTML = "";
  PAL.open = false;
}
const mhead = (t: string) => `<div class="modal-head"><h2 class="h2">${t}</h2><button class="ibtn" data-a="close" aria-label="Schliessen">${ic("x")}</button></div>`;
const mfoot = (label: string) => `<div class="modal-foot"><button type="button" class="btn ghost" data-a="close">Abbrechen</button><button class="btn pri" type="submit">${label}</button></div>`;
function confirmModal(text: string, label: string, fn: () => void | Promise<void>) {
  UI.confirm = fn;
  openModal(`${mhead("Bist du sicher?")}<p class="muted" style="margin:0">${text}</p><div class="modal-foot"><button class="btn ghost" data-a="close">Abbrechen</button><button class="btn stop" data-a="confirm-yes">${label}</button></div>`);
}
function clientModal(cid?: string) {
  const c = cid ? C(cid) : undefined;
  UI.clientEditId = cid ?? null;
  openModal(`${mhead(c ? "Kunde bearbeiten" : "Kunde erstellen")}<form data-form="client" class="stack" style="gap:14px"><div class="form-grid">
    ${fld("cf-name", "Name / Firma", c?.name ?? "", { full: true })}${fld("cf-contact", "Ansprechperson", c?.contact ?? "")}${fld("cf-email", "E-Mail", c?.email ?? "")}${fld("cf-street", "Strasse & Nr.", c?.street ?? "", { full: true })}${fld("cf-zip", "PLZ", c?.zip ?? "")}${fld("cf-city", "Ort", c?.city ?? "")}${fld("cf-phone", "Telefon", c?.phone ?? "")}
    <div class="field full"><span class="flabel">Farbe</span><div class="swatches">${COLORS.map((col, i) => `<label><input type="radio" name="cf-color" value="${col}" ${(c ? c.color === col : i === 0) ? "checked" : ""}><span style="--c:${col}"></span></label>`).join("")}</div></div></div>${mfoot(c ? "Speichern" : "Kunde erstellen")}</form>`);
}
function tabsModal() {
  const c = C(UI.cid);
  if (!c) return;
  openModal(`${mhead(`Bereiche für ${esc(c.name)}`)}<p class="muted" style="margin:0">Jeder Kunde zeigt nur, was du bei ihm brauchst.</p><form data-form="tabs" class="stack" style="gap:8px">${Object.entries(TABS).map(([k, m]) => `<label class="opt"><input type="checkbox" name="tb" value="${k}" ${c.tabs.includes(k) ? "checked" : ""}><span class="lic">${ic(m.icon, 16)}</span><span><b>${m.name}</b><small>${m.desc}</small></span></label>`).join("")}${mfoot("Übernehmen")}</form>`);
}
function projectModal(cid?: string) {
  if (!S.clients.length) return toast("Erstelle zuerst einen Kunden.");
  openModal(`${mhead("Projekt erstellen")}<form data-form="project" class="stack" style="gap:14px"><div class="form-grid">${fld("pm-title", "Titel", "", { full: true })}<div class="field"><label for="pm-client">Kunde</label><select class="in" id="pm-client">${clientOptions(cid || UI.projClient || "")}</select></div>${fld("pm-due", "Deadline (optional)", "", { type: "date" })}<div class="field"><label for="pm-phase">Phase</label><select class="in" id="pm-phase">${PHASES.map((p) => `<option ${p === "Laufend" ? "selected" : ""}>${p}</option>`).join("")}</select></div></div>${MK.configured ? `<label class="toggle"><input type="checkbox" id="pm-mk" checked><span><b>Auch in MILKEE anlegen</b><small>Zeiten und Rechnungen laufen dann über dieses Projekt.</small></span></label>` : ""}${mfoot("Erstellen")}</form>`);
}
function uploadModal() {
  const t = S.tables[UI.cid];
  openModal(`${mhead("Upload-Link")}<p class="muted" style="margin:0">Der Ordner, in den du fertige Videos hochlädst. Er erscheint als Knopf «Hochladen» in der Live-Tabelle.</p><form data-form="upload-link" class="stack" style="gap:14px">${fld("up-url", "Link zum Upload-Ordner", t?.uploadUrl ?? "", { ph: "https://drive.google.com/drive/folders/…" })}${mfoot("Speichern")}</form>`);
}
function quickAddModal() {
  openModal(`${mhead("Was möchtest du erstellen?")}<div class="stack" style="gap:8px">${[["todo-new", "check", "To-do"], ["client-new", "users", "Kunde"], ["project-new", "board", "Projekt"], ["script-new", "film", "Skript"], ["delivery-new", "send", "Abgabe"], ["preview-new", "eye", "Vorschau (v1, Sample)"], ["photos-new", "image", "Fotos für den Kunden"], ...(MK.configured ? [["go-time", "clock", "Zeit erfassen"], ["inv-new", "receipt", "Rechnung"], ["entry-new", "scale", "Ausgabe mit Beleg"]] : [])].map(([a, i, n]) => `<button class="opt" data-a="${a}"><span class="lic">${ic(i!, 16)}</span><span><b>${n}</b></span></button>`).join("")}</div>`);
}
function moreModal() {
  openModal(`${mhead("Alle Bereiche")}<div class="stack" style="gap:6px">${NAVFLAT.map(([k, n, i]) => `<button class="opt" data-a="go" data-to="${k}"><span class="lic">${ic(i, 16)}</span><span><b>${n}</b></span></button>`).join("")}</div>`);
}

/* ============ Suche (⌘K) ============ */

interface PalItem {
  g: string;
  label: string;
  icon: string;
  k: string;
  run: () => void;
  dot?: string;
  hidden?: boolean;
}
const PAL = { open: false, q: "", sel: 0, items: [] as PalItem[] };
function palItems(q: string) {
  q = q.trim().toLowerCase();
  const out: PalItem[] = [];
  NAVFLAT.forEach(([k, l, i]) => out.push({ g: "Seiten", label: l, icon: i, k: "", run: () => go(k) }));
  S.clients.forEach((c) => out.push({ g: "Kunden", label: c.name, icon: "users", k: c.city || "", run: () => go(`kunde-${c.id}`), dot: c.color }));
  S.projects.forEach((p) =>
    out.push({
      g: "Projekte",
      label: p.title,
      icon: "board",
      k: C(p.client)?.name ?? "",
      run: () => {
        UI.ctab = "projects";
        go(`kunde-${p.client}`);
      },
      hidden: true,
    }),
  );
  return (q ? out.filter((o) => `${o.label} ${o.k}`.toLowerCase().includes(q)) : out.filter((o) => !o.hidden)).slice(0, 30);
}
function openPalette() {
  PAL.open = true;
  PAL.q = "";
  PAL.sel = 0;
  openModal(`<div class="pin">${ic("search", 20)}<input id="pal-in" placeholder="Kunde, Projekt oder Seite" autocomplete="off" aria-label="Suchen"></div><div class="plist" id="pal-list"></div>`, "palette");
  drawPalette();
}
function drawPalette() {
  PAL.items = palItems(PAL.q);
  PAL.sel = Math.min(PAL.sel, Math.max(0, PAL.items.length - 1));
  let g = "";
  let html = "";
  PAL.items.forEach((it, i) => {
    if (it.g !== g) {
      g = it.g;
      html += `<div class="pgroup">${g}</div>`;
    }
    html += `<button class="pitem ${i === PAL.sel ? "on" : ""}" data-a="pal-run" data-i="${i}"><span class="lic">${it.dot ? `<i class="dot" style="--c:${esc(it.dot)}"></i>` : ic(it.icon, 15)}</span><span class="ellip">${esc(it.label)}</span><span class="k ellip">${esc(it.k)}</span></button>`;
  });
  const l = byId("pal-list");
  if (l) {
    l.innerHTML = html || emptyBox("Nichts gefunden", "");
    l.querySelector(".pitem.on")?.scrollIntoView({ block: "nearest" });
  }
}
function runPal(i: number) {
  const it = PAL.items[i];
  if (!it) return;
  closeModal();
  it.run();
}

/* ============ Board ============ */

function bindBoard() {
  const b = byId("board");
  if (!b) return;
  b.querySelectorAll<HTMLElement>(".pcard").forEach((card) => {
    card.addEventListener("dragstart", (e) => {
      card.classList.add("dragging");
      e.dataTransfer?.setData("text/plain", card.dataset.id ?? "");
    });
    card.addEventListener("dragend", () => card.classList.remove("dragging"));
  });
  b.querySelectorAll<HTMLElement>(".col").forEach((col) => {
    col.addEventListener("dragover", (e) => {
      e.preventDefault();
      col.classList.add("over");
    });
    col.addEventListener("dragleave", (e) => {
      if (!col.contains(e.relatedTarget as Node | null)) col.classList.remove("over");
    });
    col.addEventListener("drop", (e) => {
      e.preventDefault();
      const p = P(e.dataTransfer?.getData("text/plain") ?? "");
      const phase = col.dataset.phase ?? "";
      if (p && p.phase !== phase) {
        p.phase = phase;
        save();
        syncPhase(p);
      }
      render();
    });
  });
}

/* ============ Sicherung ============ */

function exportData() {
  const data = Object.fromEntries(STATE_KEYS.map((k) => [k, S[k]]));
  const blob = new Blob([JSON.stringify(data, null, 1)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `socialsolve-management-sicherung-${toInput(nowIso())}.json`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(a.href);
    a.remove();
  }, 1000);
}
function importData(file: File) {
  const r = new FileReader();
  r.onload = () => {
    let o: Partial<State> & { zentrale?: Partial<State> };
    try {
      o = JSON.parse(String(r.result)) as typeof o;
    } catch {
      return toast("Die Datei ist keine gültige Sicherung.");
    }
    if (!o.clients && o.zentrale) o = o.zentrale;
    const keys = STATE_KEYS.filter((k) => o[k] !== undefined);
    if (!keys.length) return toast("In der Datei wurden keine Daten von Social Solve Management gefunden.");
    // Nur Projekte, Notizen und Tabellen übernehmen, deren Kunde in der Sicherung existiert
    const ids = new Set((o.clients ?? S.clients).map((c) => c.id));
    if (o.projects) o.projects = o.projects.filter((p) => ids.has(p.client));
    if (o.notes) o.notes = Object.fromEntries(Object.entries(o.notes).filter(([cid]) => ids.has(cid)));
    if (o.tables) o.tables = Object.fromEntries(Object.entries(o.tables).filter(([cid]) => ids.has(cid)));
    confirmModal(`Die Sicherung enthält ${(o.clients ?? []).length} Kunden und ${(o.projects ?? []).length} Projekte. Die aktuellen Daten werden dabei ersetzt.`, "Importieren", () => {
      keys.forEach((k) => applyRemote(k, o[k] as never));
      save();
      startWatches();
      render(true);
      toast("Sicherung importiert");
    });
  };
  r.readAsText(file);
}

/* ============ Ereignisse ============ */

document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  const a = target.closest<HTMLElement>("[data-a]");
  if (!a) {
    const l = target.closest<HTMLAnchorElement>('a[href^="#"]');
    // Knöpfe in einer verlinkten Karte (z. B. «Archivieren») öffnen die Karte nicht
    const btn = target.closest("button");
    if (l && !(btn && l.contains(btn))) {
      e.preventDefault();
      go((l.getAttribute("href") ?? "#").slice(1));
    }
    document.querySelectorAll("details.menu[open]").forEach((m) => {
      if (!m.contains(target)) m.removeAttribute("open");
    });
    return;
  }
  const act = a.dataset.a ?? "";
  if (act === "scrim") {
    if (target === a) closeModal();
    return;
  }
  if (["phase", "projclient", "live-set", "sec-link", "import-file", "google-file", "mk-tproj", "mk-dyear", "mk-eyear"].includes(act)) return;
  e.preventDefault();
  e.stopPropagation();
  a.closest("details.menu")?.removeAttribute("open");
  const id = a.dataset.id ?? "";
  const k = a.dataset.k ?? "";
  switch (act) {
    case "go":
      closeModal();
      go(a.dataset.to ?? "uebersicht");
      if (a.dataset.ctab) {
        UI.ctab = a.dataset.ctab;
        render(true);
      }
      break;
    case "live-mode":
      UI.liveMode = k === "raw" ? "raw" : "plan";
      render();
      break;
    case "upload-edit":
      uploadModal();
      break;
    case "live-tab":
      UI.liveTabIdx = Number(k) || 0;
      render();
      break;
    case "live-refresh": {
      const L = LIVE[UI.cid];
      if (L && POLL[UI.cid]) {
        if (!L.items.length) L.state = "loading";
        render();
        void fetchSheet(UI.cid);
        void fetchFeedback(UI.cid, true);
      }
      if (DRIVE[UI.cid] !== null) {
        delete DRIVE[UI.cid];
        void loadDrive(UI.cid, true);
      }
      toast("Tabelle wird neu geladen");
      break;
    }
    case "close":
      closeModal();
      break;
    case "more":
      moreModal();
      break;
    case "palette":
      openPalette();
      break;
    case "pal-run":
      runPal(Number(a.dataset.i));
      break;
    case "quick-add":
      quickAddModal();
      break;
    case "confirm-yes": {
      const f = UI.confirm;
      closeModal();
      UI.confirm = null;
      if (f) void f();
      break;
    }
    case "client-new":
      closeModal();
      clientModal();
      break;
    case "client-edit":
      clientModal(UI.cid);
      break;
    case "client-archive": {
      const c = C(UI.cid);
      if (!c) break;
      c.status = c.status === "archiv" ? "aktiv" : "archiv";
      save();
      render();
      toast(c.status === "archiv" ? "Kunde archiviert" : "Kunde wieder aktiv");
      break;
    }
    case "tabs-edit":
      tabsModal();
      break;
    case "ctab":
      UI.ctab = k;
      render();
      break;
    case "cfilter":
      UI.clientFilter = k;
      render();
      break;
    case "link-del": {
      const c = C(UI.cid);
      if (!c) break;
      c.links.splice(Number(a.dataset.i), 1);
      save();
      render();
      break;
    }
    case "xl-reset":
      confirmModal("Die Verbindung zur Tabelle wird entfernt. Die Tabelle selbst bleibt unverändert.", "Entfernen", () => {
        delete S.tables[UI.cid];
        save();
        startWatches();
        render();
      });
      break;
    case "copy":
      navigator.clipboard.writeText(a.dataset.v ?? "").then(
        () => toast("Kopiert"),
        () => toast("Kopieren ging nicht. Markier den Text von Hand."),
      );
      break;
    case "fb-done": {
      // Punkt für mich abhaken: sofort sichtbar, dann speichern
      const cid = a.dataset.cid ?? "";
      const done = a.dataset.k === "1";
      for (const f of Object.values(FEEDBACK[cid] ?? {})) for (const c of f.comments) if (c.id === id) c.done = done;
      render();
      // im offenen Projekt-Fenster gleich mit nachführen
      const inModal = !!(PD_OPEN && a.closest(".modal"));
      if (inModal) projectDetail(PD_OPEN);
      void api.feedbackDone(cid, id, done).catch(async (e: unknown) => {
        if (!onAuthLost(e)) toast(e instanceof ApiError ? e.message : "Nicht gespeichert");
        // gespeicherten Stand zurückholen, auch im offenen Projekt-Fenster
        await fetchFeedback(cid);
        if (inModal && PD_OPEN) projectDetail(PD_OPEN);
      });
      break;
    }
    case "note-done": {
      const n = (S.notes[a.dataset.cid ?? ""] ?? []).find((x) => x.id === id);
      if (n) {
        n.done = !n.done;
        save();
        render();
      }
      break;
    }
    case "note-del": {
      const cid = a.dataset.cid ?? "";
      S.notes[cid] = (S.notes[cid] ?? []).filter((x) => x.id !== id);
      save();
      render();
      break;
    }
    case "drive-choose":
      driveChooseModal(a.dataset.cid ?? "", k);
      break;
    case "drive-pick": {
      const cid = a.dataset.cid ?? "";
      const fileId = id || null;
      void api.chooseDriveScript(cid, k, fileId).then(
        () => {
          closeModal();
          toast(fileId ? "Drehbuch festgelegt" : "Gemerkt: kein Drehbuch im Ordner");
          delete DRIVE[cid];
          void loadDrive(cid);
        },
        (e: unknown) => {
          if (!onAuthLost(e)) toast((e as Error).message);
        },
      );
      break;
    }
    case "proj-open":
      projectDetail(a.dataset.id ?? "");
      break;
    case "delivery-new":
      openNewDelivery(UI.view === "kunde" ? UI.cid : "");
      break;
    case "photos-new":
      openNewDelivery(UI.view === "kunde" ? UI.cid : "", "photos");
      break;
    case "preview-new":
      openNewDelivery(UI.view === "kunde" ? UI.cid : "", "preview");
      break;
    case "script-new":
      openNewScript(UI.view === "kunde" ? UI.cid : undefined);
      break;
    case "project-new":
      closeModal();
      projectModal(a.dataset.cid);
      break;
    case "projview":
      UI.projView = k === "board" ? "board" : k === "clients" ? "clients" : "list";
      render();
      break;
    case "logout":
      void logout();
      break;
    case "reload":
      location.reload();
      break;
    case "export":
      exportData();
      break;
    case "set-sec":
      UI.set = k;
      if (k === "benutzer") USERS = null;
      if (k === "api") TOKENS = null;
      if (k === "verbindungen") GOOGLE = null;
      render();
      break;
    case "user-new":
      userModal();
      break;
    case "go-time":
      closeModal();
      go("zeit");
      break;
    case "entry-new":
      closeModal();
      void entryModal(a.dataset.k === "income" ? "income" : "expense");
      break;
    case "mk-link":
      linkModal();
      break;
    case "mk-range":
      MKUI.timeRange = Number(k) || 31;
      render();
      break;
    case "mk-time-del":
      confirmModal("Dieser Zeiteintrag wird in MILKEE gelöscht.", "Löschen", async () => {
        try {
          await api.mkDeleteTime(Number(id));
          toast("Gelöscht");
        } catch (err) {
          mkFail(err);
        }
        MKD.times = undefined;
        render();
      });
      break;
    case "fin-range":
      MKUI.finRange = (k in RANGE_LABEL ? k : "year") as RangeKey;
      render();
      break;
    case "cfin-range":
      MKUI.cfinRange = (k in RANGE_LABEL ? k : "12m") as RangeKey;
      render();
      break;
    case "cfin-refresh": {
      const c = C(UI.cid);
      if (c) void loadClientFinance(c, true);
      break;
    }
    case "mk-dkind":
      MKUI.docKind = k === "proposal" ? "proposal" : "invoice";
      MKUI.docStatus = MKUI.docKind === "invoice" ? "unpaid" : "sent";
      render();
      break;
    case "mk-dstatus":
      MKUI.docStatus = k;
      render();
      break;
    case "mk-etype":
      MKUI.entryType = k;
      render();
      break;
    case "inv-new":
      closeModal();
      openNewInvoice();
      break;
    case "mk-mark": {
      const docId = Number(id);
      if (k === "paid") {
        paidModal(docId);
        break;
      }
      const labels: Record<string, string> = { sent: "versendet", accepted: "angenommen", declined: "abgelehnt" };
      confirmModal(`Das Dokument wird in MILKEE als «${labels[k] ?? k}» markiert.`, "Markieren", async () => {
        try {
          await api.mkMark(MKUI.docKind, docId, k);
          toast("In MILKEE geändert");
        } catch (err) {
          mkFail(err);
        }
        MKD.docs = undefined;
        FIN = { ...FIN, state: FIN.data ? "ok" : "idle" };
        render();
      });
      break;
    }
    case "notif-open":
      notifModal();
      break;
    case "notif-del":
      NOTIFS = NOTIFS.filter((x) => x.id !== id);
      a.closest(".notif-row")?.remove();
      renderShell();
      void api.dismiss(id).catch((err: unknown) => onAuthLost(err));
      if (!NOTIFS.length) notifModal();
      break;
    case "notif-go": {
      const n = NOTIFS.find((x) => x.id === id);
      closeModal();
      if (n?.link) {
        go(n.link);
        if (n.tab) {
          UI.ctab = n.tab;
          render(true);
        }
      }
      break;
    }
    case "desktop-on":
      localStorage.removeItem("desktopNotes");
      void Notification.requestPermission().then((p) => {
        toast(p === "granted" ? "Desktop-Mitteilungen sind an" : "Der Browser hat Mitteilungen nicht erlaubt");
        closeModal();
        notifModal();
      });
      break;
    case "desktop-off":
      localStorage.setItem("desktopNotes", "off");
      toast("Desktop-Mitteilungen aus");
      closeModal();
      notifModal();
      break;
    case "notif-clear":
      NOTIFS = [];
      closeModal();
      renderShell();
      void api.dismissAll().catch((err: unknown) => onAuthLost(err));
      break;
    case "go-connections":
      UI.set = "verbindungen";
      go("einstellungen");
      break;
    case "fin-refresh":
      toast("Daten werden aus MILKEE geholt");
      void loadFin(true);
      void loadInsights(true);
      YEAR_REV = { state: "idle", map: new Map() };
      break;
    case "mk-sync":
      void runAutomation(UI.cid, true);
      break;
    case "billing-setup":
      billingModal();
      break;
    case "bill-early": {
      a.setAttribute("disabled", "");
      void billEarlyNow([a.dataset.key ?? ""]);
      break;
    }
    case "bill-early-all": {
      const L = LIVE[UI.cid];
      const b = L && billingCtx(UI.cid, L);
      if (!b?.cfg) break;
      const list = earlyOpen(b);
      confirmModal(
        `${list.length} ${list.length === 1 ? "abgegebenes Video" : "abgegebene Videos"} (${chf0(list.length * b.cfg.pricePerVideo)}) ${list.length === 1 ? "kommt" : "kommen"} als offener Posten in MILKEE und ${list.length === 1 ? "wird" : "werden"} beim nächsten Rechnungschreiben für <b>${esc(b.cfg.milkeeCustomerName)}</b> vorgeschlagen.<br><br>Der Stand in der Tabelle bleibt gleich. Wird ein Video später final, wird es nicht nochmals verrechnet.`,
        "Verrechnen",
        () => {
          closeModal();
          return billEarlyNow(list.map(itemKey));
        },
      );
      break;
    }
    case "bill-early-undo": {
      const cid = UI.cid;
      void api.billEarlyUndo(cid, a.dataset.key ?? "").then(
        async () => {
          toast("Zurückgenommen. Das Video wird bei «Final» wieder automatisch verrechnet.");
          delete AUTO[cid];
          await refreshState();
          render();
        },
        (e: unknown) => {
          if (!onAuthLost(e)) toast((e as Error).message);
        },
      );
      break;
    }
    case "bill-all": {
      const L = LIVE[UI.cid];
      const b = L && billingCtx(UI.cid, L);
      if (b) void billItems(b.unbilled);
      break;
    }
    case "bill-one": {
      const it = LIVE[UI.cid]?.items.find((x) => itemKey(x) === a.dataset.key);
      if (it) void billItems([it]);
      break;
    }
    case "unbill": {
      const cid = UI.cid;
      const row = BILLED[cid]?.find((x) => x.id === id);
      if (!row) break;
      confirmModal(row.invoiceId ? `«${esc(row.title)}» wird aus dem Rechnungsentwurf in MILKEE entfernt. Das geht nur, solange die Rechnung noch nicht versendet ist.` : `«${esc(row.title)}» gilt danach wieder als nicht verrechnet.`, "Zurücknehmen", async () => {
        try {
          await api.unbill(id);
          toast("Zurückgenommen");
          void loadFin(true);
        } catch (err) {
          if (!onAuthLost(err)) toast((err as Error).message);
        }
        delete BILLED[cid];
        void loadBilled(cid);
      });
      break;
    }
    case "health-open":
      UI.set = "verbindungen";
      go("einstellungen");
      break;
    case "health-check":
      toast("Wird geprüft …");
      void api.healthCheck().then(
        (st) => {
          HEALTH = st;
          renderHealthChip();
          toast(st.issues.length ? "Es gibt eine Störung" : "Alles läuft");
          render();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    case "ev-new":
      eventModal("", k);
      break;
    case "ev-edit":
      eventModal(id);
      break;
    case "ev-del":
      confirmModal("Der Termin wird in Exchange gelöscht (auch in Outlook und auf dem Handy).", "Löschen", async () => {
        try {
          await api.eventDelete(id);
          toast("Termin gelöscht");
          await loadCalendar(true);
        } catch (err) {
          if (!onAuthLost(err)) toast((err as Error).message);
        }
      });
      break;
    case "priv":
      setPrivate(!document.documentElement.classList.contains("priv"));
      break;
    case "todo-new":
      todoNewModal(k);
      break;
    case "todo-when":
      todoWhenModal(a.dataset.cid ?? "", id);
      break;
    case "wp-form":
      wpFormModal(k);
      break;
    case "wp-apply": {
      const when = wpValue();
      if (!when) break;
      const set = (sfx: string, v: string | null | undefined) => {
        const el = byId<HTMLInputElement>(`${k}-${sfx}`);
        if (el) el.value = v ?? "";
      };
      set("d", when.day);
      set("f", when.from);
      set("u", when.to);
      const chip = byId(`${k}-w`);
      if (chip) {
        chip.innerHTML = `${ic("cal", 13)} ${when.day ? esc(whenText(when)) : "Wann?"}`;
        chip.classList.toggle("set", !!when.day);
      }
      closeModal();
      byId(`${k}-t`)?.focus();
      break;
    }
    case "wp-day":
      WP.day = k;
      if (k) WP.month = `${k.slice(0, 8)}01`;
      wpRender();
      break;
    case "wp-month": {
      const m = dayDate(WP.month);
      m.setMonth(m.getMonth() + Number(k));
      WP.month = `${isoDay(m).slice(0, 8)}01`;
      wpRender();
      break;
    }
    case "wp-timed":
      WP.timed = k === "1";
      WP.edit = "from";
      wpRender();
      break;
    case "wp-edit":
      WP.edit = k === "to" ? "to" : "from";
      wpRender();
      break;
    case "wp-wh":
      wpSetTime(k as "h" | "m", a.dataset.v ?? "00");
      wpRender();
      break;
    case "wp-dur":
      WP.to = fromMin(toMin(WP.from) + Number(k));
      wpRender();
      break;
    case "cal-day":
      CALV.sel = k;
      if (k.slice(0, 7) !== CALV.month.slice(0, 7)) CALV.month = `${k.slice(0, 8)}01`;
      render();
      break;
    case "cal-month": {
      if (k === "0") {
        CALV.sel = todayDay();
        CALV.month = `${CALV.sel.slice(0, 8)}01`;
      } else {
        const m = dayDate(CALV.month);
        m.setMonth(m.getMonth() + Number(k));
        CALV.month = `${isoDay(m).slice(0, 8)}01`;
        CALV.sel = CALV.month.slice(0, 7) === todayDay().slice(0, 7) ? todayDay() : CALV.month;
      }
      render();
      break;
    }
    case "cal-show": {
      const key = k as keyof typeof CALV.show;
      CALV.show[key] = !CALV.show[key];
      try {
        localStorage.setItem("cal-show", JSON.stringify(CALV.show));
      } catch {
        /* nur für diesen Besuch */
      }
      render();
      break;
    }
    case "cal-sync":
      toast("Wird mit Exchange abgeglichen …");
      void api.calendarSync().then(
        async (st) => {
          MSI = st;
          toast(st.error ? st.error : "Mit Exchange abgeglichen");
          await refreshState();
          await loadCalendar(true);
          render();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    case "ms-remove":
      confirmModal("Die Verbindung zu Microsoft 365 wird getrennt. Die schon eingetragenen Termine bleiben in Exchange stehen; neue To-dos kommen nicht mehr dazu.", "Trennen", async () => {
        try {
          MSI = await api.msRemove();
          MSCALS = null;
          toast("Microsoft 365 getrennt");
        } catch (err) {
          if (!onAuthLost(err)) toast((err as Error).message);
        }
        render();
      });
      break;
    case "nextcloud-remove":
      confirmModal("Die Nextcloud wird getrennt. Neue Videos werden dann wieder auf dem Server gespeichert. Videos, die schon in der Nextcloud liegen, lassen sich erst wieder abspielen, wenn sie wieder verbunden ist.", "Trennen", async () => {
        try {
          NCI = await api.disconnectNextcloud();
          toast("Nextcloud getrennt");
        } catch (err) {
          if (!onAuthLost(err)) toast((err as Error).message);
        }
        render();
      });
      break;
    case "apns-test":
      void api.pushTest().then(
        (r) => toast(r.sent ? `Testmitteilung an ${r.sent === 1 ? "1 iPhone" : `${r.sent} iPhones`} geschickt` : `Nicht zugestellt: ${r.failed.join(", ")}`),
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    case "apns-remove":
      confirmModal("Der APNs-Schlüssel wird gelöscht. Die iPhone-App bekommt danach keine Push-Mitteilungen mehr, die Hinweise in der Glocke bleiben.", "Entfernen", async () => {
        try {
          APNS = await api.removeApns();
          toast("Push entfernt");
        } catch (err) {
          if (!onAuthLost(err)) toast((err as Error).message);
        }
        render();
      });
      break;
    case "milkee-remove":
      confirmModal("Die Verbindung zu MILKEE wird getrennt und das Token gelöscht. In MILKEE selbst ändert sich nichts.", "Trennen", async () => {
        try {
          MK = await api.disconnectMilkee();
          FIN = { state: "idle" };
          toast("MILKEE getrennt");
        } catch (err) {
          if (!onAuthLost(err)) toast((err as Error).message);
        }
        render();
      });
      break;
    case "google-remove":
      confirmModal("Der hochgeladene Google-Schlüssel wird entfernt. Die Live-Tabellen funktionieren danach nicht mehr, bis ein neuer Schlüssel hochgeladen ist.", "Entfernen", async () => {
        try {
          afterGoogleChange(await api.removeGoogle(), "Google-Schlüssel entfernt");
        } catch (err) {
          if (!onAuthLost(err)) toast((err as Error).message);
        }
      });
      break;
    case "token-new":
      tokenModal();
      break;
    case "token-del": {
      const tk = TOKENS?.find((x) => x.id === id);
      if (!tk) break;
      confirmModal(`Der Schlüssel «${esc(tk.name)}» wird gelöscht. Programme, die ihn benutzen, haben danach sofort keinen Zugriff mehr.`, "Löschen", async () => {
        try {
          await api.deleteToken(id);
          toast("Schlüssel gelöscht");
        } catch (err) {
          if (!onAuthLost(err)) toast((err as Error).message);
        }
        await loadTokens();
      });
      break;
    }
    case "user-role":
    case "user-toggle":
    case "user-reset-pw":
    case "user-reset-2fa":
    case "user-del":
      void userAction(act, id);
      break;
  }
});

document.addEventListener("change", (e) => {
  const a = (e.target as HTMLElement).closest<HTMLElement>("[data-a]");
  if (!a) return;
  const act = a.dataset.a;
  if (act === "live-set") void liveSet(a as HTMLSelectElement);
  // neues To-do: Projekte des gewählten Kunden
  if (act === "nt-client") {
    const sel = byId<HTMLSelectElement>("nt-p");
    if (sel) sel.innerHTML = projectOptions((a as HTMLSelectElement).value);
  }
  if (act === "google-file") {
    const f = (a as HTMLInputElement).files?.[0];
    (a as HTMLInputElement).value = "";
    if (f) uploadGoogleKey(f);
  }
  if (act === "import-file") {
    const f = (a as HTMLInputElement).files?.[0];
    if (f) importData(f);
  }
  if (act === "mk-tproj") {
    MKUI.timeProject = Number((a as HTMLSelectElement).value) || 0;
    render();
  }
  if (act === "mk-dyear") {
    MKUI.docYear = Number((a as HTMLSelectElement).value) || 0;
    render();
  }
  if (act === "mk-eyear") {
    MKUI.entryYear = Number((a as HTMLSelectElement).value) || new Date().getFullYear();
    render();
  }
  if (act === "sec-link") {
    const c = C(UI.cid);
    const sec = a.dataset.k ?? "";
    if (!c || !sec) return;
    const v = (a as HTMLInputElement).value.trim();
    const links = { ...sectionLinks(c) };
    if (v) links[secKey(sec)] = v;
    else delete links[secKey(sec)];
    c.sectionLinks = links;
    save();
    render();
    toast(v ? `B-Roll-Ordner für «${sec}» gespeichert` : `B-Roll-Ordner für «${sec}» entfernt`);
    return;
  }
  if (act === "projclient") {
    UI.projClient = (a as HTMLSelectElement).value;
    render();
  }
  if (act === "phase") {
    const p = P(a.dataset.id ?? "");
    if (!p) return;
    p.phase = (a as HTMLSelectElement).value;
    save();
    syncPhase(p);
    toast(`«${p.title}» → ${p.phase}`);
  }
});

document.addEventListener("input", (e) => {
  const el = e.target as HTMLInputElement;
  if (el.id === "client-q") {
    UI.clientQ = el.value;
    render();
  } else if (el.id === "pal-in") {
    PAL.q = el.value;
    PAL.sel = 0;
    drawPalette();
  }
});

async function gateSubmit(btn: HTMLButtonElement | null, fn: () => Promise<void>) {
  if (btn) btn.disabled = true;
  try {
    await fn();
  } finally {
    if (btn) btn.disabled = false;
  }
}

document.addEventListener("submit", (e) => {
  const f = (e.target as HTMLElement).closest<HTMLFormElement>("[data-form]");
  if (!f) return;
  e.preventDefault();
  const btn = f.querySelector<HTMLButtonElement>("button[type=submit]");
  switch (f.dataset.form) {
    case "login":
      void gateSubmit(btn, async () => {
        try {
          await afterAuth(await api.login(val("g-email"), rawVal("g-pass")));
        } catch (err) {
          showGate("login", (err as Error).message);
        }
      });
      break;
    case "mfa":
      void gateSubmit(btn, async () => {
        try {
          await afterAuth(await api.mfa(val("g-code")));
        } catch (err) {
          if (err instanceof ApiError && err.code === "auth") showGate("login", err.message);
          else showGate("mfa", "Der Code stimmt nicht. Nimm den aktuellen Code aus der App.");
        }
      });
      break;
    case "enroll":
      void gateSubmit(btn, async () => {
        try {
          const me = await api.totpEnable(val("g-code"));
          ENROLL = null;
          toast("2-Faktor ist eingerichtet");
          await afterAuth(me);
        } catch (err) {
          if (onAuthLost(err)) return;
          showGate("enroll", "Der Code stimmt nicht. Prüf, ob die Uhrzeit auf dem Handy stimmt, und nimm den aktuellen Code.");
        }
      });
      break;
    case "newpw": {
      const p1 = rawVal("g-pw1");
      if (p1 !== rawVal("g-pw2")) return showGate("newpw", "Die beiden Passwörter stimmen nicht überein.");
      void gateSubmit(btn, async () => {
        try {
          await afterAuth(await api.password(p1));
          toast("Passwort gespeichert");
        } catch (err) {
          if (!onAuthLost(err)) showGate("newpw", (err as Error).message);
        }
      });
      break;
    }
    case "password": {
      const p1 = rawVal("pw-new");
      if (p1.length < 14) return toast("Das Passwort braucht mindestens 14 Zeichen.");
      if (p1 !== rawVal("pw-new2")) return toast("Die beiden Passwörter stimmen nicht überein.");
      void api.password(p1, rawVal("pw-cur")).then(
        () => {
          f.reset();
          toast("Passwort geändert. Andere Geräte wurden abgemeldet.");
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    }
    case "user": {
      const email = val("us-email");
      if (!email) return toast("Gib eine E-Mail-Adresse ein.");
      void api.createUser(email, val("us-name"), val("us-role")).then(
        (r) => {
          passwordModal(r.user.email, r.password, "Der Benutzer ist erstellt.");
          void loadUsers();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    }
    case "mk-link": {
      const c = C(UI.cid);
      const cid = Number(val("ml-cust"));
      if (!c || !cid) break;
      c.milkee = { customerId: cid, customerName: MK_CUSTOMERS?.find((m) => m.id === cid)?.name ?? "" };
      save();
      closeModal();
      render();
      toast("Mit MILKEE verknüpft");
      break;
    }
    case "mk-time": {
      const pid = Number(val("tm-proj"));
      // von–bis ausgefüllt: Dauer daraus (der Server rechnet nochmals nach und gibt beides an MILKEE)
      const from = val("tm-from");
      const to = val("tm-to");
      const span = from && to ? spanMinutes(from, to) : null;
      if (from && to && span === null) return toast("Von und Bis prüfen, z. B. 13:00 bis 18:00.");
      const dur = span !== null ? ([Math.floor(span / 60), span % 60] as [number, number]) : parseDur(val("tm-dur"));
      if (!pid) return toast("Wähl ein Projekt.");
      if (!dur) return toast("Dauer z. B. als 1:30 eingeben oder Von und Bis ausfüllen.");
      const desc = val("tm-desc");
      void api.mkAddTime({ projectId: pid, date: val("tm-date") || todayIso(), hours: dur[0], minutes: dur[1], description: desc, billable: byId<HTMLInputElement>("tm-bill")?.checked ?? true, ...(span !== null ? { start: from, end: to } : {}) }).then(
        () => {
          toast(`${hm(dur[0], dur[1])} h in MILKEE erfasst`);
          MKUI.timeProject = MKUI.timeProject || 0;
          MKD.times = undefined;
          render();
        },
        (err: unknown) => mkFail(err),
      );
      break;
    }
    case "mk-start": {
      const pid = Number(val("ts-proj"));
      if (!pid) return toast("Wähl ein Projekt.");
      void api.mkStartTimer(pid).then(
        (t) => {
          MKD.timer = t;
          toast("Timer läuft in MILKEE");
          render();
        },
        (err: unknown) => mkFail(err),
      );
      break;
    }
    case "mk-stop":
      void api.mkStopTimer(val("ts-desc")).then(
        (t) => {
          MKD.timer = t;
          MKD.times = undefined;
          toast("Zeit in MILKEE gespeichert");
          render();
        },
        (err: unknown) => mkFail(err),
      );
      break;
    case "mk-paid": {
      const docId = Number(f.dataset.id);
      void api.mkMark("invoice", docId, "paid", val("pd-date") || todayIso()).then(
        () => {
          closeModal();
          toast("Als bezahlt markiert und gebucht");
          invoiceChanged();
          MKD.docs = undefined;
          MKD.entries = undefined;
          FIN = { ...FIN, state: FIN.data ? "ok" : "idle" };
          void loadFin(true);
          render();
        },
        (err: unknown) => mkFail(err),
      );
      break;
    }
    case "mk-entry": {
      const kind = f.dataset.k === "income" ? "income" : "expense";
      const sum = Number(val("en-sum"));
      if (!val("en-desc")) return toast("Wofür war die Buchung?");
      if (!(sum > 0)) return toast("Gib einen Betrag ein.");
      const file = byId<HTMLInputElement>("en-file")?.files?.[0];
      if (file && file.size > 6 * 1024 * 1024) return toast("Der Beleg ist grösser als 6 MB.");
      const fd = new FormData();
      fd.set("kind", kind);
      fd.set("date", val("en-date") || todayIso());
      fd.set("description", val("en-desc"));
      fd.set("sum", String(sum));
      fd.set("accountId", val("en-acc"));
      fd.set("bankAccountId", val("en-bank"));
      if (val("en-cust")) fd.set("customerId", val("en-cust"));
      if (file) fd.set("file", file, file.name);
      if (btn) btn.disabled = true;
      toast(file ? "Buchung und Beleg gehen an MILKEE …" : "Buchung geht an MILKEE …");
      void api.mkAddEntry(fd).then(
        () => {
          closeModal();
          toast(`${kind === "expense" ? "Ausgabe" : "Einnahme"} über ${chfPlain(sum)} in MILKEE gebucht${file ? ", mit Beleg" : ""}`);
          MKD.entries = undefined;
          FIN = { ...FIN, state: FIN.data ? "ok" : "idle" };
          void loadFin(true);
          render();
        },
        (err: unknown) => {
          if (btn) btn.disabled = false;
          mkFail(err);
        },
      );
      break;
    }
    case "social":
      void api.saveSocialSettings({ name: val("so-name"), handle: val("so-handle"), line: val("so-line") }).then(
        (b) => {
          SOCIAL = b;
          toast("Profil für die Vorschau gespeichert");
          render();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    case "todo": {
      const p = f.dataset.p ?? "";
      const cid = f.dataset.cid || val(`${p}-c`);
      const text = val(`${p}-t`);
      if (!cid) return toast("Bitte einen Kunden wählen.");
      if (!text) return toast("Was ist zu tun?");
      const when = f.dataset.wp ? wpValue() : readWhen(val(`${p}-d`), val(`${p}-f`), val(`${p}-u`));
      if (!when) break;
      const project = byId<HTMLSelectElement>(`${p}-p`)?.value || null;
      const n: Note = { id: uid(), text, kind: "todo", done: false, at: Date.now(), ...(when.day ? when : {}), ...(project ? { project } : {}) };
      (S.notes[cid] ??= []).unshift(n);
      if (!f.dataset.cid) UI.lastTodoCid = cid;
      save();
      if (f.closest(".modal")) closeModal();
      toast(when.day ? `To-do für ${whenText(n)} eingetragen` : "To-do eingetragen");
      render();
      break;
    }
    case "event": {
      const subject = val("ev-s");
      if (!subject) return toast("Bitte einen Titel eingeben.");
      const when = wpValue();
      if (!when?.day) break;
      const draft = { subject, location: val("ev-l"), day: when.day, from: when.from ?? null, to: when.to ?? null };
      const eid = f.dataset.id ?? "";
      if (btn) btn.disabled = true;
      void (eid ? api.eventUpdate(eid, draft) : api.eventCreate(draft)).then(
        async () => {
          closeModal();
          toast(eid ? "Termin gespeichert" : `Termin für ${whenText(when)} eingetragen`);
          await loadCalendar(true);
        },
        (err: unknown) => {
          if (btn) btn.disabled = false;
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    }
    case "todo-when": {
      const n = findNote(f.dataset.cid ?? "", f.dataset.id ?? "");
      if (!n) break;
      const when = wpValue();
      if (!when) break;
      Object.assign(n, when);
      save();
      closeModal();
      render();
      break;
    }
    case "ms-app": {
      const b = { tenantId: val("ms-tenant"), clientId: val("ms-client"), ...(rawVal("ms-secret").trim() ? { clientSecret: rawVal("ms-secret").trim() } : {}) };
      if (!b.tenantId || !b.clientId) return toast("Verzeichnis-ID und Anwendungs-ID fehlen.");
      void api.msSave(b).then(
        (st) => {
          MSI = st;
          toast(st.connected ? "Gespeichert" : "Gespeichert. Jetzt «Mit Microsoft verbinden».");
          render();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    }
    case "ms-cal": {
      const id = val("ms-cal");
      if (!id) return toast("Bitte einen Kalender wählen.");
      const show = [...f.querySelectorAll<HTMLInputElement>('input[name="ms-show"]:checked')].map((x) => x.value);
      toast("Kalender wird verbunden und abgeglichen …");
      void api.msSetCalendar({ id, show }).then(
        (st) => {
          MSI = st;
          CALV.key = "";
          toast(st.error ? st.error : `Verbunden mit «${st.calendarName}»`);
          render();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    }
    case "nextcloud": {
      const b = { url: val("nc-url"), user: val("nc-user"), root: val("nc-root") || "Abgaben", ...(rawVal("nc-pass").trim() ? { pass: rawVal("nc-pass").trim() } : {}) };
      if (!b.url || !b.user) return toast("Adresse und Benutzer fehlen.");
      toast("Verbindung zur Nextcloud wird geprüft …");
      void api.connectNextcloud(b).then(
        (info) => {
          NCI = info;
          toast(`Nextcloud verbunden: Ordner «${info.root}»`);
          render();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    }
    case "apns": {
      const file = (document.getElementById("ap-file") as HTMLInputElement | null)?.files?.[0];
      const keyId = val("ap-key").toUpperCase();
      const teamId = val("ap-team").toUpperCase();
      if (!keyId || !teamId) return toast("Key-ID und Team-ID fehlen.");
      if (!file) return toast("Wähle die .p8-Datei aus dem Apple-Developer-Konto.");
      if (file.size > 5000) return toast("Die Datei ist zu gross für einen APNs-Schlüssel.");
      void file.text().then((key) =>
        api.saveApns({ keyId, teamId, key }).then(
          (info) => {
            APNS = info;
            toast("Push ist eingerichtet");
            render();
          },
          (err: unknown) => {
            if (!onAuthLost(err)) toast((err as Error).message);
          },
        ),
      );
      break;
    }
    case "milkee": {
      const token = rawVal("mk-token").trim();
      if (!token) return toast("Füg zuerst das Token aus MILKEE ein.");
      toast("Token wird bei MILKEE geprüft …");
      void api.connectMilkee(token).then(
        (info) => {
          MK = info;
          FIN = { state: "idle" };
          MK_CUSTOMERS = null;
          toast(`MILKEE verbunden: ${info.company}`);
          void autoLinkMilkee(true);
          render();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    }
    case "billing": {
      const c = C(UI.cid);
      const custId = Number(val("bl-cust"));
      const price = Number(val("bl-price"));
      if (!c || !custId) return toast("Wähl den Kunden in MILKEE aus.");
      if (!(price > 0)) return toast("Gib einen Preis pro Video ein.");
      const first = !billingOf(c);
      const skip = (byId<HTMLInputElement>("bl-skip")?.checked ?? false) && first;
      const auto = byId<HTMLInputElement>("bl-auto")?.checked ?? false;
      c.billing = { milkeeCustomerId: custId, milkeeCustomerName: MK_CUSTOMERS?.find((m) => m.id === custId)?.name ?? "", pricePerVideo: price, auto } satisfies ClientBilling;
      save();
      closeModal();
      if (skip) {
        const items = (LIVE[c.id]?.items ?? []).filter(isDone).filter((it) => !(BILLED[c.id] ?? []).some((b) => b.itemKey === itemKey(it)));
        void api.skipBilled(c.id, items.map((it) => ({ key: itemKey(it), title: it.section ? `${it.video} (${it.section})` : it.video }))).then(
          () => {
            delete BILLED[c.id];
            void loadBilled(c.id);
          },
          (err: unknown) => {
            if (!onAuthLost(err)) toast((err as Error).message);
          },
        );
      }
      render();
      toast(auto ? "Gespeichert. Die Automatik legt jetzt die Projekte an …" : "Abrechnung gespeichert");
      if (auto) setTimeout(() => void runAutomation(c.id, true), 1200);
      break;
    }
    case "token": {
      const name = val("tk-name");
      if (!name) return toast("Gib dem Schlüssel einen Namen.");
      void api.createToken(name, val("tk-scope"), Number(val("tk-days"))).then(
        (r) => {
          secretModal(r.token.name, r.secret);
          void loadTokens();
        },
        (err: unknown) => {
          if (!onAuthLost(err)) toast((err as Error).message);
        },
      );
      break;
    }
    case "note": {
      const cid = f.dataset.cid ?? "";
      const txt = val(`ni-${cid}`);
      if (!txt) break;
      const note: Note = { id: uid(), text: txt, kind: val(`nk-${cid}`) === "todo" ? "todo" : "note", done: false, at: Date.now() };
      (S.notes[cid] ??= []).unshift(note);
      save();
      render();
      break;
    }
    case "upload-link": {
      const u = val("up-url");
      if (u && !/^https?:\/\//i.test(u)) return toast("Bitte den vollständigen Link einfügen (beginnt mit https://).");
      const t = S.tables[UI.cid];
      if (!t) break;
      t.uploadUrl = u;
      save();
      closeModal();
      render();
      toast(u ? "Upload-Link gespeichert" : "Upload-Link entfernt");
      break;
    }
    case "link-add": {
      const url = val("ln-url");
      if (!url) return toast("Füg zuerst einen Link ein.");
      const c = C(UI.cid);
      if (!c) break;
      c.links.push({ label: val("ln-label"), url: safeUrl(url) });
      save();
      render();
      break;
    }
    case "xl-link": {
      const url = val("xl-url");
      if (!/^https?:\/\//i.test(url)) return toast("Bitte den vollständigen Link einfügen (beginnt mit https://).");
      const gid = sheetIdFrom(url);
      S.tables[UI.cid] = gid ? { source: "gsheet", id: gid, url, name: val("xl-name"), addedAt: nowIso() } : { source: "excel", url, name: val("xl-name") || "Excel-Tabelle", addedAt: nowIso() };
      save();
      startWatches();
      render();
      toast(gid ? "Tabelle verbunden" : "Link gespeichert");
      break;
    }
    case "project": {
      const title = val("pm-title");
      if (!title) break;
      const client = val("pm-client");
      if (!C(client)) return toast("Wähl einen Kunden aus.");
      const proj: Project = { id: uid(), client, title, phase: val("pm-phase") || "Laufend", due: fromInput(val("pm-due")) };
      S.projects.push(proj);
      save();
      closeModal();
      render();
      const custId = mkCustomerOf(C(client));
      if (MK.configured && byId<HTMLInputElement>("pm-mk")?.checked !== false && custId) {
        api.mkCreateProject(title, custId).then(
          (r) => {
            proj.milkeeProjectId = r.project.id;
            save();
            syncPhase(proj);
            MKD.projects = undefined;
            render();
            toast("Projekt erstellt, auch in MILKEE");
          },
          (err: unknown) => {
            if (!onAuthLost(err)) toast(`Projekt erstellt, aber nicht in MILKEE: ${(err as Error).message}`);
          },
        );
      } else toast(MK.configured && !custId ? "Projekt erstellt. Diesen Kunden gibt es in MILKEE nicht, darum nur hier." : "Projekt erstellt");
      break;
    }
    case "client": {
      const name = val("cf-name");
      if (!name) return toast("Gib einen Namen ein.");
      const color = document.querySelector<HTMLInputElement>("input[name=cf-color]:checked")?.value ?? COLORS[0]!;
      const short =
        name
          .split(/\s+/)
          .filter((w) => !/^(ag|gmbh|sa|&)$/i.test(w))
          .slice(0, 2)
          .map((w) => w[0])
          .join("")
          .toUpperCase() || name.slice(0, 2).toUpperCase();
      const data = { name, short, contact: val("cf-contact"), email: val("cf-email"), street: val("cf-street"), zip: val("cf-zip"), city: val("cf-city"), phone: val("cf-phone"), color };
      const existing = UI.clientEditId ? C(UI.clientEditId) : undefined;
      if (existing) {
        Object.assign(existing, data);
        save();
        closeModal();
        render();
        toast("Gespeichert");
        break;
      }
      const id = `${name.toLowerCase().normalize("NFD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24)}-${uid().slice(0, 3)}`;
      S.clients.push({ id, status: "aktiv", tabs: ["projects", "notes"], links: [], ...data });
      save();
      closeModal();
      toast(`${name} erstellt`);
      go(`kunde-${id}`);
      break;
    }
    case "tabs": {
      const c = C(UI.cid);
      if (!c) break;
      const sel = [...f.querySelectorAll<HTMLInputElement>("input[name=tb]:checked")].map((x) => x.value);
      c.tabs = Object.keys(TABS).filter((k) => sel.includes(k));
      save();
      closeModal();
      render();
      break;
    }
    case "settings":
      CO().owner = val("st-owner");
      save();
      render();
      toast("Einstellungen gespeichert");
      break;
  }
});

document.addEventListener("keydown", (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
    if (!LOADED) return;
    e.preventDefault();
    if (PAL.open) closeModal();
    else openPalette();
    return;
  }
  if (e.key === "Escape") {
    if (must("overlay").innerHTML) return closeModal();
    document.querySelectorAll("details.menu[open]").forEach((m) => m.removeAttribute("open"));
  }
  if (PAL.open) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      PAL.sel = Math.min(PAL.sel + 1, PAL.items.length - 1);
      drawPalette();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      PAL.sel = Math.max(PAL.sel - 1, 0);
      drawPalette();
    } else if (e.key === "Enter") {
      e.preventDefault();
      runPal(PAL.sel);
    }
  }
});

/* ---------- «⋯»-Menüs: über allem schweben, nach oben öffnen, wenn unten kein Platz ist ---------- */

function placeMenu(m: HTMLDetailsElement) {
  const list = m.querySelector<HTMLElement>(".menu-list");
  const btn = m.querySelector<HTMLElement>("summary");
  if (!list || !btn) return;
  if (!m.open) {
    list.removeAttribute("style");
    return;
  }
  // fest positioniert, damit Tabellen und Karten mit overflow das Menü nicht abschneiden
  const r = btn.getBoundingClientRect();
  Object.assign(list.style, { position: "fixed", right: "auto", top: "0px", left: "0px", visibility: "hidden" });
  const w = list.offsetWidth;
  const h = list.offsetHeight;
  const gap = 6;
  // unten endet der Platz an der Navigationsleiste (schmale Bildschirme)
  const nav = document.getElementById("bNav");
  const navTop = nav && nav.offsetParent !== null ? nav.getBoundingClientRect().top : window.innerHeight;
  const bottom = Math.min(window.innerHeight, navTop);
  const below = bottom - r.bottom - gap - 8;
  const top = h <= below || r.top < h + gap + 8 ? r.bottom + gap : r.top - h - gap;
  const left = Math.min(Math.max(8, r.right - w), window.innerWidth - w - 8);
  Object.assign(list.style, { top: `${Math.max(8, top)}px`, left: `${left}px`, visibility: "" });
}
document.addEventListener("toggle", (e) => {
  const m = e.target as HTMLElement;
  if (m instanceof HTMLDetailsElement && m.classList.contains("menu")) placeMenu(m);
}, true);
// beim Scrollen oder Grössenändern mitführen
const reposition = () => document.querySelectorAll<HTMLDetailsElement>("details.menu[open]").forEach(placeMenu);
window.addEventListener("scroll", reposition, true);
window.addEventListener("resize", reposition);

initDeliveries({
  clients: () => S.clients,
  projects: () => S.projects,
  ownerName: () => firstName() || "Social Solve",
  go,
  rerender: () => render(),
  toast,
  openModal,
  closeModal,
  confirm: confirmModal,
  mhead,
  pageHead,
  emptyBox,
  authLost: onAuthLost,
});
initInvoices({
  rerender: () => render(),
  toast,
  go,
  openModal,
  closeModal,
  confirm: confirmModal,
  mhead,
  mfoot,
  pageHead,
  emptyBox,
  authLost: onAuthLost,
  paid: (id) => paidModal(id),
  setRoute: (rid) => {
    UI.rid = rid;
    history.replaceState(null, "", `#rechnung-${rid}`);
  },
});
initClosing({
  rerender: () => render(),
  toast,
  openModal,
  closeModal,
  confirm: confirmModal,
  mhead,
  mfoot,
  pageHead,
  emptyBox,
  authLost: onAuthLost,
});
initScripts({
  clients: () => S.clients,
  projects: () => S.projects,
  go,
  rerender: () => render(),
  toast,
  openModal,
  closeModal,
  confirm: confirmModal,
  mhead,
  pageHead,
  emptyBox,
  authLost: onAuthLost,
});
// Brücke für die Mac-App: Bereich öffnen, Timer und Hinweise nach einer Änderung in der Menüleiste nachführen
(window as unknown as { socialsolve: object }).socialsolve = {
  go,
  refreshTimer: () => {
    if (MK.configured) void api.mkTimer().then((t) => ((MKD.timer = t), renderShell(), UI.view === "zeit" && render()), () => undefined);
  },
  newItem: () => must("quickAddBtn").click(),
};
setRoute((location.hash || "#uebersicht").slice(1));
void init();
